import { packageCount, packageLineName, packageUnitPrice, toPackageFiscalLine } from '../utils/packageLine';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { CartItem, InventoryItem as QuickSaleInventoryItem } from '../QuickSaleScreen';
import { PaymentDetails } from '../components/modals/PartialPaymentModal';
import { usePrintReceipt, ReceiptData } from '../components/PrintReceiptService';
import { useAuth } from '../../../../contexts/AuthContext';
import salesService, { 
  QuickSalePosPaymentRequest,
  QuickSaleCashWithInvoiceRequest,
  QuickSaleCashWithInvoiceItem,
  QuickSalePosWithInvoiceRequest,
  QuickSalePosWithInvoiceItem,
  QuickSalePartialWithInvoiceRequest,
  QuickSaleOpenAccountWithInvoiceRequest,
  QuickSaleWithInvoiceResponse
} from '../../../../services/salesService';
import accountingService from '../../../../services/accountingService';
// ✅ YENİ: Yazarkasa entegrasyonları
import { getFiscalDevice, isFiscalDeviceSupported, getFiscalDeviceDisplayName } from './fiscalDevices';
import type { FiscalDeviceType, FiscalProductItem } from './fiscalDevices/types';
import { syncDepartmentVatMapping, type DepartmentVatSyncResult } from './fiscalDevices/ingenico';
import { checkBekoLastReceiptStatus } from './fiscalDevices/beko-token';
import { parseVatRate } from '../utils/vatTaxGroup';
// ✅ YENİ: Karma ödeme hook'u
import { usePartialPayment } from './usePartialPayment';
import { accessLockCode, accessLockMessage, clearPendingAtomicPayment } from '../utils/accessLockError';
// ✅ Kayıt hatası kurtarma: çok yuvalı bekleyen kayıt kuyruğu + ortak "Ödeme Kaydı Hatası" akışı + telemetri
import {
  savePendingAtomicPayment,
  removePendingAtomicPayment,
  listPendingAtomicPayments,
  tryBeginPendingRecovery,
  endPendingRecovery,
  type PendingAtomicPayment,
} from '../utils/pendingAtomicPayments';
import {
  AtomicSaveError,
  handleAtomicSaveFailure,
  isAlreadySavedError,
  runOnce,
  telemetryFiscalDevice,
  telemetryCashierName,
  type SaveTelemetryContext,
} from '../utils/atomicSaveRecovery';
import { reportPosSaveError, reportPosSaveOutcome } from '../../../../services/posTelemetryService';
// PavoPay P2P entegrasyonu
import { pavoApiService } from '../../../../services/pavoApiService';
import { pavoSignalRService } from '../../../../services/pavoSignalRService';
// ✅ Rest-P2P (pavo_rest_p2p) ödeme yönlendirmesi
import {
  restP2PPaymentService,
  type RestP2PPaymentPart,
  type RestP2PProgressEvent,
  getPendingRestP2PRequestId,
  fetchPaymentStatus as fetchRestP2PPaymentStatus,
  createRestP2PRequestId,
  type RestP2PPaymentOutcome,
} from '../../../../services/restP2PPaymentService';
// ✅ Ödeme niyeti (Aşama 2+3): cihazdan ÖNCE sunucuya yazılır; tarayıcı kapansa da satış tamamlanır
import {
  newPaymentIntentKey,
  createPaymentIntent,
  reportDeviceResult,
  abandonPaymentIntent,
  wasPaymentIntentAttempted,
  isDeviceFailureCertain,
  type PaymentIntentEndpoint,
  type PaymentIntentKind,
} from '../../../../services/paymentIntentService';
// ✅ F6: Rest-P2P ödeme sonuçlarını PavoPay satış kayıtlarına ekleme
import {
  savePavoPaySale,
  type PavoPaySaleRecord,
  getPendingCustomerParty,
  getPendingNotificationPhone,
  clearPendingCustomerParty,
  setLastClubEntryFireForgetRequestId,
} from './fiscalDevices/pavopay';
import { resolveWarehouseId } from '../utils/resolveWarehouseId';
import { InventoryService } from '../../../accounting/inventory/services/inventoryService';
// ✅ YENİ: İndirim kaydı yardımcıları (fatura kalemi + fiş başlığı için)
import { buildItemDiscountFields, buildHeaderDiscountFields } from '../utils/quickSaleDiscount';

// Sabitler artık SQL API tarafından yönetiliyor

/**
 * Rest-P2P completed payload'undan e-belge alanlarını (ETTN, fatura no,
 * sorgu linki) çıkarır. Cihaz dispatcher'ı bu alanları üst seviyede yollar
 * (`Ettn`/`InvoiceNo`/`InquiryLink` — SignalR ham payload PascalCase,
 * reconciliation yolunda camelCase). Eski cihaz sürümleri için fallback:
 * `RawSaleJson.FinancialDocuments[0].DocumentNo` veya `PaymentResultJson`
 * string'i parse edilip aynı alanlara bakılır. Doğrudan REST akışındaki
 * `extractETTNFromResponse` (pavopay.ts) ile aynı mantık.
 */
function extractRestP2PEDocFields(result: any): {
  ettn: string | null;
  invoiceNo: string | null;
  inquiryLink: string | null;
} {
  const empty = { ettn: null, invoiceNo: null, inquiryLink: null };
  if (!result || typeof result !== 'object') return empty;

  const str = (v: any): string | null => {
    const s = v == null ? '' : String(v).trim();
    return s.length > 0 ? s : null;
  };

  const fromSale = (sale: any) => {
    if (!sale || typeof sale !== 'object') return empty;
    const docs = sale.FinancialDocuments || sale.financialDocuments;
    const doc = Array.isArray(docs) && docs.length > 0 ? docs[0] : null;
    return {
      ettn: str(doc?.DocumentNo ?? doc?.documentNo),
      invoiceNo: str(sale.InvoiceNo ?? sale.invoiceNo ?? doc?.InvoiceNo ?? doc?.invoiceNo),
      inquiryLink: str(sale.SaleInquieryLink ?? sale.saleInquieryLink),
    };
  };

  // 1) Üst seviye alanlar (yeni cihaz sürümü)
  let ettn = str(result.Ettn ?? result.ettn);
  let invoiceNo = str(result.InvoiceNo ?? result.invoiceNo);
  let inquiryLink = str(result.InquiryLink ?? result.inquiryLink);

  // 2) RawSaleJson (obje veya JSON string olabilir)
  if (!ettn) {
    let rawSale = result.RawSaleJson ?? result.rawSaleJson;
    if (typeof rawSale === 'string') {
      try { rawSale = JSON.parse(rawSale); } catch { rawSale = null; }
    }
    const f = fromSale(rawSale);
    ettn = ettn || f.ettn;
    invoiceNo = invoiceNo || f.invoiceNo;
    inquiryLink = inquiryLink || f.inquiryLink;
  }

  // 3) PaymentResultJson string'i (DB kanonik kaydı / recovery yolu)
  if (!ettn) {
    const prjRaw = result.PaymentResultJson ?? result.paymentResultJson ?? result.resultJson;
    if (typeof prjRaw === 'string' && prjRaw.length > 0) {
      try {
        const prj = JSON.parse(prjRaw);
        ettn = ettn || str(prj?.Ettn ?? prj?.ettn);
        invoiceNo = invoiceNo || str(prj?.InvoiceNo ?? prj?.invoiceNo);
        inquiryLink = inquiryLink || str(prj?.InquiryLink ?? prj?.inquiryLink);
        if (!ettn) {
          const f = fromSale(prj?.RawSaleJson ?? prj?.rawSaleJson);
          ettn = ettn || f.ettn;
          invoiceNo = invoiceNo || f.invoiceNo;
          inquiryLink = inquiryLink || f.inquiryLink;
        }
      } catch { /* bozuk JSON — yoksay */ }
    }
  }

  return { ettn, invoiceNo, inquiryLink };
}

// QuickSaleInvoiceItem tipi (QuickSaleScreen'den alınacak)
interface QuickSaleInvoiceItem {
  id: string;
  itemId: string;
  itemName: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  vatRate: number;
  sctRate: number;
  discountRate: number;
  lineTotal: number;
  netTotal: number;
  vatAmount: number;
  sctAmount: number;
  grossTotal: number;
}

// QuickSaleInvoice artık SQL API tarafında yönetiliyor

interface UsePaymentProps {
  cart: CartItem[];
  companyId: string | null;
  companyName?: string | null; // ✅ YENİ: QuickSaleScreen'den cache'lenmiş şirket adı
  /**
   * ✅ PR2 (Invoice Seller Snapshot Refactor): Backend'in çözdüğü satıcı snapshot'ı.
   * Companies veya Branches.Firm* kaynağı + IsFranchiseInvoice flag içerir.
   * Kayıt sırasında DTO'ya gönderilmez (backend zaten resolve eder), yalnızca
   * fiş başlığı (PrintReceiptService) ve UI gösterim için kullanılır.
   */
  seller?: import('../../../../services/sellerInfoService').SellerSnapshot | null;
  selectedCustomer: any; // CustomerData tipini import etmeyi unutma!
  allInventoryItems: QuickSaleInventoryItem[];
  userDetails: any; // UserData tipini import etmeyi unutma!
  currentUserDisplayName: string | null;
  cashPaymentCashboxId: string | null;
  cashPaymentCashboxName?: string | null; // ✅ YENİ: Kasa adı (gün sonu raporu için)
  posOptions: any[]; // PaymentOption[] tipini import etmeyi unutma!
  showAlertModal: (title: string, message: string, showQuickAdd?: boolean, onRetry?: (() => void) | null, onDismiss?: (() => void) | null) => void;
  showCriticalErrorModal: (title: string, message: string) => void;
  showSnackbar: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  clearCart: () => void;
  appliedDiscountType: 'percentage' | 'amount' | null;
  lastAppliedDiscountAmount: number;
  calculateTotal: () => number;
  formatCurrency?: (amount: number) => string;
  screenId?: string | null;
  screenSettings?: any; // ✅ SQL'den çekilen ekran ayarları
  useFiscalDeviceForCash?: boolean; // YENİ EKLENDİ
  useBothFiscalAndReceiptPrinter?: boolean; // ✅ YENİ: Yazarkasa + Fiş Yazıcısı birlikte kullanım
  warnIfFiscalNotConnected?: boolean; // ✅ YENİ: Yazarkasa bağlı değilse uyar (varsayılan true)
  prepaidSystemEnabled?: boolean; // YENİ: Ön ödemeli sistem etkin mi
  selectedFiscalDevice?: FiscalDeviceType; // ✅ YENİ: Seçilen yazarkasa cihazı
  // YENİ: Ödeme bekleme modalı için callback'ler
  setPaymentWaitingModalOpen?: (open: boolean) => void;
  setPaymentWaitingStatus?: (status: 'waiting' | 'success' | 'error') => void;
  setPaymentWaitingMessage?: (message: string) => void;
  // ✅ YENİ: Restoran modu için prop'lar
  isRestaurantMode?: boolean;
  tableId?: string | null;
  tableName?: string | null;
  waiterName?: string | null;
  loginType?: 'waiter' | 'cashier';
  restaurantScreenId?: number | null;
  navigateToRestaurantScreen?: () => void; // ✅ YENİ: Ödeme sonrası geri dönüş için
  // ✅ YENİ: Sipariş numarası rezervasyonu
  reservedLongCode?: string | null; // Rezerve edilen uzun kod (ödeme kaydında kullanılır)
  reservedShortCode?: string | null; // Rezerve edilen kısa kod (fişte gösterilir - RS-1, HS-1)
  onPaymentComplete?: () => void; // Ödeme tamamlandığında yeni numara almak için callback
  // ✅ YENİ: Nakit ödeme başarıyla kaydedildikten sonra çağrılır (para çekmecesi tetiği için)
  // paidByCash: ödenen nakit tutarı (0 ise tetik atılmamalı). Karma ödemeler için nakit parçası geçer.
  onCashPaymentSuccess?: (paidByCash: number) => void;
  // ✅ YENİ: Sepet bölme için opsiyonel parametreler
  skipNavigationAfterPayment?: boolean; // true ise ödeme sonrası navigate yapma (sepet bölme için)
  onPaymentSuccessOverride?: () => void; // varsa clearCart yerine bu çağrılır (sepet bölme için)
  splitCartOverride?: CartItem[]; // varsa cart yerine bu kullanılır (sepet bölme için)
  // ✅ YENİ: Restoran sepet bölme fix'i — hangi ActiveSale kapatılacak (sol=mainSaleId, sağ=splitSaleId)
  // Verilmezse hook getByTableId ile ana satışı bulur (klasik davranış).
  restaurantActiveSaleIdOverride?: number | null;
  // ✅ YENİ: Kasiyer bilgileri (gün sonu raporu için)
  currentCashierId?: number | null;
  currentCashierName?: string | null;
  // ✅ YENİ: Müşteri notu (fişe yazdırılır)
  customerNote?: string;
  // ✅ YENİ: Para üstü modalında girilen alınan para / para üstü (fişe yazdırılır).
  // Modal onayı ödemeyi aynı tick içinde başlattığı için state değil ref taşınır.
  cashChangeInfoRef?: React.MutableRefObject<{ receivedAmount: number; changeAmount: number } | null>;
  // ✅ YENİ: Şube ID (Branch bazında benzersiz sipariş numarası için)
  activeBranchId?: string | null;
  // ✅ YENİ: Şubenin deposu (varsa tüm ürünler bu depodan düşülür)
  branchWarehouseId?: string | null;
  // ✅ Ingenico departman-KDV uyumsuzluğu tespit edildiğinde modal göster (ödeme DURUR)
  onIngenicoDeptVatMismatch?: (syncResult: DepartmentVatSyncResult) => void;
  // Kulüp girişi: ödeme sonrası kart atama callback'i
  onClubEntryPaymentComplete?: (data: {
    receiptId: number;
    referenceNumber: string;
    paymentMethod: 'CASH' | 'POS' | 'PARTIAL';
    pavoPaySaleNumber?: string;
    cart: CartItem[];
  }) => void;
  skipClearCartAfterPayment?: boolean;
}

// Yerel saat fonksiyonu artık SQL API tarafında yönetiliyor

/**
 * Şube deposu varsa onu, yoksa stockVisibility ayarlarından resolveWarehouseId'yi döndürür.
 */
function resolveEffectiveWarehouseId(
  productId: string,
  categoryId: string | undefined | null,
  screenSettings: any,
  branchWarehouseId: string | null | undefined
): string | null {
  if (branchWarehouseId) return branchWarehouseId;
  return resolveWarehouseId(productId, categoryId, screenSettings);
}

/**
 * Şube deposu aktifken, sepetteki ürünlerden o depoda kaydı olmayanlar için qty=0
 * stok satırı açar. Satış anında backend eksik satırı kendisi de açar; bu adım
 * ürünün depo listelerinde satış öncesinde de görünmesini sağlar.
 *
 * NOT: Eskiden ürün başına `warehouse/add-stock` çağrılıyordu ama o uç
 * `Quantity > 0` şartı koştuğu için qty=0 istekleri 400 ile dönüyor, yani hiç
 * satır açılmıyordu. `items/bulk-assign-warehouse` ucu ise satırı olmayanlara
 * verilen başlangıç miktarıyla satır açar, olanları atlar — tek istek yeterli.
 */
async function enrichCartItemsForBranchWarehouse(
  cartItems: { id: string; name: string }[],
  branchWarehouseId: string,
  companyId: string
): Promise<void> {
  const productIds = Array.from(new Set(cartItems.map((item) => item.id).filter(Boolean)));
  if (productIds.length === 0) return;

  try {
    const result = await InventoryService.bulkAssignWarehouse(
      companyId,
      productIds,
      branchWarehouseId,
      0
    );
    console.log(`[STOK] Şube deposunda ${result.count} ürün kaydı açıldı (qty=0)`);
  } catch (e: any) {
    // Kritik değil: satır yoksa backend satış anında kendisi açıyor.
    console.warn('[STOK] Ürünler şube deposuna eklenemedi', e?.response?.data || e?.message);
  }
}

export const usePayment = ({
  cart,
  companyId,
  companyName, // ✅ YENİ: Cache'lenmiş şirket adı
  seller = null, // ✅ PR2: Backend tarafından çözülmüş satıcı snapshot (fiş başlığı için)
  selectedCustomer,
  allInventoryItems,
  userDetails,
  currentUserDisplayName,
  cashPaymentCashboxId,
  cashPaymentCashboxName = null, // ✅ YENİ: Kasa adı
  posOptions,
  showAlertModal,
  showCriticalErrorModal, // YENİ EKLENDİ
  showSnackbar,
  clearCart,
  appliedDiscountType,
  lastAppliedDiscountAmount,
  calculateTotal,
  formatCurrency = (amount) => amount.toFixed(2),
  screenId = null,
  screenSettings, // ✅ SQL'den çekilen ekran ayarları
  useFiscalDeviceForCash = false,
  useBothFiscalAndReceiptPrinter = false, // ✅ YENİ: Yazarkasa + Fiş Yazıcısı birlikte kullanım (varsayılan kapalı)
  warnIfFiscalNotConnected = true, // ✅ YENİ: Yazarkasa bağlı değilse uyar (varsayılan açık)
  prepaidSystemEnabled = false, // YENİ: Varsayılan olarak kapalı
  selectedFiscalDevice = 'hugin_t300', // ✅ YENİ: Varsayılan olarak Hugin T300
  // YENİ: Ödeme bekleme modalı callback'leri
  setPaymentWaitingModalOpen,
  setPaymentWaitingStatus,
  setPaymentWaitingMessage,
  // ✅ YENİ: Restoran modu için
  isRestaurantMode = false,
  tableId = null,
  tableName = null,
  waiterName = null,
  loginType = 'waiter',
  restaurantScreenId = null,
  navigateToRestaurantScreen, // ✅ YENİ: Ödeme sonrası geri dönüş için
  // ✅ YENİ: Sipariş numarası rezervasyonu
  reservedLongCode = null,
  reservedShortCode = null,
  onPaymentComplete,
  onCashPaymentSuccess,
  // ✅ YENİ: Sepet bölme için opsiyonel parametreler
  skipNavigationAfterPayment = false,
  onPaymentSuccessOverride,
  splitCartOverride, // varsa cart yerine bu kullanılır
  restaurantActiveSaleIdOverride = null, // ✅ YENİ: Sepet bölme — kapatılacak ActiveSale Id
  // ✅ YENİ: Kasiyer bilgileri
  currentCashierId = null,
  currentCashierName = null,
  // ✅ YENİ: Müşteri notu (fişe yazdırılır)
  customerNote = '',
  // ✅ YENİ: Para üstü bilgisi (alınan para / para üstü fişe yazdırılır)
  cashChangeInfoRef,
  // ✅ YENİ: Şube ID (Branch bazında benzersiz sipariş numarası için)
  activeBranchId = null,
  // ✅ YENİ: Şubenin deposu (varsa tüm ürünler bu depodan düşülür)
  branchWarehouseId = null,
  // ✅ Ingenico departman-KDV uyumsuzluğu (modal gösterimi için)
  onIngenicoDeptVatMismatch,
  // Kulüp girişi
  onClubEntryPaymentComplete,
  skipClearCartAfterPayment = false,
}: UsePaymentProps) => {
  // ✅ Harici POS: entegre yazarkasa yok. Banka POS cihazı bağımsız çalışır ve
  // kendi slibini basar; tüm fiscal/ÖKC adımları atlanır, satış yalnızca SQL
  // kayıtlarıyla ilerler. Bayrağı burada söndürmek tüm nakit-fiscal kapılarını
  // ve IsFiscalReceipt işaretlerini tutarlı şekilde kapatır.
  const isExternalPosDevice = selectedFiscalDevice === 'harici_pos';
  if (isExternalPosDevice && useFiscalDeviceForCash) {
    useFiscalDeviceForCash = false;
  }

  // ✅ YENİ: AuthContext'ten şube adı alma
  const { getBranchName } = useAuth();
  
  // ✅ YENİ: Kasiyer sistemi aktifse kasiyer adı, yoksa kullanıcı adı kullan
  // Bu değer tüm CreatedBy ve completedByName alanlarında kullanılır
  const effectiveCreatedBy = currentCashierName || currentUserDisplayName || userDetails?.email || 'System';
  
  // ✅ YENİ: Sepet bölme modunda cart yerine splitCartOverride kullanılır
  const effectiveCart = splitCartOverride ?? cart;

  // Cart ref: closure sorunlarını önlemek için her zaman güncel sepete erişim sağlar
  const cartRef = useRef(cart);
  cartRef.current = cart;

  // Verilen sepet dizisinden toplam hesapla (closure-safe)
  const calculateTotalFromCart = (cartItems: typeof cart): number => {
    let total = 0;
    for (const item of cartItems) {
      if (item.vatStatus === 'included') {
        total += item.price * item.quantity;
      } else {
        const product = allInventoryItems.find(inv => inv.id === item.id);
        const vatRate = product?.vatRate || item.vatRate || 0;
        const priceWithVat = item.price * (1 + vatRate / 100);
        total += priceWithVat * item.quantity;
      }
    }
    return parseFloat(total.toFixed(2));
  };

  // Sepet kaleminin KDV dahil birim fiyatı. Oran calculateTotalFromCart ile aynı
  // kuraldan alınır; Pavo'ya giden kalemler toplamı ödeme tutarıyla aynı çıkar.
  // (Stok kartında fiyatı KDV hariç olan ürün: Soda 5,00 + %10 → 5,50.)
  const cartItemGrossUnitPrice = (item: CartItem): number => {
    if (item.vatStatus === 'included') return item.price;
    const product = allInventoryItems.find(inv => inv.id === item.id);
    const vatRate = Number(product?.vatRate || item.vatRate || 0);
    return item.price * (1 + vatRate / 100);
  };

  // ✅ YENİ: Sepet bölme modunda doğru toplam hesaplama
  // splitCartOverride varsa onun toplamını hesapla, yoksa normal calculateTotal() kullan
  const calculateEffectiveTotal = useCallback((): number => {
    if (splitCartOverride && splitCartOverride.length > 0) {
      let total = 0;
      for (const item of splitCartOverride) {
        const itemTotal = item.price * item.quantity;
        if (item.vatStatus === 'included') {
          total += itemTotal;
        } else {
          const product = allInventoryItems.find(inv => inv.id === item.id);
          const vatRate = product?.vatRate || item.vatRate || 0;
          const priceWithVat = item.price * (1 + vatRate / 100);
          total += priceWithVat * item.quantity;
        }
      }
      console.log(`[calculateEffectiveTotal] Split cart toplam: ${total.toFixed(2)} TL (${splitCartOverride.length} ürün, isRestaurantMode: ${isRestaurantMode})`);
      return parseFloat(total.toFixed(2));
    }
    // Normal cart için calculateTotal kullan
    return calculateTotal();
  }, [splitCartOverride, allInventoryItems, calculateTotal, isRestaurantMode]);
  
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isProcessingPosPayment, setIsProcessingPosPayment] = useState(false); // Sadece POS için
  const [posPaymentError, setPosPaymentError] = useState<string | null>(null);
  const [isPosErrorModalOpen, setIsPosErrorModalOpen] = useState(false);
  const [posRetryCallback, setPosRetryCallback] = useState<(() => void) | null>(null);
  
  // ✅ YENİ: Yazarkasa bağlı değil onay modalı için state'ler
  const [isFiscalDisconnectedConfirmOpen, setIsFiscalDisconnectedConfirmOpen] = useState(false);
  const [pendingPosPaymentCallback, setPendingPosPaymentCallback] = useState<(() => void) | null>(null);

  // ✅ YENİ (Ingenico): Yazarkasa HATASI sonrası "Yine de Devam Et" onay modalı.
  // Ödeme cihazdan alınmış ama sistem hata sanıyorsa kullanıcı satışı yine de kaydedebilmeli.
  const [isFiscalErrorContinueOpen, setIsFiscalErrorContinueOpen] = useState(false);
  const [fiscalErrorContinueMessage, setFiscalErrorContinueMessage] = useState<string>('');
  const [fiscalErrorContinueCallback, setFiscalErrorContinueCallback] = useState<(() => void) | null>(null);

  // ✅ YENİ (Ingenico): POS hata modalındaki "Yine de Devam Et" callback'i
  const [posContinueCallback, setPosContinueCallback] = useState<(() => void) | null>(null);
  
  // NOT: Eski `atomicRetryCount` state'i kaldırıldı — retry closure'ı sayacı ilk değerinde
  // yakalıyordu, sınır hiç işlemiyordu. Sayaç artık handleAtomicSaveFailure içinde.

  // ✅ Kayıt hatası telemetrisi: tüm akışlarda ortak bağlam (kasiyer ADI, e-posta değil)
  const telemetryCashier = telemetryCashierName(currentCashierName, currentUserDisplayName);
  const buildSaveTelemetry = (extra: Partial<SaveTelemetryContext>): SaveTelemetryContext => ({
    branchId: activeBranchId || (userDetails?.branchesId ? String(userDetails.branchesId) : null),
    screenId: screenId ? (parseInt(String(screenId), 10) || null) : null,
    screenMode: isRestaurantMode ? 'restoran' : 'hizli-satis',
    tableName: isRestaurantMode ? (tableName || null) : null,
    cashierName: telemetryCashier,
    ...extra,
  });

  // ✅ Ödeme niyeti: cihaz çağrısından ÖNCE (cihaz yoksa kayıttan hemen önce) sunucuya yazılır.
  // En fazla 4 sn bekler, asla hata fırlatmaz; yazılamazsa ödeme normal devam eder.
  const openPaymentIntent = (p: {
    key: string;
    endpoint: PaymentIntentEndpoint;
    payload: any;
    amount: number | null | undefined;
    paymentKind: PaymentIntentKind;
    fiscalDevice: string;
    deviceCode?: string | null;
    pavoRequestId?: string | null;
  }) =>
    createPaymentIntent({
      intentKey: p.key,
      endpoint: p.endpoint,
      payload: p.payload,
      amount: p.amount,
      paymentKind: p.paymentKind,
      fiscalDevice: p.fiscalDevice,
      deviceCode: p.deviceCode ?? null,
      pavoRequestId: p.pavoRequestId ?? null,
      screenId: screenId ? (parseInt(String(screenId), 10) || null) : null,
      screenMode: isRestaurantMode ? 'restoran' : 'hizli-satis',
      branchId: activeBranchId || (userDetails?.branchesId ? String(userDetails.branchesId) : null),
      tableId: isRestaurantMode ? (tableId || null) : null,
      activeSaleId: isRestaurantMode ? (restaurantActiveSaleIdOverride ?? null) : null,
      tableName: isRestaurantMode ? (tableName || null) : null,
      cashierName: telemetryCashier,
    });

  // ✅ Beko: cihaz zaman aşımından sonra arka planda son fiş durumu sorgulanırken
  // (10 sn) cihaz YENİDEN çalıştırılmaz — aksi halde hem geç onay otomatik kaydı hem
  // kasiyerin "Yeniden Dene"si çalışıp çift çekim + iki satış oluşabiliyordu.
  const bekoLatePollActiveRef = useRef(false);

  // ───────────────────────────────────────────────
  // ✅ SİPARİŞ NUMARASI GARANTİSİ (deneme yaşam döngüsü)
  // ───────────────────────────────────────────────
  // Kural: Ödeme DENEMESİ gerçekten başladıysa (yazarkasa/Pavo cihaz çağrısı
  // veya atomik SQL isteği), sonuç ne olursa olsun (başarılı / hata / iptal)
  // rezerve edilen sipariş numarası YAKILIR ve yeni numara alınır. Böylece
  // başarısız bir denemenin numarası bir sonraki satışta asla tekrar kullanılmaz.
  // Erken validasyon dönüşleri (boş sepet, cari seçilmedi vb.) numara yakmaz.
  //
  // attemptDepthRef: processPaymentAfterPartialModal → handlePosPaymentForQuickSale
  // gibi iç içe handler çağrılarında yalnızca EN DIŞTAKİ scope'un finally'sinin
  // yenileme yapmasını sağlar (tek denemede tek yenileme).
  const attemptDepthRef = useRef(0);
  const attemptStartedRef = useRef(false);

  /** Ödeme denemesi başladı olarak işaretle (cihaz/atomik API çağrısından hemen önce). */
  const markPaymentAttempt = () => {
    attemptStartedRef.current = true;
  };

  const beginPaymentScope = () => {
    attemptDepthRef.current += 1;
    if (attemptDepthRef.current === 1) {
      attemptStartedRef.current = false;
    }
  };

  const endPaymentScope = () => {
    attemptDepthRef.current = Math.max(0, attemptDepthRef.current - 1);
    if (attemptDepthRef.current === 0 && attemptStartedRef.current) {
      attemptStartedRef.current = false;
      if (!isRestaurantMode && onPaymentComplete) {
        console.log('🎫 [NUMARA] Ödeme denemesi tamamlandı (başarılı/başarısız) — sipariş numarası yenileniyor...');
        onPaymentComplete();
      }
    }
  };

  /** Handler'ı deneme scope'u ile sarmalar: finally'de numara garantili yenilenir. */
  const withPaymentScope = <TArgs extends any[], TResult>(
    fn: (...args: TArgs) => Promise<TResult>
  ) => {
    return async (...args: TArgs): Promise<TResult> => {
      beginPaymentScope();
      try {
        return await fn(...args);
      } finally {
        endPaymentScope();
      }
    };
  };

  // ───────────────────────────────────────────────
  // ✅ Rest-P2P (pavo_rest_p2p) bekleme modalı state'leri
  // ───────────────────────────────────────────────
  const [restP2PModalOpen, setRestP2PModalOpen] = useState(false);
  const [restP2PStatus, setRestP2PStatus] = useState<'waiting' | 'verifying' | 'success' | 'error' | 'cancelled'>('waiting');
  const [restP2PMode, setRestP2PMode] = useState<'cash' | 'pos' | 'mixed'>('cash');
  const [restP2PTotal, setRestP2PTotal] = useState<number>(0);
  const [restP2PParts, setRestP2PParts] = useState<RestP2PPaymentPart[]>([]);
  const [restP2PProgress, setRestP2PProgress] = useState<RestP2PProgressEvent | null>(null);
  const [restP2PMessage, setRestP2PMessage] = useState<string | null>(null);
  // Backend'den dönen makine-okunabilir hata kodu (örn. "DEVICE_OFFLINE",
  // "HTTP_503", "HUB_TIMEOUT"). Modal "Yine de Devam Et" uyarısında
  // teknik bağlam için gösterilir. Yalnızca status==='error' iken anlamlıdır.
  const [restP2PErrorCode, setRestP2PErrorCode] = useState<string | null>(null);
  const restP2PActiveRequestIdRef = useRef<string | null>(null);
  // ✅ Rest-P2P başarılı ödeme tamamlandığında Pavo satış numarasını tutar.
  // Kulüp girişi atomik (PARTIAL) akışında onClubEntryPaymentComplete'e
  // pavoPaySaleNumber geçirmek için kullanılır — aksi halde POS depozito
  // "nakit" gibi kaydedilip iade nakit gidiyor (depositPavoPaySaleNumber boş).
  const lastRestP2PSaleNumberRef = useRef<string | null>(null);
  // ✅ Rest-P2P e-belge bilgileri: cihaz dispatcher'ı Sale.FinancialDocuments'tan
  // ETTN/fatura no/sorgu linkini çıkarıp completed payload'una koyar. Bunları
  // yakalayıp atomik satış isteğine (EInvoiceETTN vb.) taşıyoruz ki fatura
  // "pavopay" e-belgesi olarak işaretlensin (doğrudan REST akışıyla aynı).
  const lastRestP2PEttnRef = useRef<string | null>(null);
  const lastRestP2PInvoiceNoRef = useRef<string | null>(null);
  const lastRestP2PInquiryLinkRef = useRef<string | null>(null);
  // `runRestP2PFlow` failed outcome aldığında modal'ı KAPAMAZ —
  // kullanıcı "Yine de Devam Et" veya "İptal" basana kadar bekler.
  // Bu ref kullanıcı kararını promise'a köprüler. Resolve değeri:
  //   - 'continue' → fiscal'siz devam (atomik SQL akışı tetiklenir)
  //   - 'cancel'   → akış iptal (sepet açık kalır)
  const restP2PContinueResolveRef = useRef<((decision: 'continue' | 'cancel') => void) | null>(null);
  const restP2PDeviceName = screenSettings?.pavoRestP2PDeviceName || null;
  // Harici POS seçiliyken Rest-P2P bayrakları (pavopay döneminden kalmış olabilir)
  // yok sayılır; ödeme akışı Pavo cihazına yönlenmez.
  const isRestP2PEnabled = Boolean(
    screenSettings?.useRestP2PForPayments && screenSettings?.pavoRestP2PDeviceScreenCode
  ) && !isExternalPosDevice;

  // ✅ F7: Sayfa yenilendi / HMR oldu ise Rest-P2P icin pending bir istek varsa
  // backend'den status sorgusu ile kurtarma yap: completed ise savePavoPaySale,
  // degilse pending'i temizle.
  useEffect(() => {
    if (!isRestP2PEnabled || !companyId) return;
    const pending = getPendingRestP2PRequestId();
    if (!pending) return;
    let cancelled = false;
    (async () => {
      try {
        const status = await fetchRestP2PPaymentStatus(pending);
        if (cancelled) return;
        if (status.status === 'completed') {
          const result: any = status.paymentResult || {};
          const modeRaw = (result?.paymentMode || result?.mode || 'cash') as string;
          const paymentType: PavoPaySaleRecord['paymentType'] =
            modeRaw === 'pos' ? 'POS' : modeRaw === 'mixed' ? 'KARMA' : 'CASH';
          const saleNumber: string | undefined =
            status.pavoSaleNumber || result?.pavoSaleNumber || result?.saleNumber;
          const amount: number = status.totalAmount ?? result?.totalAmount ?? 0;
          if (saleNumber && amount > 0) {
            savePavoPaySale({
              saleNumber,
              invoiceNo: result?.invoiceNo || result?.InvoiceNo || '',
              amount,
              date: new Date().toISOString(),
              paymentType,
              requestId: pending,
              fiscalData: { source: 'pavo_rest_p2p_recovery', requestId: pending, raw: result },
            }, companyId);
            console.log('[usePayment][F7] 🔁 Rest-P2P kurtarma: pending completed, satış kaydedildi:', saleNumber);
          }
        } else if (status.status === 'failed' || status.status === 'cancelled') {
          console.log('[usePayment][F7] 🔁 Rest-P2P pending ', status.status, pending);
        } else {
          // halen pending/processing/unknown — burada zorlamaya gerek yok
          return;
        }
      } catch (e) {
        console.warn('[usePayment][F7] Kurtarma hatası:', e);
      } finally {
        try { localStorage.removeItem('restP2PPendingRequestId'); } catch (_) {}
      }
    })();
    return () => { cancelled = true; };
  }, [isRestP2PEnabled, companyId]);

  // ✅ F6: Rest-P2P ödeme tamamlandığında PavoPay satış kaydını localStorage'a
  // yaz ki `PavoPayRefundModal` günün satışları sekmesinde görebilsin.
  // Hem fire-and-forget nakit hem de POS/mixed akışlarda tek global listener.
  useEffect(() => {
    if (!isRestP2PEnabled || !companyId) return;
    const subId = `restp2p-sale-record-${companyId}`;
    pavoSignalRService.onQuickSalePaymentCompleted(subId, (requestId, result: any) => {
      try {
        const modeRaw = (result?.paymentMode || result?.mode || restP2PMode || 'cash') as string;
        const paymentType: PavoPaySaleRecord['paymentType'] =
          modeRaw === 'pos' ? 'POS' : modeRaw === 'mixed' ? 'KARMA' : 'CASH';
        const saleNumber: string | undefined =
          result?.pavoSaleNumber || result?.saleNumber || result?.PavoSaleNumber;
        const invoiceNo: string = result?.invoiceNo || result?.InvoiceNo || '';
        const amount: number =
          typeof result?.totalAmount === 'number' ? result.totalAmount : restP2PTotal;
        if (!saleNumber || amount <= 0) {
          return;
        }
        const record: PavoPaySaleRecord = {
          saleNumber,
          invoiceNo,
          amount,
          date: new Date().toISOString(),
          paymentType,
          // requestId → kulüp giriş kart eşleştirmesi (fire-and-forget nakit) için anahtar.
          requestId,
          fiscalData: { source: 'pavo_rest_p2p', requestId, raw: result },
        };
        savePavoPaySale(record, companyId);
        console.log('[usePayment][F6] 💾 Rest-P2P satış kaydı eklendi:', saleNumber, paymentType, amount);
      } catch (e) {
        console.error('[usePayment][F6] savePavoPaySale hata:', e);
      }
    });
    return () => {
      pavoSignalRService.removeQuickSaleCallbacks(subId);
    };
  }, [isRestP2PEnabled, companyId, restP2PMode, restP2PTotal]);

  // ✅ Önceki oturumdan kalan (kaydedilememiş) atomik satışları kurtar.
  // Çok yuvalı kuyruk: her bekleyen satış sırayla ele alınır (bir pencere kapanınca sıradaki).
  // Firma kimliği belli olmadan başlamaz — başka firmanın kaydı bu oturumda gönderilmez.
  // Not: Burada yalnızca SQL kaydı tekrarlanır (eski davranış); masa kapatma / mutfak
  // adımları sayfa kapandığı için bilinmez ve TEKRARLANMAZ (mutfak fişi iki kez basılmaz).
  const pendingRecoveryStartedRef = useRef(false);
  useEffect(() => {
    if (!companyId || pendingRecoveryStartedRef.current) return;
    pendingRecoveryStartedRef.current = true;

    let queue: PendingAtomicPayment[] = [];
    try {
      queue = listPendingAtomicPayments(companyId);
    } catch (err) {
      console.warn('[RECOVERY] localStorage kontrolü sırasında hata:', err);
      return;
    }
    if (queue.length === 0) return;

    const MAX_AGE = 24 * 60 * 60 * 1000; // 24 saat
    const withInvoiceEndpoints = ['process-pos-with-invoice', 'process-cash-with-invoice', 'process-partial-with-invoice'];
    const endpointTitle = (ep: string) =>
      ep === 'process-pos-with-invoice' ? 'POS Ödeme' : ep === 'process-cash-with-invoice' ? 'Nakit Ödeme' : 'Karma Ödeme';
    const paymentKindOf = (ep: string) =>
      ep === 'process-pos-with-invoice' ? 'pos' : ep === 'process-cash-with-invoice' ? 'nakit' : 'karma';
    const sendPending = (pending: PendingAtomicPayment): Promise<QuickSaleWithInvoiceResponse> => {
      if (pending.endpoint === 'process-pos-with-invoice') return salesService.processPosWithInvoice(pending.request);
      if (pending.endpoint === 'process-cash-with-invoice') return salesService.processCashWithInvoice(pending.request);
      return salesService.processPartialWithInvoice(pending.request);
    };

    const processAt = (index: number) => {
      const pending = queue[index];
      if (!pending) return;
      const next = () => processAt(index + 1);
      if (!tryBeginPendingRecovery(pending.key)) { next(); return; }
      const finish = (remove: boolean) => {
        if (remove) removePendingAtomicPayment(pending.key);
        endPendingRecovery(pending.key);
      };

      const ageMs = Date.now() - (pending.timestamp || 0);
      if (ageMs > MAX_AGE) {
        console.warn('[RECOVERY] Bekleyen ödeme kaydı 24 saatten eski, siliniyor.', pending.key);
        finish(true);
        next();
        return;
      }

      if (!withInvoiceEndpoints.includes(pending.endpoint)) {
        console.warn('[RECOVERY] Bilinmeyen endpoint:', pending.endpoint);
        finish(true);
        next();
        return;
      }

      // 🧹 KURTARILAMAZ KAYITLARI OTOMATİK TEMİZLE:
      //   Kalemsiz (Items boş) *-with-invoice kayıtları backend tarafından her
      //   zaman 400 (validation error) ile reddedilir → "Yeniden Dene" sonsuza
      //   kadar başarısız olur ve kullanıcı bu modalda kilitlenir. Bu tür kayıtlar
      //   tipik olarak eski kulüp girişi "depozito-only" denemesinden kalır.
      //   Sessizce sil; kullanıcıyı serbest bırak.
      const recoverItems = pending?.request?.Items;
      if (!Array.isArray(recoverItems) || recoverItems.length === 0) {
        console.warn('[RECOVERY] Kalemsiz (kurtarılamaz) bekleyen ödeme bulundu, siliniyor:', pending.endpoint);
        finish(true);
        showSnackbar('Geçersiz bekleyen ödeme kaydı temizlendi (kalemsiz satış kaydedilemez).', 'info');
        next();
        return;
      }

      console.log('[RECOVERY] Bekleyen ödeme kaydı bulundu:', pending.endpoint, '- Yaş:', Math.round(ageMs / 1000), 's');

      const key: string | null = pending.request?.IdempotencyKey || null;
      const req = pending.request || {};
      const amount: number | null =
        Number(req.TotalAmount) || (Number(req.CashAmount || 0) + Number(req.PosAmount || 0)) || null;
      const telemetry = buildSaveTelemetry({
        endpoint: `/api/QuickSaleWithInvoice/${pending.endpoint}`,
        screenMode: req.IsRestaurantMode ? 'restoran' : 'hizli-satis',
        tableName: null,
        paymentKind: paymentKindOf(pending.endpoint),
        fiscalReference: req.PavoPaySaleNumber || req.FiscalTransactionId || null,
        amount,
      });
      let recoveryAttempt = 1;
      let settled = false;

      // Kasiyer kaydetmeden kapattı: kayıt kuyrukta KALIR (sonraki açılışta yine sorulur)
      const onDismiss = () => {
        if (settled) return;
        settled = true;
        reportPosSaveOutcome(key, 2);
        finish(false);
        next();
      };

      const markRecovered = (message: string, severity: 'success' | 'info') => {
        settled = true;
        finish(true);
        reportPosSaveOutcome(key, 3);
        showSnackbar(message, severity);
        next();
      };

      const retryPending = async () => {
        try {
          const response = await sendPending(pending);
          if (!response.Success) {
            throw new AtomicSaveError(response.Message || 'Kayıt başarısız', response);
          }
          console.log('[RECOVERY] Bekleyen ödeme başarıyla kaydedildi!', response.ReferenceNumber);
          markRecovered(`Bekleyen ödeme kaydedildi (${response.ReferenceNumber})`, 'success');
        } catch (retryErr: any) {
          console.error('[RECOVERY] Bekleyen ödeme kaydedilemedi:', retryErr);

          if (isAlreadySavedError(retryErr)) {
            markRecovered('Bekleyen ödeme sunucuda zaten kayıtlıydı; tekrar kaydedilmedi.', 'info');
            return;
          }

          if (accessLockCode(retryErr)) {
            settled = true;
            clearPendingAtomicPayment();
            endPendingRecovery(pending.key);
            showAlertModal('Süreniz Doldu', accessLockMessage(retryErr), false, null);
            return;
          }

          // 🧹 Kalıcı doğrulama hatası → bu kayıt asla kaydedilemez.
          //    Kullanıcıyı sonsuz "Yeniden Dene" döngüsünden kurtarmak için sil.
          const isValidationError =
            /validation error|one or more validation/i.test(retryErr?.message || '');
          if (isValidationError) {
            console.warn('[RECOVERY] Kalıcı doğrulama hatası, bekleyen ödeme siliniyor (kurtarılamaz).');
            settled = true;
            finish(true);
            showAlertModal(
              'Bekleyen Ödeme Temizlendi',
              `Önceki oturumdan kalan ödeme kaydı geçersiz olduğu için kaydedilemiyor ` +
              `(sunucu doğrulama hatası) ve temizlendi.\n\n` +
              `İşlemlerinize sıfırdan devam edebilirsiniz.`,
              false,
              null,
              next
            );
            return;
          }

          recoveryAttempt += 1;
          const resp = retryErr instanceof AtomicSaveError ? retryErr.response : undefined;
          reportPosSaveError({
            ...telemetry,
            errorMessage: retryErr?.message || 'Kayıt başarısız',
            errorKind: resp?.ClientErrorKind ?? undefined,
            httpStatus: resp?.HttpStatus ?? null,
            durationMs: resp?.DurationMs ?? null,
            idempotencyKey: key,
            attempt: recoveryAttempt,
          });
          showAlertModal(
            'Tamamlanmamış Ödeme Kaydı',
            `Önceki oturumdan kaydedilmemiş bir ödeme bulundu.\n\n` +
            `Endpoint: ${pending.endpoint}\n` +
            `Tutar: ${req.TotalAmount || req.PosAmount || '?'} TL\n` +
            `Hata: ${retryErr.message}\n\n` +
            `"Yeniden Dene" ile kayıt işlemini tekrarlayabilirsiniz.`,
            false,
            retryPending,
            onDismiss
          );
        }
      };

      // Pencere açılışı da bir "kayıt hatası" olayıdır (sunucu satışın gerçekte
      // kaydedilip kaydedilmediğini anahtar üzerinden kendisi kontrol eder).
      reportPosSaveError({
        ...telemetry,
        errorMessage: 'Önceki oturumdan kaydedilmemiş ödeme bulundu (sayfa açılışı)',
        errorKind: 'istemci',
        idempotencyKey: key,
        attempt: recoveryAttempt,
      });
      showAlertModal(
        'Tamamlanmamış Ödeme Kaydı',
        `Önceki oturumdan kaydedilmemiş bir ödeme bulundu.\n\n` +
        `Tür: ${endpointTitle(pending.endpoint)}\n` +
        `Tarih: ${new Date(pending.timestamp).toLocaleString('tr-TR')}\n\n` +
        `"Yeniden Dene" ile bu ödemeyi kaydetmeyi deneyebilirsiniz.`,
        false,
        retryPending,
        onDismiss
      );
    };

    processAt(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // ✅ FIX: useMemo ile optimize et - sonsuz re-render'ı önle
  const fiscalDevice = useMemo(() => getFiscalDevice(selectedFiscalDevice), [selectedFiscalDevice]);
  const isFiscalDeviceActive = useMemo(() => isFiscalDeviceSupported(selectedFiscalDevice), [selectedFiscalDevice]);
  const fiscalDeviceName = useMemo(() => getFiscalDeviceDisplayName(selectedFiscalDevice), [selectedFiscalDevice]);
  
  // Fiş yazdırma servisi hook'unu başlat
  const { printReceipt } = usePrintReceipt({
    companyId, 
    screenId,
    screenSettings, // ✅ SQL'den çekilen ekran ayarlarını gönder
    seller, // ✅ PR2: Backend resolver'dan satıcı snapshot (franchise için kritik fiş başlığı)
    onFetchSettingsError: (error) => console.error('Yazıcı ayarları hatası:', error),
    onPrintError: (error) => showSnackbar(`Fiş yazdırma hatası: ${error}`, 'error'),
    onPrintSuccess: (message) => showSnackbar(message, 'success'),
    lazyLoad: true // Yazıcı ayarlarını sadece ihtiyaç olduğunda yükle
  });

  // Fiş yazdırma yardımcı fonksiyonu - tüm ödeme işlemlerinde ortak kullanılabilir
  const printReceiptAfterPayment = useCallback(async (invoiceNumber: string, paymentType: string, items: QuickSaleInvoiceItem[], newRemainingBalance?: number, kitchenShortCode?: string | null, cardNumber?: string | null, pricesAreVatInclusive?: boolean) => {
    // 💵 Para üstü bilgisini TÜKET (oku + temizle). Fiş bassak da basmasak da
    // burada sıfırlanır; aksi halde bir sonraki satışa sızardı.
    const pendingCashChange = cashChangeInfoRef?.current ?? null;
    if (cashChangeInfoRef) cashChangeInfoRef.current = null;
    // Bilgi yalnızca saf nakit fişine aittir (karma/POS fişine taşınmaz).
    const cashChangeInfo =
      paymentType.trim().toLocaleUpperCase('tr-TR') === 'NAKİT' ? pendingCashChange : null;

    // 🚦 TEK NOKTA YETKİ — "Yazarkasa ile Birlikte Fiş Yazıcısı Kullan" ayarı.
    // Bu toggle KAPALI ise hiçbir ödeme tipinde (Nakit / POS / Karma / Açık
    // Hesap / PavoP2P) fiş yazdırılmaz. AÇIK ise her şekilde yazdırılır.
    // Çağrı yerlerindeki ek `shouldPrintReceipt` koşulları bu kuralı sadece
    // sıkılaştırır (toggle açık + yazarkasa açık kombinasyonunda fiş çıkar);
    // dolayısıyla bu guard tek başına yeterli garantidir.
    if (!useBothFiscalAndReceiptPrinter) {
      console.log(
        '[printReceiptAfterPayment] 🚫 "Yazarkasa ile Birlikte Fiş Yazıcısı Kullan" KAPALI — fiş yazdırılmayacak ' +
          `(paymentType=${paymentType}, invoiceNumber=${invoiceNumber})`,
      );
      return;
    }

    try {
      const currentDate = new Date();
      const formattedDate = currentDate.toLocaleDateString('tr-TR');
      const formattedTime = currentDate.toLocaleTimeString('tr-TR');
      
      // ✅ Mutfak ekranı entegrasyonu kontrolü - sadece aktifse sipariş numarası ekle
      // @ts-ignore - useKitchenScreen screenSettings'de var
      const useKitchenScreen = screenSettings?.useKitchenScreen || false;
      
      // ✅ YENİ: Sipariş numarası belirleme
      // 1. Restoran modu: reservedShortCode kullan (RS-1, RS-2...)
      // 2. Hızlı satış (mutfak ekranı açık): kitchenShortCode kullan (HS-1, HS-2...)
      // 3. Hızlı satış (mutfak ekranı kapalı): numara gösterme
      let orderNumberForReceipt: string | undefined;
      if (isRestaurantMode && reservedShortCode) {
        orderNumberForReceipt = reservedShortCode;
        console.log('[printReceiptAfterPayment] 🍽️ Restoran modu - reservedShortCode kullanılıyor:', reservedShortCode);
      } else if (useKitchenScreen && kitchenShortCode) {
        orderNumberForReceipt = kitchenShortCode;
        console.log('[printReceiptAfterPayment] 🛒 Hızlı satış - kitchenShortCode kullanılıyor:', kitchenShortCode);
      }
      
      // Fiş verisi hazırla
      const receiptData: ReceiptData = {
        invoiceNumber,
        date: formattedDate,
        time: formattedTime,
        // Ön ödemeli kart no'su "Cari" satırının yanına "- Kart No: X" olarak eklenir
        // (local-service şablonu değiştirilmeden mevcut Cari satırında gösterilir).
        customerName: cardNumber
          ? `${selectedCustomer?.companyName || 'Genel Cari'} - Kart No: ${cardNumber}`
          : (selectedCustomer?.companyName || 'Genel Cari'),
        items: items.map(item => ({
          name: item.itemName,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          totalPrice: item.grossTotal,
          vatRate: 0 // Hata almamak için 0 değeri gönder
        })),
        subtotal: items.reduce((total, item) => total + item.netTotal, 0),
        vatTotal: 0, // Değer olarak 0 gönder (görünmeyecek şekilde)
        discountTotal: appliedDiscountType === 'amount' ? lastAppliedDiscountAmount : 0,
        grandTotal: calculateTotal(),
        paymentMethod: paymentType,
        cashierName: '', // Boş değer olarak gönder
        companyInfo: {
          name: 'AQUASOFT', // Bu bilgiler şirket verilerinden alınabilir
          address: '',
          taxNumber: '',
          phone: ''
        },
        // Diğer gerekli ayarları ekle
        showVatInfo: false,
        showReceiptHeader: true,
        showDiscountInfo: false,
        showCashierName: false, // Eksik alan: Kasiyer adını gösterme
        showReceiptNumber: false, // Eksik alan: Fiş numarasını gösterme
        remainingBalance: newRemainingBalance, // YENİ: Kalan bakiye (varsa)
        // ✅ FIX: Sipariş numarası - Restoran modu veya mutfak ekranı aktifse eklenir
        orderNumber: orderNumberForReceipt,
        tableName: isRestaurantMode && tableName ? tableName : undefined,
        // ✅ YENİ: Müşteri notu (fişte logo altında gösterilir)
        customerNote: customerNote || undefined,
        // ✅ YENİ: Fiyatlar zaten KDV dahilse ön ödemeli KDV yeniden ekleme atlanır
        pricesAreVatInclusive: pricesAreVatInclusive || undefined,
        // ✅ YENİ: Para üstü modalından gelen alınan para / para üstü.
        // Yalnızca nakit ödemede ve para üstü varsa fişe basılır.
        receivedAmount: cashChangeInfo?.receivedAmount,
        changeAmount: cashChangeInfo?.changeAmount,
      };
      
      console.log("[printReceiptAfterPayment] Fiş verisi oluşturuldu, KDV ve Kasiyer bilgileri boş değerlerle gönderiliyor");
      console.log("[printReceiptAfterPayment] Sipariş numarası:", orderNumberForReceipt || 'YOK');
      console.log("[printReceiptAfterPayment] Masa adı:", isRestaurantMode && tableName ? tableName : 'YOK');
      if (newRemainingBalance !== undefined) {
        console.log("[printReceiptAfterPayment] Fişe kalan bakiye bilgisi eklendi:", newRemainingBalance);
      }
      
      // Restoran: ödeme fişi tek yazıcıya (adisyon/varsayılan) — mutfak yazıcılarına dağılmaz.
      // Normal hızlı satış: kategori modu aktifse ürünler categoryPrinterAssignments'a göre ayrılır.
      const forceSinglePrinter = isRestaurantMode;
      if (forceSinglePrinter) {
        console.log('[printReceiptAfterPayment] 🍽️ Restoran — tek yazıcı (forceSinglePrinter)');
      } else {
        console.log('[printReceiptAfterPayment] 🛒 Normal hızlı satış — kategori yazdırma ayarına göre dağıtım');
      }
      await printReceipt(receiptData, cart, { forceSinglePrinter });
    } catch (error) {
      console.error('Fiş yazdırma hatası:', error);
      showSnackbar('Fiş yazdırılırken bir hata oluştu, ancak satış işlemi tamamlandı.', 'warning');
    }
  }, [useBothFiscalAndReceiptPrinter, selectedCustomer?.companyName, appliedDiscountType, lastAppliedDiscountAmount, calculateTotal, printReceipt, showSnackbar, cart, isRestaurantMode, reservedShortCode, screenSettings?.useKitchenScreen, customerNote, tableName, cashChangeInfoRef]);
  
  // ✅ YENİ: Karma ödeme hook'u (printReceiptAfterPayment SONRA tanımlandı)
  const { 
    processPartialPayment, 
    isProcessing: isProcessingPartialPayment,
    // ✅ YENİ: Karma ödeme için yazarkasa bağlı değil onay modalı
    isFiscalDisconnectedConfirmOpenPartial,
    handleFiscalDisconnectedConfirmPartial,
    handleFiscalDisconnectedCancelPartial,
  } = usePartialPayment({
    cart,
    companyId,
    companyName, // ✅ Cache'lenmiş şirket adı
    selectedCustomer,
    allInventoryItems,
    userDetails,
    currentUserDisplayName,
    cashPaymentCashboxId,
    cashPaymentCashboxName, // ✅ YENİ: Kasa adı (gün sonu raporu için)
    posOptions,
    showAlertModal,
    showSnackbar,
    clearCart,
    calculateTotal,
    screenId,
    screenSettings,
    selectedFiscalDevice,
    setPaymentWaitingModalOpen,
    setPaymentWaitingStatus,
    setPaymentWaitingMessage,
    setIsProcessingPosPayment: setIsProcessingPosPayment, // ✅ YENİ: POS modal kontrolü (local state)
    printReceiptAfterPayment: printReceiptAfterPayment, // ✅ YENİ: Fiş yazdırma
    // ✅ YENİ: Restoran modu desteği
    isRestaurantMode,
    tableId,
    tableName,
    restaurantScreenId,
    waiterName,
    navigateToRestaurantScreen,
    // ✅ YENİ: Kasiyer bilgileri (gün sonu raporu için)
    currentCashierId,
    currentCashierName,
    // ✅ YENİ: Şube ID
    activeBranchId,
    // Sipariş numarası
    reservedLongCode,
    reservedShortCode,
    // ✅ Ingenico departman-KDV uyumsuzluğu (karma ödeme için)
    onIngenicoDeptVatMismatch,
    // ✅ YENİ: Karma ödemede nakit parçası kaydedildiğinde çekmece tetiği için
    onCashPaymentSuccess,
  });

  // ───────────────────────────────────────────────
  // ✅ Rest-P2P ödeme yönlendirmesi (pavo_rest_p2p)
  // ───────────────────────────────────────────────
  /**
   * PaymentDetails + sepet toplamından Rest-P2P parts listesi üret.
   */
  const buildRestP2PPartsFromPaymentDetails = (
    paymentDetails: PaymentDetails
  ): { mode: 'cash' | 'pos' | 'mixed'; parts: RestP2PPaymentPart[]; total: number } => {
    const cashAmount = Number((paymentDetails.paidByCash || 0).toFixed(2));
    const parts: RestP2PPaymentPart[] = [];
    let order = 0;
    if (cashAmount > 0) {
      parts.push({ method: 'cash', amount: cashAmount, label: 'Nakit', order: order++ });
    }

    // ✅ Rest-P2P split desteği: PartialPaymentModal aynı POS'a yapılan
    // birden fazla ödemeyi paidByPosMap içinde tek anahtara birleştirir
    // (örn. {POS1: 185} ← 100+25+60). Sonuç: parts.length=1 → mode='pos' →
    // Pavo cihazı tek seferde 185 TL kart okutuyor. Modal artık her
    // ödemeyi pavoSplitPosParts dizisinde de tutuyor; varsa onu kullanıp
    // her bir kart okutmasını AYRI bir part olarak Pavo cihazına iletiyoruz.
    // Yoksa eski (paidByPosMap üzerinden) davranışa düşeriz — Rest-P2P
    // dışındaki yerlerden çağrılan paymentDetails'lar (örn. tek POS,
    // doğrudan POS butonu) etkilenmez.
    const splitParts = paymentDetails.pavoSplitPosParts;
    if (splitParts && splitParts.length > 0) {
      for (const p of splitParts) {
        const amt = Number((p.amount || 0).toFixed(2));
        if (amt <= 0) continue;
        const posOption = posOptions.find((opt) => opt.id === p.posId);
        parts.push({
          method: 'pos',
          amount: amt,
          label: p.label || posOption?.label || posOption?.bankAccountName || 'POS',
          order: order++,
        });
      }
    } else {
      const posEntries = Object.entries(paymentDetails.paidByPosMap || {}).filter(
        ([, amt]) => (amt as number) > 0
      );
      for (const [posId, amount] of posEntries) {
        const posOption = posOptions.find((p) => p.id === posId);
        parts.push({
          method: 'pos',
          amount: Number((amount as number).toFixed(2)),
          label: posOption?.label || posOption?.bankAccountName || 'POS',
          order: order++,
        });
      }
    }

    const total = parts.reduce((s, p) => s + p.amount, 0);
    const mode: 'cash' | 'pos' | 'mixed' =
      parts.length > 1 ? 'mixed' : parts[0]?.method === 'cash' ? 'cash' : 'pos';
    return { mode, parts, total: Number(total.toFixed(2)) };
  };

  /**
   * Sepetteki ürünlerden Pavo için hafif bir fiş listesi üret.
   *
   * ⚠️ Kesirli adet (1.5, 0.5 vb.) yönetimi:
   *   Pavo cihaz tarafı (gts_pavo / PavoPay intent) `Quantity` alanını
   *   integer olarak işliyor — 1.5 değeri cihazda 1'e cast ediliyor,
   *   ama bizim `GrossTotal` 1.5×UnitPrice olarak gidiyor. Bu durumda
   *   PavoPay validasyonu `qty * unitPrice == grossTotal` kuralını
   *   çiğniyor ve hata kodu 69 ("Sipariş kalemi miktarı ve birim ücreti
   *   hesabı ... sipariş kalemi brüt ... ücrete eşit olmalıdır")
   *   alıyoruz. Çözüm: Kesirli kalemleri `Quantity=1` +
   *   `UnitPrice=total` olarak yeniden modelle. `GrossTotal/NetTotal`
   *   aynı kalır → fiş üzerindeki tutar bozulmaz, sadece adet 1
   *   olarak gözükür. Kullanıcı orijinal kesirli adedi `Name` suffix'i
   *   olarak görür ("KAŞARLI PİDE (×1.5)").
   */
  /**
   * Sepet kaleminin mali fişe yazılacak KDV oranı.
   *
   * Stok kartı esastır; kalemin kendi değeri yalnızca ürün envanterde
   * bulunamadığında kullanılır. Depozito kalemleri envanterde olmadığı için
   * klasik akıştaki (`isDeposit ? 20 : ...`) sabit oranla aynı davranır.
   * Çözülemezse `undefined` döner — 0'a çevirmek fişi KDV'siz kestirir.
   */
  const resolveCartItemVatRate = (it: CartItem): number | undefined => {
    if (it.id?.startsWith('DEPOSIT-')) return 20;
    const product = allInventoryItems.find((p) => p.id === it.id);
    return parseVatRate(product?.vatRate ?? it.vatRate);
  };

  /**
   * Mali fiş kesilmeden önce her kalemin KDV oranı çözülebiliyor mu?
   *
   * Restoran masası kalemleri KDV saklamadığı için oran stok kartından türetiliyor;
   * kart bulunamadığında eskiden sessizce 0 gidiyor ve fiş KDV'siz kesiliyordu.
   * Artık kasiyer uyarılıp işlem durduruluyor. Dönüş: devam edilebilir mi?
   *
   * Hem Rest-P2P hem de klasik (local-service) yazarkasa akışında çağrılır;
   * ikisi de aynı sepetten fiş kalemi üretiyor.
   */
  const ensureCartVatRatesResolved = (cartOverride?: typeof cart): boolean => {
    const paymentCart = cartOverride ?? splitCartOverride ?? cartRef.current;
    const unresolved = (paymentCart || [])
      .filter((it) => resolveCartItemVatRate(it) === undefined)
      .map((it) => `• ${it.name}`);

    if (unresolved.length === 0) return true;

    showAlertModal(
      'KDV Oranı Belirlenemedi',
      'Aşağıdaki ürünlerin KDV oranı okunamadığı için fiş kesilmedi:\n\n' +
      `${unresolved.join('\n')}\n\n` +
      'Fişin KDV\'siz kesilmemesi için işlem durduruldu. Ürünlerin stok kartındaki ' +
      'KDV oranını kontrol edip tekrar deneyin.'
    );
    return false;
  };

  const buildRestP2PReceiptItemsJson = (cartOverride?: typeof cart): string | null => {
    try {
      const paymentCart = cartOverride ?? splitCartOverride ?? cartRef.current;
      if (!paymentCart || paymentCart.length === 0) return null;
      const items = paymentCart.map((it) => {
        // Eskiden burada `it.vatRate ?? product?.vatRate ?? 0` vardı: sepette 0
        // duruyorsa `??` bunu geçerli bir oran sayıp stok kartına hiç bakmıyor ve
        // fiş KDV0 olarak kesiliyordu. Restoran masasından gelen kalemler oranı
        // taşımadığı için sık sık bu duruma düşüyordu.
        const vatRate = resolveCartItemVatRate(it);

        // Pavo fiyatları KDV dahil bekler. Stok kartında fiyatı KDV hariç olan
        // ürün (ör. Soda 5,00 + %10) KDV eklenerek gönderilir; yoksa kalemler
        // toplamı sepet toplamından düşük kalır ve Pavo "Sipariş kalemleri
        // toplam ücreti ... eşit olmalıdır" hatası verir.
        const grossPrice = cartItemGrossUnitPrice(it);

        // Pavo Quantity integer constraint'i (yukarıdaki yorum)
        const isFractionalQty = !Number.isInteger(it.quantity);
        // Ambalaj satırı: 2 × KOLİ 540,00 (adet fiyatı kuruşa yuvarlanınca toplam kaymasın).
        const pkgCount = packageCount(it);
        const pavoQuantity = pkgCount ?? (isFractionalQty ? 1 : it.quantity);
        const pavoUnitPrice = pkgCount != null
          ? Number(packageUnitPrice(grossPrice, it.packageFactor as number).toFixed(2))
          : isFractionalQty
            ? Number((grossPrice * it.quantity).toFixed(2))
            : Number(grossPrice.toFixed(2));
        // Pavo kuralı: Quantity × UnitPrice = satır toplamı.
        const total = Number((pavoUnitPrice * pavoQuantity).toFixed(2));
        const pavoName = pkgCount != null
          ? packageLineName(it)
          : isFractionalQty
            ? `${it.name} (×${Number(it.quantity.toFixed(3)).toString()})`
            : it.name;

        return {
          Name: pavoName,
          Quantity: pavoQuantity,
          UnitPrice: pavoUnitPrice,
          Total: total,
          // Pavo tarafı `resolvedSingleVatRate` bu alana bakar → dinamik
          // KDV10 / KDV20 / KDV1 üretimi için gerekli.
          ...(vatRate === undefined ? {} : { VatRate: vatRate }),
          // Ağırlıklı baskın oran seçimi için Pavo'nun kullandığı alan.
          NetTotal: total,
          GrossTotal: total,
        };
      });

      // Kalem başına kuruş yuvarlaması toplamı sepet toplamından birkaç kuruş
      // kaydırabilir (KDV hariç fiyatlarda). Pavo kalemler toplamı = ödeme
      // tutarı ister; küçük farkı adedi 1 olan bir kaleme yansıt.
      const expectedTotal = calculateTotalFromCart(paymentCart);
      const itemsTotal = Number(items.reduce((s, x) => s + x.Total, 0).toFixed(2));
      const diff = Number((expectedTotal - itemsTotal).toFixed(2));
      if (diff !== 0 && Math.abs(diff) <= 0.05) {
        const line = items.find((x) => x.Quantity === 1 && x.UnitPrice + diff > 0);
        if (line) {
          const fixed = Number((line.UnitPrice + diff).toFixed(2));
          line.UnitPrice = fixed;
          line.Total = fixed;
          line.NetTotal = fixed;
          line.GrossTotal = fixed;
        } else {
          console.warn(`[RestP2P] Kalem toplamı (${itemsTotal}) ≠ sepet toplamı (${expectedTotal}); düzeltilecek tek adetli kalem yok`);
        }
      } else if (diff !== 0) {
        console.warn(`[RestP2P] Kalem toplamı (${itemsTotal}) ≠ sepet toplamı (${expectedTotal}), fark ${diff}`);
      }
      return JSON.stringify(items);
    } catch (e) {
      console.warn('[RestP2P] receiptItemsJson üretilemedi:', e);
      return null;
    }
  };

  // ───────────────────────────────────────────────
  // ✅ Ödeme niyeti yardımcıları (Rest-P2P / karma atomik akış)
  // ───────────────────────────────────────────────
  /** PaymentDetails → niyetin ödeme türü */
  const intentKindOf = (pd: PaymentDetails): PaymentIntentKind => {
    const pos = Object.values(pd.paidByPosMap || {}).some((a) => (a as number) > 0);
    const cash = (pd.paidByCash || 0) > 0;
    return pos && cash ? 'karma' : pos ? 'pos' : 'nakit';
  };

  /**
   * Rest-P2P sonucu parayı KESİN olarak almamış mı? Yalnızca cihazın/sunucunun açıkça
   * reddettiği ya da isteğin cihaza hiç ulaşmadığı durumlar. Zaman aşımı, ağ kopması,
   * doğrulanamayan (UNKNOWN / RECONCILIATION_*) sonuçlar belirsizdir → niyet terk edilmez;
   * sunucu PavoRequestId ile cihazdan gerçek sonucu öğrenir.
   */
  const restP2PMoneySurelyNotTaken = (outcome: RestP2PPaymentOutcome): boolean => {
    if (outcome.status === 'cancelled') return true;
    if (outcome.status !== 'failed') return false;
    const code = String(outcome.errorCode || '').toUpperCase();
    return code === 'DEVICE_OFFLINE' || code === 'NOT_FOUND' || code === 'FAILED' || code.startsWith('HTTP_');
  };

  /**
   * Rest-P2P: cihaza gitmeden ÖNCE ödeme niyetini yazar (mali alanlar boş istekle).
   * Hata fırlatmaz; istek hazırlanamazsa niyet atlanır (ödeme eskisi gibi devam eder).
   */
  const openRestP2PIntent = async (
    key: string,
    pavoRequestId: string,
    saveDetails: PaymentDetails,
    deviceCode: string | null | undefined
  ): Promise<void> => {
    let payload: QuickSalePartialWithInvoiceRequest;
    try {
      payload = buildPartialAtomicRequest({ ...saveDetails, fiscalData: undefined }, key);
    } catch (e) {
      console.warn('[PaymentIntent] Rest-P2P niyet isteği hazırlanamadı (ödeme devam ediyor):', e);
      return;
    }
    await openPaymentIntent({
      key,
      endpoint: 'process-partial-with-invoice',
      payload,
      amount: saveDetails.totalPaid,
      paymentKind: intentKindOf(saveDetails),
      fiscalDevice: 'pavo_rest_p2p',
      deviceCode: deviceCode ?? null,
      pavoRequestId,
    });
  };

  /**
   * Rest-P2P akışı: Pavo cihazına isteği yollar, modal açar, sonucu bekler.
   *
   * Outcome:
   *   - 'completed'              → Pavo başarıyla ödemeyi aldı. Çağıran
   *                                 atomik SQL kaydını tetikler.
   *   - 'continue_without_fiscal'→ Pavo'ya ulaşılamadı/hata oluştu fakat
   *                                 kullanıcı modal'da "Yine de Devam Et"
   *                                 dedi. Çağıran atomik SQL kaydını
   *                                 tetikler ama mali fiş kesilmediğini
   *                                 işaretler (fiscalData.source).
   *   - 'failed'                 → Hata + kullanıcı iptal etti / cancelled.
   *                                 Sepet açık kalır.
   *
   * NOT: Failed durumunda promise hemen resolve OLMAZ — modal'da
   * kullanıcı "Yine de Devam Et" veya "İptal" basana kadar bekler.
   * REST entegrasyonundaki "Yazarkasa Bağlı Değil → Evet, Devam Et"
   * davranışının REST-P2P karşılığıdır.
   */
  const runRestP2PFlow = async (
    mode: 'cash' | 'pos' | 'mixed',
    parts: RestP2PPaymentPart[],
    total: number,
    /**
     * Ödeme niyeti: verilirse cihaza gitmeden ÖNCE sunucuya yazılır (PavoRequestId ile).
     * saveDetails = cihaz sonrası kaydedilecek PaymentDetails (mali alanlar hariç).
     */
    intent?: { key: string; requestId: string; saveDetails: PaymentDetails }
  ): Promise<'completed' | 'failed' | 'continue_without_fiscal'> => {
    if (!isRestP2PEnabled) return 'failed';
    const deviceCode = screenSettings?.pavoRestP2PDeviceScreenCode as string;
    if (!deviceCode) {
      showAlertModal('Rest-P2P Hatası', 'Rest-P2P için Pavo cihazı seçilmemiş. Lütfen ayarlardan cihaz seçin.');
      return 'failed';
    }
    if (total <= 0 || parts.length === 0) {
      showAlertModal('Hata', 'Geçersiz ödeme tutarı.');
      return 'failed';
    }

    if (!ensureCartVatRatesResolved()) return 'failed';

    // Yeni akış başlıyor — önceki satış numarası ve e-belge bilgilerini
    // temizle ki bayat değer sonraki satışa/kulüp girişi depozitosuna sızmasın.
    lastRestP2PSaleNumberRef.current = null;
    lastRestP2PEttnRef.current = null;
    lastRestP2PInvoiceNoRef.current = null;
    lastRestP2PInquiryLinkRef.current = null;
    setRestP2PMode(mode);
    setRestP2PParts(parts);
    setRestP2PTotal(total);
    setRestP2PProgress(null);
    setRestP2PMessage(null);
    setRestP2PErrorCode(null);
    setRestP2PStatus('waiting');
    setRestP2PModalOpen(true);

    // Phase 2: Fatura Türü modalından seçilmiş CustomerParty + NotificationPhone
    // varsa payload'a ekle. PavoPay intent'i CustomerParty'ye bakıp E-Fatura vs
    // E-Arşiv ayrımını otomatik yapacak.
    const pendingCp = getPendingCustomerParty();
    const pendingPhone = getPendingNotificationPhone();

    // Mutfak entegrasyonu: Pavo cihazının kestiği fişin üstüne/altına şirket adı
    // + sipariş kısa kodu basmak için (cash path'teki Pavopay kuralının aynısı).
    // @ts-ignore - useKitchenScreen screenSettings'de var
    const useKitchenForRestP2P = screenSettings?.useKitchenScreen || false;
    const kitchenOrderCodeForRestP2P = (useKitchenForRestP2P && reservedShortCode && !isRestaurantMode)
      ? reservedShortCode
      : null;
    const receiptCompanyNameForRestP2P = useKitchenForRestP2P
      // @ts-ignore - receiptDisplaySettings screenSettings'de var
      ? (screenSettings?.receiptDisplaySettings?.companyName || companyName || null)
      : null;

    // Timeout politikası (CRITICAL_API_DEVELOPMENT_RULES dışı, planda sabit):
    //   - Tek ödeme (cash/pos): 100s — Pavo 90s+10s'sine yakın.
    //   - Karma ödeme (mixed): 180s — parçalı süreç daha uzun olabilir.
    const restP2PTimeoutMs = mode === 'mixed' ? 180_000 : 100_000;

    // ✅ Ödeme niyeti: cihaza gitmeden ÖNCE (en fazla 4 sn; yazılamazsa ödeme devam eder)
    if (intent) {
      await openRestP2PIntent(intent.key, intent.requestId, intent.saveDetails, deviceCode);
    }

    // ✅ NUMARA GARANTİSİ: Cihaza istek gidiyor — deneme başladı (iptal/hata olsa bile numara yakılır)
    markPaymentAttempt();
    const outcome = await restP2PPaymentService.requestPayment(
      {
        // Niyetteki PavoRequestId ile aynı kimlik (yoksa servis üretir)
        requestId: intent?.requestId,
        deviceCode,
        paymentMode: mode,
        totalAmount: total,
        parts,
        receiptItemsJson: buildRestP2PReceiptItemsJson(),
        companyId: companyId ?? null,
        branchId: activeBranchId ?? null,
        screenId: screenId ? Number(screenId) : null,
        cashierName: effectiveCreatedBy,
        sourceLabel: isRestaurantMode ? tableName || null : null,
        customerParty: pendingCp ?? null,
        notificationPhone: pendingPhone && pendingPhone.length > 0 ? pendingPhone : null,
        kitchenOrderCode: kitchenOrderCodeForRestP2P,
        receiptCompanyName: receiptCompanyNameForRestP2P,
      },
      {
        onProgress: (p) => setRestP2PProgress(p),
        timeoutMs: restP2PTimeoutMs,
        // Reconciliation tetiklendiğinde modal'a "doğrulanıyor..." state'i göster.
        // paranoid mod modal zaten settle ettiği için UI'a yansımayacak; sadece
        // timeout/presence/hub-state durumlarında kullanıcı doğrulama beklerken
        // bilgilendirilir.
        onVerifying: ({ reason }) => {
          if (reason === 'paranoid') return;
          setRestP2PStatus('verifying');
          setRestP2PMessage('Pavo cihazından ödeme durumu doğrulanıyor...');
        },
      }
    );

    restP2PActiveRequestIdRef.current = outcome.requestId;
    // Para kesin alınmadıysa niyet terk edilir (belirsiz sonuçta sunucu cihaza kendisi sorar)
    const abandonIntentIfNotTaken = () => {
      if (intent && restP2PMoneySurelyNotTaken(outcome)) {
        abandonPaymentIntent(
          intent.key,
          outcome.status === 'cancelled'
            ? `Pavo iptal: ${outcome.reason || ''}`
            : outcome.status === 'failed' ? `Pavo hata (${outcome.errorCode}): ${outcome.message || ''}` : null
        );
      }
    };

    if (outcome.status === 'completed') {
      // Pavo satış numarasını sakla — kulüp girişi POS depozitosunun
      // karta "POS" olarak (depositPavoPaySaleNumber dolu) kaydedilmesi için.
      // ⚠️ Normal SignalR tamamlanma yolunda `outcome.result` HAM cihaz cevabıdır
      // ve satış no `PavoSaleNumber` (PascalCase) gelir; reconciliation yolunda ise
      // camelCase `pavoSaleNumber`. Bu yüzden her iki casing'i de tolere et —
      // aksi halde kulüp girişi POS depozitosu satış no'suz kalıp iadede NAKİT'e düşüyordu.
      const _rp2pResult = outcome.result as any;
      lastRestP2PSaleNumberRef.current =
        _rp2pResult?.pavoSaleNumber ||
        _rp2pResult?.PavoSaleNumber ||
        _rp2pResult?.saleNumber ||
        _rp2pResult?.SaleNumber ||
        null;
      // ✅ E-belge bilgileri: cihaz dispatcher'ı Ettn/InvoiceNo/InquiryLink'i
      // üst seviyede yollar (PascalCase); reconciliation yolunda camelCase
      // gelebilir. Eski cihaz sürümlerinde üst seviye alan yoksa RawSaleJson
      // veya PaymentResultJson içindeki FinancialDocuments'tan çıkarılır.
      const _rp2pEDoc = extractRestP2PEDocFields(_rp2pResult);
      lastRestP2PEttnRef.current = _rp2pEDoc.ettn;
      lastRestP2PInvoiceNoRef.current = _rp2pEDoc.invoiceNo;
      lastRestP2PInquiryLinkRef.current = _rp2pEDoc.inquiryLink;
      console.log('[runRestP2PFlow] 💾 Pavo satış no yakalandı:', lastRestP2PSaleNumberRef.current, {
        ettn: lastRestP2PEttnRef.current,
        invoiceNo: lastRestP2PInvoiceNoRef.current
      });
      setRestP2PStatus('success');
      setRestP2PMessage(null);
      setRestP2PErrorCode(null);
      // Phase 2: Başarılı satıştan sonra pending CustomerParty'yi temizle ki
      // sonraki satışa taşmasın. Başarısız/iptal durumunda kullanıcının yeniden
      // denemesi için state aynı kalır.
      try { clearPendingCustomerParty(); } catch (_) {}
      // Kısa süre başarı mesajı göster, sonra otomatik kapat
      setTimeout(() => {
        setRestP2PModalOpen(false);
        restP2PActiveRequestIdRef.current = null;
      }, 1200);
      return 'completed';
    }

    if (outcome.status === 'cancelled') {
      // Kullanıcı / sistem iptali → "Yine de Devam Et" sunulmaz, modal
      // sadece kapatılabilir. Promise burada hemen resolve eder.
      setRestP2PStatus('cancelled');
      setRestP2PMessage(outcome.reason || 'Ödeme iptal edildi');
      setRestP2PErrorCode(null);
      abandonIntentIfNotTaken();
      return 'failed';
    }

    // status === 'failed' → kullanıcıya modal'da "Yine de Devam Et" / "İptal"
    // seçimi sunulur. Promise kullanıcı kararını bekler.
    // (Discriminated union: failed branch'ında `errorCode` + `message` zorunlu.)
    setRestP2PStatus('error');
    setRestP2PMessage(outcome.message || 'Ödeme başarısız');
    setRestP2PErrorCode(outcome.errorCode || null);

    return new Promise<'failed' | 'continue_without_fiscal'>((resolve) => {
      restP2PContinueResolveRef.current = (decision) => {
        restP2PContinueResolveRef.current = null;
        setRestP2PModalOpen(false);
        restP2PActiveRequestIdRef.current = null;
        // "İptal": satış kaydedilmeyecek → para kesin alınmadıysa niyet terk edilir.
        // "Yine de Devam Et": satış kaydedilecek → niyet açık kalır (cihaz sonucu kayıtta bildirilir).
        if (decision !== 'continue') abandonIntentIfNotTaken();
        resolve(decision === 'continue' ? 'continue_without_fiscal' : 'failed');
      };
    });
  };

  /**
   * Modal'daki "Yine de Devam Et" butonuna basıldığında çağrılır.
   * Pending olan failure outcome'unu `continue_without_fiscal` olarak
   * çözer; çağıran (processPaymentAfterPartialModal vs.) atomik SQL
   * akışını mali kayıt olmadan başlatır.
   */
  const handleRestP2PContinueWithoutDevice = () => {
    if (restP2PContinueResolveRef.current) {
      console.log('[RestP2P] Kullanıcı "Yine de Devam Et" seçti — fiscal\'siz akış tetikleniyor');
      restP2PContinueResolveRef.current('continue');
    }
  };

  /**
   * Nakit fire-and-forget yolu cihaz offline'a takıldığında çağrılır.
   * `runRestP2PFlow` kullanmıyoruz çünkü o `requestPayment` (cevap bekleyen)
   * çağrısı yapıyor — fire-and-forget akışında HTTP zaten reddedilmiş, modal'ı
   * doğrudan error state'inde açıp kullanıcıya "Yine de Devam Et" / "İptal"
   * seçeneği sunuyoruz. (REST entegrasyonundaki "Yazarkasa Bağlı Değil →
   * Evet, Devam Et" davranışıyla eşdeğer.)
   *
   * Resolve değeri:
   *   - true  → atomik SQL akışı tetiklenir (fiscalData.source = ..._skipped_offline)
   *   - false → akış iptal edilir
   */
  const confirmContinueAfterPavoOffline = (
    errorMessage: string,
    errorCode?: string,
    paymentMode: 'cash' | 'pos' | 'mixed' = 'cash',
    totalAmount: number = 0,
    parts: RestP2PPaymentPart[] = []
  ): Promise<boolean> => {
    setRestP2PMode(paymentMode);
    setRestP2PParts(parts);
    setRestP2PTotal(totalAmount);
    setRestP2PProgress(null);
    setRestP2PStatus('error');
    setRestP2PMessage(errorMessage);
    setRestP2PErrorCode(errorCode || 'DEVICE_OFFLINE');
    setRestP2PModalOpen(true);

    return new Promise<boolean>((resolve) => {
      restP2PContinueResolveRef.current = (decision) => {
        restP2PContinueResolveRef.current = null;
        setRestP2PModalOpen(false);
        restP2PActiveRequestIdRef.current = null;
        resolve(decision === 'continue');
      };
    });
  };

  /**
   * Kullanıcı Rest-P2P bekleme modalında "İptal Et"e bastığında çağrılır.
   */
  const handleRestP2PCancel = async () => {
    const rid = restP2PActiveRequestIdRef.current;
    if (rid) {
      await restP2PPaymentService.cancelPayment(rid);
    }
    // requestPayment promise'i `OnQuickSalePaymentCancelled` veya timeout ile
    // settle olacak; burada direkt modal kapatmıyoruz ki tutarlı state kalsın.
    setRestP2PStatus('cancelled');
    setRestP2PMessage('İptal isteği gönderildi');
  };

  const handleRestP2PClose = () => {
    // Eğer error state'inde "kullanıcı kararı" bekleniyorsa, kapat = iptal.
    if (restP2PContinueResolveRef.current) {
      restP2PContinueResolveRef.current('cancel');
      return;
    }
    setRestP2PModalOpen(false);
    restP2PActiveRequestIdRef.current = null;
  };

  /**
   * Karma / Rest-P2P / modal içi yazarkasa akışının atomik isteği (process-partial-with-invoice).
   * Ödeme niyeti için cihazdan ÖNCE (mali alanlar boş) ve kayıt için cihazdan SONRA (mali
   * alanlar dolu) AYNI fonksiyonla üretilir: iki istek mali alanlar dışında birebir aynıdır.
   * Şube deposu zenginleştirmesi (ağ çağrısı) burada YAPILMAZ — yalnızca gerçek kayıttan önce.
   */
  const buildPartialAtomicRequest = (
    paymentDetails: PaymentDetails,
    idempotencyKey: string
  ): QuickSalePartialWithInvoiceRequest => {
    const isPosUsed = Object.values(paymentDetails.paidByPosMap).some((amount) => amount > 0);
    const isCashUsed = paymentDetails.paidByCash > 0;
    const posEntries = Object.entries(paymentDetails.paidByPosMap).filter(([_, amount]) => (amount as number) > 0);
    let posAmount = 0;
    let posOption: any = null;
    if (posEntries.length > 0) {
      const [posId, amount] = posEntries[0];
      posAmount = amount as number;
      posOption = posOptions.find(p => p.id === posId);
    }
    // Deposito items'ları SQL API'den filtrele (stok hareketi gerekmez)
    const partialRegularCart = cart.filter((item: any) => !item.id?.startsWith('DEPOSIT-'));

    const cartItemsForApi = partialRegularCart.map((item: any) => {
      const product = allInventoryItems.find((inv) => inv.id === item.id);
      // ⚠️ Kulüp girişi paket/servis kalemleri (giriş, hizmet) ya da stok
      // deposu tanımsız ürünlerde depo çözülemeyebilir. Satışı bloklamak
      // yerine Guid.Empty gönder: backend (WarehouseId != Guid.Empty filtresi)
      // bu kalemin stok hareketini atlar; fatura/ödeme kaydı sürer.
      const EMPTY_WAREHOUSE_GUID = '00000000-0000-0000-0000-000000000000';
      const resolvedWarehouseId = resolveEffectiveWarehouseId(item.id, product?.categoryId, screenSettings, branchWarehouseId);
      const warehouseId = resolvedWarehouseId || EMPTY_WAREHOUSE_GUID;
      if (!resolvedWarehouseId) {
        console.warn(`[processPaymentAfterPartialModal] Depo çözümlenemedi, stok hareketi atlanacak: ${item.name} (${item.id})`);
      }

      // ✅ KDV Dahil/Hariç bilgisi: Sepetteki vatStatus'a göre PriceType belirle
      const priceType = item.vatStatus === 'included' ? 'VAT_INCLUDED' : 'VAT_EXCLUDED';

      return {
        ProductId: item.id,
        ProductName: product?.name || item.name,
        WarehouseId: warehouseId,
        Quantity: item.quantity,
        UnitPrice: item.price,
        VatRate: product?.vatRate || 0,
        SctRate: product?.sctRate || 0,
        // ✅ KDV Dahil/Hariç bilgisi (fatura hesaplaması için)
        PriceType: priceType,
        // ✅ Reçete/Menü bazlı stok düşme için
        HasRecipe: product?.hasRecipe || false,
        HasMenu: product?.hasMenu || false,
        // ✅ YENİ: Opsiyonlu menü için seçilen opsiyonlar
        SelectedOptions: (item as any).selectedOptions || null,
        TotalPriceDifference: (item as any).totalPriceDifference || 0,
        // ✅ YENİ: İndirim kaydı (faturaya gerçek indirim için)
        ...buildItemDiscountFields(item)
      };
    });

    // ✅ ATOMİK REQUEST - TEK API ÇAĞRISI (POS + NAKİT + STOK + FATURA)
    // ✅ Restoran modunda Description "Restoran Satışı" olmalı
    const partialPaymentDescription = isRestaurantMode 
      ? (isPosUsed && isCashUsed ? 'Restoran Satışı (Karma)' : isPosUsed ? 'Restoran Satışı (POS)' : 'Restoran Satışı (Nakit)')
      : (isPosUsed && isCashUsed ? 'Karma Ödeme' : isPosUsed ? 'POS Satış' : 'Hızlı Satış (Nakit)');
    
    return {
      IdempotencyKey: idempotencyKey,

      CompanyName: companyName || null,
      ScreenId: screenId ? parseInt(screenId.toString(), 10) : null,
      AccountId: parseInt(selectedCustomer.id.toString(), 10),
      AccountName: selectedCustomer.companyName,
      // POS Bilgileri (varsa)
      BankAccountId: posOption?.bankAccountId || 0,
      BankSubAccountId: posOption?.subAccountId || null,
      BankAccountName: posOption?.bankAccountName || null, // ✅ DÜZELTME: label değil, gerçek banka adı
      PosAmount: posAmount,
      // Nakit Bilgileri (varsa)
      CashboxId: cashPaymentCashboxId ? parseInt(cashPaymentCashboxId) : 0,
      CashboxName: cashPaymentCashboxName || null, // ✅ YENİ: Gün sonu raporu için kasa adı
      CashAmount: paymentDetails.paidByCash,
      // Ortak
      Currency: 'TRY',
      Description: partialPaymentDescription, // ✅ YENİ: Restoran modu için açıklama
      BranchId: userDetails?.branchesId ? parseInt(userDetails.branchesId, 10) : null, // ✅ Gün sonu raporu için kritik! (string → int)
      BranchName: userDetails?.branchesId ? getBranchName(userDetails.branchesId) : null, // ✅ YENİ: Kasa hareket raporu için şube adı
      IsRestaurantMode: isRestaurantMode,
      CashierId: currentCashierId || null,
      CashierName: currentCashierName || null,
      // POS Detayları (PavoPay'den gelebilir)
      AuthCode: paymentDetails.fiscalData?.AuthCode,
      BatchNo: paymentDetails.fiscalData?.BatchNo,
      MaskedCardNumber: paymentDetails.fiscalData?.MaskedCardNumber,
      FiscalTransactionId: paymentDetails.fiscalData?.FiscalTransactionId || paymentDetails.fiscalData?.SaleNumber,
      CardType: paymentDetails.fiscalData?.CardType,
      CreatedBy: effectiveCreatedBy,
      IsFiscalReceipt: useFiscalDeviceForCash,
      PavoPaySaleNumber: paymentDetails.fiscalData?.SaleNumber || null,
      PavoPayInvoiceNo: paymentDetails.fiscalData?.InvoiceNo || null,
      EInvoiceETTN: paymentDetails.fiscalData?.EInvoiceETTN || null,
      PavoPayInquiryLink: paymentDetails.fiscalData?.PavoPayInquiryLink || null,
      // ✅ YENİ: Sepete uygulanan indirim kaydı (fiş başlığı)
      ...buildHeaderDiscountFields(cart, appliedDiscountType, lastAppliedDiscountAmount),
      Items: cartItemsForApi,
    };
  };

  // Parçalı ödeme modalından sonra ödemeyi işleyecek fonksiyon
  const processPaymentAfterPartialModalInner = async (
    paymentDetails: PaymentDetails,
    /**
     * Cihazdan ÖNCE açılmış ödeme niyeti (Rest-P2P): kayıt aynı anahtarla yapılır ve
     * kayıttan önce cihaz sonucu bildirilir. Verilmezse anahtar burada üretilir ve
     * niyet kayıttan hemen önce açılır (cihazsız / modal içi yazarkasa akışı).
     */
    intentOpts?: { intentKey?: string; pavoRequestId?: string | null; fiscalResult?: string | null }
  ): Promise<void> => {
    console.log(`[processPaymentAfterPartialModal] Başladı. Customer ID: ${selectedCustomer?.id}`, paymentDetails);

    // ✅ Rest-P2P (pavo_rest_p2p): Fiscal + ödeme tahsilatı Pavo cihazında yapılır.
    // Atomik SQL kayıttan ÖNCE cihaza yolla, bekle; başarı durumunda fiscalProcessed=true
    // ile aynı fonksiyonu yeniden çağırıp mevcut atomik akışı tetikle.
    if (isRestP2PEnabled && !paymentDetails.fiscalProcessed) {
      const isOpenAccountOnly =
        paymentDetails.paidByOpenAccount > 0 &&
        paymentDetails.paidByCash === 0 &&
        !Object.values(paymentDetails.paidByPosMap || {}).some((a: unknown) => (a as number) > 0);

      // Açık hesap saf ödemesi Pavo'ya gitmez (zaten yukarıda da desteklenmiyor)
      if (!isOpenAccountOnly) {
        const { mode, parts, total } = buildRestP2PPartsFromPaymentDetails(paymentDetails);
        if (parts.length > 0 && total > 0) {
          const deviceCode = screenSettings?.pavoRestP2PDeviceScreenCode as string | undefined;

          // ⚠️ "Nakit ödemelerde ÖKC kullan" KAPALI ise nakit parçalarını
          // Pavo'ya göndermeden at. (REST entegrasyonundaki gibi — kasiyer nakiti
          // elden alır, SQL'e yazılır, Pavo'da fiş kesilmez.)
          //
          // → Tüm ödeme nakit + ÖKC kapalı  : Pavo tamamen atlanır, direkt SQL.
          // → Karma (POS + nakit) + ÖKC kapalı: Sadece POS parçaları Pavo'ya gider,
          //   nakit parçaları SQL tarafında kaydedilir.
          // → ÖKC açık: Tüm parçalar eskisi gibi Pavo'ya gider.
          const partsForPavo = useFiscalDeviceForCash
            ? parts
            : parts
                .filter((p) => p.method !== 'cash')
                .map((p, idx) => ({ ...p, order: idx }));
          const totalForPavo = Number(
            partsForPavo.reduce((s, p) => s + p.amount, 0).toFixed(2)
          );
          const effectiveMode: 'cash' | 'pos' | 'mixed' =
            partsForPavo.length === 0
              ? 'cash'
              : partsForPavo.length > 1
              ? 'mixed'
              : partsForPavo[0].method === 'cash'
              ? 'cash'
              : 'pos';

          // Pavo'ya gidecek bir şey kalmadıysa (tüm ödeme nakit + ÖKC kapalı)
          // → Pavo atla, direkt SQL atomik akışına geç.
          if (partsForPavo.length === 0 || totalForPavo <= 0) {
            console.log(
              '[processPaymentAfterPartialModal] 🟢 Rest-P2P: Nakit ÖKC kullanımı kapalı + tüm ödeme nakit → Pavo\'ya gönderilmedi, direkt SQL'
            );
            try { clearPendingCustomerParty(); } catch (_) {}
            return processPaymentAfterPartialModal({
              ...paymentDetails,
              fiscalProcessed: true,
              fiscalData: {
                source: 'pavo_rest_p2p_cash_bypass_all',
                reason: 'useFiscalDeviceForCash=false',
              },
            });
          }

          if (!useFiscalDeviceForCash && partsForPavo.length !== parts.length) {
            console.log(
              `[processPaymentAfterPartialModal] 🟡 Rest-P2P: Nakit ÖKC kapalı → ${parts.length - partsForPavo.length} nakit parça Pavo'dan çıkarıldı, POS parçaları Pavo'ya gidiyor`
            );
          }

          // 💰 Sadece NAKIT ise fire-and-forget: modal AÇMA, cevap BEKLEME
          // (kart okutması yok, kasiyer parayı elden aldı; Pavo sadece fiş kesiyor)
          if (effectiveMode === 'cash') {
            if (!deviceCode) {
              showAlertModal('Rest-P2P Hatası', 'Rest-P2P için Pavo cihazı seçilmemiş. Lütfen ayarlardan cihaz seçin.');
              return;
            }
            let cashFFSucceeded = true;
            // ✅ Ödeme niyeti: anahtar + Pavo istek kimliği cihaza gitmeden ÖNCE belli olur
            const ffIntentKey = newPaymentIntentKey();
            const ffRequestId = createRestP2PRequestId();
            const ffSaveDetails: PaymentDetails = { ...paymentDetails, fiscalProcessed: true };
            try {
              // Phase 2: Nakit fişi de CustomerParty + NotificationPhone
              // taşıyabilir (örn. VKN'li mükellef NAKIT öder → E-Fatura fişi).
              const pendingCpCash = getPendingCustomerParty();
              const pendingPhoneCash = getPendingNotificationPhone();
              // Mutfak entegrasyonu: Nakit fire-and-forget Pavo fişine üst/alt bilgi
              // @ts-ignore - useKitchenScreen screenSettings'de var
              const useKitchenForCashFF = screenSettings?.useKitchenScreen || false;
              const kitchenOrderCodeForCashFF = (useKitchenForCashFF && reservedShortCode && !isRestaurantMode)
                ? reservedShortCode
                : null;
              const receiptCompanyNameForCashFF = useKitchenForCashFF
                // @ts-ignore - receiptDisplaySettings screenSettings'de var
                ? (screenSettings?.receiptDisplaySettings?.companyName || companyName || null)
                : null;
              await openRestP2PIntent(ffIntentKey, ffRequestId, ffSaveDetails, deviceCode);
              // ✅ NUMARA GARANTİSİ: Cihaza fire-and-forget gidiyor — deneme başladı
              markPaymentAttempt();
              await restP2PPaymentService.sendCashFireAndForget({
                requestId: ffRequestId,
                deviceCode,
                paymentMode: 'cash',
                totalAmount: totalForPavo,
                parts: partsForPavo,
                receiptItemsJson: buildRestP2PReceiptItemsJson(),
                companyId: companyId ?? null,
                branchId: activeBranchId ?? null,
                screenId: screenId ? Number(screenId) : null,
                cashierName: effectiveCreatedBy,
                sourceLabel: isRestaurantMode ? tableName || null : null,
                customerParty: pendingCpCash ?? null,
                notificationPhone: pendingPhoneCash && pendingPhoneCash.length > 0 ? pendingPhoneCash : null,
                kitchenOrderCode: kitchenOrderCodeForCashFF,
                receiptCompanyName: receiptCompanyNameForCashFF,
              });
              // Fire-and-forget: cevap beklemiyoruz ama CustomerParty pending'i
              // temizliyoruz ki sonraki satışa taşmasın.
              try { clearPendingCustomerParty(); } catch (_) {}
              console.log('[processPaymentAfterPartialModal] 💰 Nakit fire-and-forget Pavo\'ya gönderildi');
            } catch (err: any) {
              cashFFSucceeded = false;
              // ⚠️ Pavo cihazına ulaşılamadı (offline/timeout/network)
              // → kullanıcıya REST-P2P bekleme modal'ı üzerinden "Yine de Devam Et"
              //   seçeneği sun (REST entegrasyonundaki davranışla eşdeğer).
              const continueAnyway = await confirmContinueAfterPavoOffline(
                err?.message || 'Pavo cihazına ulaşılamadı. Nakit fişi iletilemedi.',
                err?.code || 'CASH_FIRE_FORGET_ERROR',
                'cash',
                totalForPavo,
                partsForPavo
              );
              if (!continueAnyway) {
                // Satış kaydedilmeyecek. Fiş isteği Pavo'ya hiç ulaşmadıysa (HTTP reddi) niyet
                // terk edilir; ağ hatasında sonuç belirsiz → sunucu PavoRequestId ile kendisi sorar.
                if (isDeviceFailureCertain(err?.message)) {
                  abandonPaymentIntent(ffIntentKey, `Pavo nakit fişi iletilemedi, kasiyer iptal etti: ${err?.message || ''}`);
                }
                return;
              }
              console.log('[processPaymentAfterPartialModal] ⚠️ Cihaz offline, kullanıcı "Yine de Devam Et" seçti — atomik SQL devam ediyor');
              try { clearPendingCustomerParty(); } catch (_) {}
            }
            return processPaymentAfterPartialModal({
              ...ffSaveDetails,
              fiscalData: cashFFSucceeded
                ? {
                    source: 'pavo_rest_p2p_cash_fire_forget',
                    deviceCode,
                  }
                : {
                    source: 'pavo_rest_p2p_cash_skipped_offline',
                    deviceCode,
                    reason: 'user_continued_without_device',
                  },
            }, {
              intentKey: ffIntentKey,
              pavoRequestId: ffRequestId,
              fiscalResult: cashFFSucceeded ? 'nakit-fis-istegi-iletildi' : 'kasiyer-devam-etti',
            });
          }

          // 💳 POS veya KARMA: modal açılır, cevap beklenir
          // Not: useFiscalDeviceForCash=false iken karma ödemelerde nakit parçaları
          // yukarıda filtrelenip çıkarıldı; Pavo sadece POS parça(lar)ını tahsil edecek.
          // ✅ Ödeme niyeti: anahtar + Pavo istek kimliği cihaza gitmeden ÖNCE belli olur
          const rpIntentKey = newPaymentIntentKey();
          const rpRequestId = createRestP2PRequestId();
          const outcome = await runRestP2PFlow(effectiveMode, partsForPavo, totalForPavo, {
            key: rpIntentKey,
            requestId: rpRequestId,
            saveDetails: { ...paymentDetails, fiscalProcessed: true },
          });
          if (outcome === 'failed') {
            // Sepet açık kalır; atomik kayıt yapılmaz.
            return;
          }
          // ✅ "completed" veya "continue_without_fiscal": atomik SQL akışı tetiklenir.
          //   - completed                → mali fiş Pavo'da kesildi
          //   - continue_without_fiscal  → kullanıcı "Yine de Devam Et" dedi,
          //                                 mali fiş kesilmeden SQL kaydı yapılacak
          //                                 (REST entegrasyonundaki "Yazarkasa Bağlı Değil
          //                                 → Evet, Devam Et" davranışıyla eşdeğer).
          const wasFiscalSkipped = outcome === 'continue_without_fiscal';
          return processPaymentAfterPartialModal({
            ...paymentDetails,
            fiscalProcessed: true,
            fiscalData: wasFiscalSkipped
              ? {
                  source: 'pavo_rest_p2p_skipped_offline',
                  deviceCode,
                  reason: 'user_continued_without_device',
                }
              : {
                  source: 'pavo_rest_p2p',
                  deviceCode,
                  // ✅ E-belge bilgileri (doğrudan REST akışıyla aynı sözleşme):
                  // atomik istekte PavoPaySaleNumber/PavoPayInvoiceNo/EInvoiceETTN'e
                  // map edilir → fatura "pavopay" e-belgesi olarak işaretlenir.
                  SaleNumber: lastRestP2PSaleNumberRef.current || undefined,
                  InvoiceNo: lastRestP2PInvoiceNoRef.current || undefined,
                  EInvoiceETTN: lastRestP2PEttnRef.current || undefined,
                  PavoPayInquiryLink: lastRestP2PInquiryLinkRef.current || undefined,
                },
          }, {
            intentKey: rpIntentKey,
            pavoRequestId: rpRequestId,
            fiscalResult: wasFiscalSkipped ? 'kasiyer-devam-etti' : 'onaylandi',
          });
        }
      }
    }

    // ✅ YENİ: Eğer fiscal işlem modal içinde yapıldıysa (PavoPay parçalı ödeme)
    // Yazarkasa işlemi modal'da yapıldı AMA SQL atomik işlem hala YAPILMALI!
    if (paymentDetails.fiscalProcessed) {
      console.log('[processPaymentAfterPartialModal] ✅ Yazarkasa işlemi modal içinde yapıldı (PavoPay Parçalı Ödeme)');
      console.log('[processPaymentAfterPartialModal] 🔄 SQL ATOMİK İŞLEM BAŞLIYOR (Stok, Cari, Kasa, Fatura)...');

      let atomicRequest: QuickSalePartialWithInvoiceRequest | undefined;
      // Kayıt sunucuda başarılı oldu mu? (Sonraki adımlardan biri düşerse kayıt hatası penceresi AÇILMAZ)
      let fpSaveSucceeded = false;
      // Kayıt sonrası adımlar — ilk denemede de "Yeniden Dene"de de aynı fonksiyon (en fazla 1 kez)
      let fpCompleteAfterSave: ((response: QuickSaleWithInvoiceResponse) => Promise<void>) | null = null;

      try {
        // ✅ Ödeme bilgilerini çıkar
        const isPosUsed = Object.values(paymentDetails.paidByPosMap).some((amount) => amount > 0);
        const isCashUsed = paymentDetails.paidByCash > 0;
        
        // POS bilgilerini al
        const posEntries = Object.entries(paymentDetails.paidByPosMap).filter(([_, amount]) => (amount as number) > 0);
        let posAmount = 0;
        let posOption: any = null;
        
        if (posEntries.length > 0) {
          const [posId, amount] = posEntries[0];
          posAmount = amount as number;
          posOption = posOptions.find(p => p.id === posId);
        }
        
        // Validasyon
        if (!companyId) {
          showAlertModal('Hata', 'Şirket bilgisi bulunamadı.');
          return;
        }
        if (!selectedCustomer) {
          showAlertModal('Hata', 'Cari hesap seçilmedi.');
          return;
        }
        if (isPosUsed && (!posOption || !posOption.bankAccountId)) {
          showAlertModal('Hata', 'POS seçeneği için banka hesap ayarları eksik.');
          return;
        }
        if (isCashUsed && !cashPaymentCashboxId) {
          showAlertModal('Hata', 'Nakit kasa atanmamış.');
          return;
        }
        
        // Cart items'ı API formatına çevir
        // Deposito items'ları SQL API'den filtrele (stok hareketi gerekmez)
        const partialRegularCart = cart.filter((item: any) => !item.id?.startsWith('DEPOSIT-'));

        // ✅ Şube deposu aktifse ürünleri o depoya kaydet (qty=0 ile)
        if (branchWarehouseId && companyId) {
          await enrichCartItemsForBranchWarehouse(partialRegularCart, branchWarehouseId, companyId);
        }

        const fpIntentKey = intentOpts?.intentKey || newPaymentIntentKey();
        atomicRequest = buildPartialAtomicRequest(paymentDetails, fpIntentKey);

        console.log('[processPaymentAfterPartialModal] 🔄 Atomik istek gönderiliyor:', atomicRequest);

        // ✅ Kayıt sonrası adımlar (mutfak siparişi, fiş, masa kapatma, kulüp kartı, sepet)
        // TEK fonksiyonda: ilk denemede de "Yeniden Dene" başarısında da aynısı çalışır,
        // runOnce sayesinde en fazla BİR kez (mutfak fişi asla iki kez basılmaz).
        fpCompleteAfterSave = runOnce(async (atomicResponse: QuickSaleWithInvoiceResponse) => {
        const refNumber = atomicResponse.ReferenceNumber || '';
        const invoiceNumber = atomicResponse.InvoiceNumber || '';
        
        console.log(`[processPaymentAfterPartialModal] ✅ SQL ATOMİK KAYIT BAŞARILI!`);
        console.log(`[processPaymentAfterPartialModal] 📄 Referans: ${refNumber}`);
        console.log(`[processPaymentAfterPartialModal] 📄 Fatura: ${invoiceNumber}`);
        console.log(`[processPaymentAfterPartialModal] 📦 Stok Hareket ID'leri:`, atomicResponse.StockMovementIds);

        // 🍽️ MUTFAK EKRANI ENTEGRASYONU - Sipariş Gönderme (Rest-P2P / Parçalı / Karma)
        // handleCashPaymentForQuickSale ve handlePosPaymentForQuickSale'deki bloğun
        // aynısı — bu akış (Rest-P2P ve atomik parçalı ödeme) de SQL'e yazıldığında
        // mutfak siparişi oluşturulmalı. Hata olursa satış başarılı sayılır, sadece log.
        try {
          // @ts-ignore - useKitchenScreen ve selectedQRMenuId screenSettings'de var
          const useKitchenScreen = screenSettings?.useKitchenScreen || false;
          // @ts-ignore
          const selectedQRMenuId = screenSettings?.selectedQRMenuId || null;

          // Mutfak "Ödeme yöntemi" etiketini ödeme tipine göre belirle
          const kitchenOdemeYontemi = isPosUsed && isCashUsed
            ? 'Karma'
            : isPosUsed
            ? 'POS'
            : isCashUsed
            ? 'Nakit'
            : 'Diğer';

          if (useKitchenScreen && selectedQRMenuId && !isRestaurantMode) {
            console.log('[MUTFAK EKRANI] 🍽️ (Rest-P2P/Parçalı) Mutfak ekranı entegrasyonu AKTIF (Normal Hızlı Satış), sipariş gönderiliyor...');
            console.log('[MUTFAK EKRANI] QR Menü ID:', selectedQRMenuId);

            // Sipariş detaylarını hazırla (deposito items hariç — partialRegularCart zaten filtrelenmiş)
            const siparisDetaylari = partialRegularCart.map((item: any) => {
              const inventoryItem = allInventoryItems.find((inv) => inv.id === item.id);
              return {
                UrunId: item.id,
                UrunAdi: item.name,
                BirimFiyat: item.price,
                Miktar: item.quantity,
                ToplamFiyat: item.price * item.quantity,
                UrunResmi: inventoryItem?.imageUrl || null,
                Aciklama: null,
                CategoryId: inventoryItem?.categoryId || null,
                VatRate: inventoryItem?.vatRate || null,
              };
            });

            const kitchenOrderRequest = {
              QRMenuId: selectedQRMenuId,
              PersonelFirebaseUid: userDetails?.uid || null,
              PersonelName: userDetails ? `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() || 'Hızlı Satış' : 'Hızlı Satış',
              ToplamTutar: calculateTotal(),
              SiparisDetaylari: siparisDetaylari,
              SiparisNotu: null,
              OdemeYontemi: kitchenOdemeYontemi,
              // Rest-P2P / Parçalı akışında Pavo belge ID'si yok; null bırakılır.
              IslemId: null,
              DekontId: null,
              QuickSaleScreenId: screenId ? parseInt(screenId.toString(), 10) : null,
              ReferenceNumber: refNumber || null,
              IsRestaurantMode: false,
              ReservedLongCode: reservedLongCode || null,
              ReservedShortCode: reservedShortCode || null,
              BranchId: activeBranchId || null,
            };

            console.log('[MUTFAK EKRANI] 📤 Sipariş request (Rest-P2P/Parçalı):', kitchenOrderRequest);

            const { default: salesServiceForKitchen } = await import('../../../../services/salesService');
            const kitchenResponse = await salesServiceForKitchen.createKitchenOrderFromQuickSale(kitchenOrderRequest);

            if (kitchenResponse.success) {
              console.log('[MUTFAK EKRANI] ✅ Sipariş mutfak ekranına başarıyla gönderildi! (Rest-P2P/Parçalı)');
              console.log('[MUTFAK EKRANI] 🆔 SİPARİŞ ID:', kitchenResponse.data?.siparisId);
              console.log('[MUTFAK EKRANI] 🔢 SİPARİŞ NO:', kitchenResponse.data?.siparisNo);
              console.log('[MUTFAK EKRANI] 📋 KISA KOD:', kitchenResponse.data?.shortCode);
              showSnackbar(`Sipariş mutfak ekranına gönderildi (${kitchenResponse.data?.shortCode})`, 'info');
            } else {
              console.error('[MUTFAK EKRANI] ❌ Sipariş gönderilemedi (Rest-P2P/Parçalı):', kitchenResponse.message);
            }
          } else if (isRestaurantMode && useKitchenScreen && selectedQRMenuId) {
            console.log('[MUTFAK EKRANI] 🍽️ Restoran modu (Rest-P2P/Parçalı) - Mutfak entegrasyonu AKTIF, ödeme sonrası güncelleme/oluşturma...');

            const siparisDetaylari = partialRegularCart.map((item: any) => {
              const inventoryItem = allInventoryItems.find((inv) => inv.id === item.id);
              return {
                UrunId: item.id,
                UrunAdi: item.name,
                BirimFiyat: item.price,
                Miktar: item.quantity,
                ToplamFiyat: item.price * item.quantity,
                UrunResmi: inventoryItem?.imageUrl || null,
                Aciklama: null,
                CategoryId: inventoryItem?.categoryId || null,
                VatRate: inventoryItem?.vatRate || null,
              };
            });

            const kitchenOrderRequest = {
              QRMenuId: selectedQRMenuId,
              PersonelFirebaseUid: userDetails?.uid || null,
              PersonelName: userDetails ? `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() || 'Restoran Satışı' : 'Restoran Satışı',
              ToplamTutar: calculateTotal(),
              SiparisDetaylari: siparisDetaylari,
              SiparisNotu: null,
              OdemeYontemi: kitchenOdemeYontemi,
              IslemId: null,
              DekontId: null,
              QuickSaleScreenId: screenId ? parseInt(screenId.toString(), 10) : null,
              ReferenceNumber: refNumber || null,
              IsRestaurantMode: true,
              ReservedLongCode: reservedLongCode || null,
              ReservedShortCode: reservedShortCode || null,
              BranchId: activeBranchId || null,
              IsPaymentUpdate: true,
              TableName: tableName || null,
              CustomerAccountName: selectedCustomer?.companyName || null,
            };

            console.log('[MUTFAK EKRANI] 📤 Restoran ödeme güncelleme request (Rest-P2P/Parçalı):', kitchenOrderRequest);

            const { default: salesServiceForKitchen } = await import('../../../../services/salesService');
            const kitchenResponse = await salesServiceForKitchen.createKitchenOrderFromQuickSale(kitchenOrderRequest);

            if (kitchenResponse.success) {
              console.log('[MUTFAK EKRANI] ✅ Restoran mutfak kaydı güncellendi/oluşturuldu (Rest-P2P/Parçalı)');
              console.log('[MUTFAK EKRANI] 📋 KISA KOD:', kitchenResponse.data?.shortCode);
            } else {
              console.error('[MUTFAK EKRANI] ❌ Restoran mutfak güncelleme başarısız (Rest-P2P/Parçalı):', kitchenResponse.message);
            }
          } else if (isRestaurantMode) {
            console.log('[MUTFAK EKRANI] ℹ️ Restoran modu (Rest-P2P/Parçalı) - Mutfak entegrasyonu kapalı, atlanıyor');
          } else {
            console.log('[MUTFAK EKRANI] ℹ️ Mutfak ekranı entegrasyonu kapalı veya QR menü seçilmemiş (Rest-P2P/Parçalı)');
          }
        } catch (kitchenError) {
          console.error('[MUTFAK EKRANI] ❌ Mutfak ekranı entegrasyonu hatası (Rest-P2P/Parçalı):', kitchenError);
          // Satış başarılı olduğu için kullanıcıya hata gösterme
        }

        // ✅ Fatura kalemlerini hazırla (fiş için)
        const invoiceItems = cart.map((item) => {
          const product = allInventoryItems.find((inv) => inv.id === item.id);
          const vatRate = product?.vatRate || 0;
          const unitPrice = item.price;
          const quantity = item.quantity;
          const lineTotal = unitPrice * quantity;
          const vatAmount = lineTotal * (vatRate / 100);
          const grossTotal = lineTotal + vatAmount;

          return {
            id: `qs-${item.id}-${Date.now()}`,
            itemId: item.id,
            itemName: item.name,
            quantity: quantity,
            unit: product?.unit || 'adet',
            unitPrice: unitPrice,
            vatRate: vatRate,
            sctRate: 0,
            discountRate: 0,
            lineTotal: lineTotal,
            netTotal: lineTotal,
            vatAmount: vatAmount,
            sctAmount: 0,
            grossTotal: grossTotal,
          };
        });

        // ✅ Ödeme tipi belirle
        const paymentType = isPosUsed && isCashUsed ? 'KARMA ÖDEME (POS + NAKİT)' : 
                           isPosUsed ? 'KART' : 'NAKİT';

        // ✅ Fiş yazdır
        try {
          await printReceiptAfterPayment(
            refNumber,
            paymentType,
            invoiceItems,
            undefined,
            reservedShortCode
          );
          console.log('[processPaymentAfterPartialModal] ✅ Fiş yazdırıldı');
        } catch (printError) {
          console.error('[processPaymentAfterPartialModal] Fiş yazdırma hatası:', printError);
          showSnackbar('Fiş yazdırılırken bir hata oluştu, ancak satış işlemi tamamlandı.', 'warning');
        }

        // ✅ YENİ: Restoran modunda adisyonu kapat (CompletedSales'e taşı)
        if (isRestaurantMode && tableId) {
          const effectiveCloseTotal = calculateEffectiveTotal();
          console.log('[RESTORAN-PARÇALI] 🍽️ Restoran modu aktif (Parçalı), adisyon kapatılıyor...', {
            tableId,
            isPosUsed,
            isCashUsed,
            cashAmount: paymentDetails.paidByCash,
            posAmount,
            totalAmount: effectiveCloseTotal,
            restaurantActiveSaleIdOverride,
          });
          try {
            const { restaurantActiveSaleApi } = await import('../../../../services/restaurantApiService');

            // ✅ SEPET BÖLME FIX: Override varsa onu kullan (sol=main, sağ=splitChild).
            // Yoksa klasik davranış — tableId ile ana satışı bul.
            let targetActiveSaleId: number | null = restaurantActiveSaleIdOverride ?? null;
            const numericTableId = parseInt(tableId);
            if (!targetActiveSaleId) {
              console.log('[RESTORAN-PARÇALI] 📡 getByTableId çağrılıyor, tableId:', numericTableId);
              const activeSale = await restaurantActiveSaleApi.getByTableId(numericTableId);
              console.log('[RESTORAN-PARÇALI] 📡 getByTableId yanıtı:', activeSale ? { Id: activeSale.Id, BranchId: activeSale.BranchId, TotalAmount: activeSale.TotalAmount, TableId: activeSale.TableId } : 'NULL');
              targetActiveSaleId = activeSale?.Id ?? null;
            } else {
              console.log('[RESTORAN-PARÇALI] 🎯 Sepet bölme override — kapatılacak ActiveSale Id:', targetActiveSaleId);
            }

            if (targetActiveSaleId) {
              console.log('[RESTORAN-PARÇALI] ✅ Aktif satış bulundu (Parçalı), ID:', targetActiveSaleId);
              
              const partialPaymentMethod = isPosUsed && isCashUsed ? 'Parçalı (Nakit + POS)' : 
                                          isPosUsed ? 'Kredi Kartı' : 'Nakit';
              
              const partialPaymentDetails = JSON.stringify({
                cashAmount: paymentDetails.paidByCash,
                cashboxId: cashPaymentCashboxId,
                cashboxName: cashPaymentCashboxName || null,
                posAmount: posAmount,
                posLabel: posOption?.label || 'POS',
                bankAccountId: posOption?.bankAccountId,
                bankSubAccountId: posOption?.subAccountId,
                bankAccountName: posOption?.bankAccountName || null,
              });
              
              const paymentsArray: Array<any> = [];
              if (isCashUsed && paymentDetails.paidByCash > 0) {
                paymentsArray.push({
                  paymentMethod: 'cash',
                  amount: paymentDetails.paidByCash,
                  cashboxId: cashPaymentCashboxId ? parseInt(cashPaymentCashboxId) : undefined,
                  cashboxName: cashPaymentCashboxName || undefined,
                });
              }
              if (isPosUsed && posAmount > 0) {
                paymentsArray.push({
                  paymentMethod: 'pos',
                  amount: posAmount,
                  bankAccountId: posOption?.bankAccountId || undefined,
                  bankAccountName: posOption?.bankAccountName || undefined,
                  bankSubAccountId: posOption?.subAccountId || undefined,
                  bankSubAccountName: posOption?.label || undefined,
                  authCode: paymentDetails.fiscalData?.AuthCode || undefined,
                  batchNo: paymentDetails.fiscalData?.BatchNo || undefined,
                  maskedCardNumber: paymentDetails.fiscalData?.MaskedCardNumber || undefined,
                  cardType: paymentDetails.fiscalData?.CardType || undefined,
                  fiscalTransactionId: paymentDetails.fiscalData?.FiscalTransactionId || undefined,
                });
              }

              const completePayload = {
                paymentMethod: partialPaymentMethod,
                paymentDetailsJson: partialPaymentDetails,
                totalAmount: effectiveCloseTotal,
                completedBy: userDetails?.uid || null,
                completedByName: effectiveCreatedBy,
                saleNumber: refNumber || undefined,
                invoiceNumber: invoiceNumber || undefined,
                referenceNumber: refNumber || undefined,
                closedByWaiterName: waiterName || undefined,
                isFiscalReceipt: useFiscalDeviceForCash,
                payments: paymentsArray,
              };
              console.log('[RESTORAN-PARÇALI] 📤 completeSale çağrılıyor...', { activeSaleId: targetActiveSaleId, payload: completePayload });
              
              const completeResult = await restaurantActiveSaleApi.completeSale(targetActiveSaleId, completePayload);
              
              console.log('[RESTORAN-PARÇALI] 📥 completeSale yanıtı:', JSON.stringify(completeResult));
              
              if (completeResult.success) {
                console.log('[RESTORAN-PARÇALI] ✅ Adisyon başarıyla kapatıldı! (Parçalı)', completeResult.data);
                showSnackbar(`Masa kapatıldı - ${completeResult.data?.saleNumber}`, 'success');
              } else {
                console.error('[RESTORAN-PARÇALI] ❌ Adisyon kapatılamadı (Parçalı):', completeResult.message);
                showSnackbar(`Uyarı: Adisyon kapatılamadı - ${completeResult.message}`, 'warning');
              }
            } else {
              console.warn('[RESTORAN-PARÇALI] ⚠️ Bu masa için aktif satış bulunamadı (Parçalı), tableId:', numericTableId);
            }
          } catch (restaurantError: any) {
            console.error('[RESTORAN-PARÇALI] ❌ Restoran adisyon kapatma hatası (Parçalı):', restaurantError);
            console.error('[RESTORAN-PARÇALI] ❌ Hata detayı:', restaurantError?.response?.data || restaurantError?.message);
            showSnackbar('Uyarı: Adisyon kapatılırken hata oluştu, ancak ödeme başarılı.', 'warning');
          }
        }
        
        showSnackbar(`Ödeme başarıyla tamamlandı!\nRef: ${refNumber}\nFatura: ${invoiceNumber}`, 'success');
        
        // Kulüp girişi: ödeme sonrası kart atama
        if (onClubEntryPaymentComplete && atomicResponse.QuickSaleReceiptId) {
          onClubEntryPaymentComplete({
            receiptId: atomicResponse.QuickSaleReceiptId,
            referenceNumber: refNumber,
            // Kulüp girişi giriş ödeme yöntemi (basılan buton). Override yoksa
            // 'PARTIAL'. Aktif girişlerde POS/Nakit etiketi buna göre gösterilir.
            paymentMethod: paymentDetails.clubEntryPaymentMethodOverride || 'PARTIAL',
            // Rest-P2P POS depozitosu için Pavo satış no'su; karta
            // depositPavoPaySaleNumber olarak yazılır → iade POS'a gider.
            pavoPaySaleNumber: lastRestP2PSaleNumberRef.current || undefined,
            cart: [...effectiveCart],
          });
        }
        
        // ✅ Sepet bölme için: onPaymentSuccessOverride varsa onu çağır, yoksa clearCart
        if (onPaymentSuccessOverride) {
          onPaymentSuccessOverride();
        } else if (!skipClearCartAfterPayment) {
          clearCart();
        }
        
        // 🎫 NUMARA: Yenileme artık merkezi olarak endPaymentScope (finally) içinde yapılır.
        
        // ✅ YENİ: Restoran modundaysa RestaurantScreen'e geri dön (sepet bölme değilse)
        if (!skipNavigationAfterPayment && isRestaurantMode && restaurantScreenId && navigateToRestaurantScreen) {
          console.log('[RESTORAN] 🔙 Parçalı ödeme tamamlandı, RestaurantScreen\'e geri dönülüyor...');
          setTimeout(() => {
            navigateToRestaurantScreen();
          }, 500);
        }
        }, 'processPaymentAfterPartialModal', atomicRequest.IdempotencyKey);

        // ✅ Ödeme niyeti: cihazdan önce açılmadıysa (cihazsız / modal içi yazarkasa) kayıttan
        // hemen önce açılır; ardından son istek "cihaz sonucu" olarak bildirilir (kayıttan ÖNCE başlar).
        {
          const fpSourceForIntent = String(paymentDetails.fiscalData?.source || '');
          const fpIntentDevice = (fpSourceForIntent === 'pavo_rest_p2p_cash_bypass_all' || intentOpts?.fiscalResult === 'cihaz-yok')
            ? 'yok'
            : fpSourceForIntent.startsWith('pavo_rest_p2p') ? 'pavo_rest_p2p' : telemetryFiscalDevice(selectedFiscalDevice);
          if (!wasPaymentIntentAttempted(fpIntentKey)) {
            await openPaymentIntent({
              key: fpIntentKey,
              endpoint: 'process-partial-with-invoice',
              payload: atomicRequest,
              amount: paymentDetails.totalPaid || null,
              paymentKind: intentKindOf(paymentDetails),
              fiscalDevice: fpIntentDevice,
              deviceCode: paymentDetails.fiscalData?.deviceCode ?? null,
              pavoRequestId: intentOpts?.pavoRequestId ?? null,
            });
          }
          void reportDeviceResult(fpIntentKey, atomicRequest, {
            fiscalReference: paymentDetails.fiscalData?.SaleNumber || paymentDetails.fiscalData?.FiscalTransactionId || null,
            fiscalResult: intentOpts?.fiscalResult ?? (fpIntentDevice === 'yok' ? 'cihaz-yok' : 'onaylandi'),
          });
        }

        // Kurtarma kuyruğu: istek gitmeden önce yazılır, başarıda silinir
        savePendingAtomicPayment('process-partial-with-invoice', atomicRequest, companyId);

        // ✅ NUMARA GARANTİSİ: Atomik SQL kaydı deneniyor — deneme başladı
        markPaymentAttempt();
        const atomicResponse = await salesService.processPartialWithInvoice(atomicRequest);

        if (!atomicResponse.Success) {
          throw new AtomicSaveError(
            `Ödeme kaydı başarısız: ${atomicResponse.Message || atomicResponse.ErrorDetails || 'Bilinmeyen hata'}`,
            atomicResponse
          );
        }

        fpSaveSucceeded = true;
        removePendingAtomicPayment(atomicRequest.IdempotencyKey);
        await fpCompleteAfterSave(atomicResponse);
        return;

      } catch (error: any) {
        console.error('[processPaymentAfterPartialModal] SQL Atomik işlem hatası:', error);
        if (fpSaveSucceeded) {
          // Satış kayıtlı; yalnızca sonraki adımlardan biri düştü → kayıt tekrarlanmaz
          showSnackbar('Satış kaydedildi ancak sonraki adımlardan biri (masa/fiş/mutfak) tamamlanamadı. Lütfen kontrol edin.', 'warning');
          return;
        }

        const fpSource = String(paymentDetails.fiscalData?.source || '');
        const fpIsRestP2P = fpSource.startsWith('pavo_rest_p2p');
        const fpPosUsed = Object.values(paymentDetails.paidByPosMap || {}).some((a) => (a as number) > 0);
        const fpCashUsed = (paymentDetails.paidByCash || 0) > 0;
        handleAtomicSaveFailure(error, {
          showAlertModal,
          showSnackbar,
          request: atomicRequest,
          send: (req) => salesService.processPartialWithInvoice(req),
          onSaved: async (resp) => { if (fpCompleteAfterSave) await fpCompleteAfterSave(resp); },
          intro: 'Yazarkasa işlemi başarılı oldu ancak kayıt yapılamadı.',
          logLabel: 'Karma atomik işlem',
          telemetry: buildSaveTelemetry({
            endpoint: '/api/QuickSaleWithInvoice/process-partial-with-invoice',
            paymentKind: fpPosUsed && fpCashUsed ? 'karma' : fpPosUsed ? 'pos' : 'nakit',
            fiscalDevice: fpIsRestP2P
              ? 'pavo_rest_p2p'
              : fpSource === 'pavo_rest_p2p_cash_bypass_all' ? 'yok' : telemetryFiscalDevice(selectedFiscalDevice),
            fiscalDone: fpIsRestP2P
              ? (fpSource === 'pavo_rest_p2p' || fpSource === 'pavo_rest_p2p_cash_fire_forget')
              : true,
            fiscalReference: paymentDetails.fiscalData?.SaleNumber || paymentDetails.fiscalData?.FiscalTransactionId || null,
            pavoRequestId: intentOpts?.pavoRequestId || restP2PActiveRequestIdRef.current || null,
            amount: paymentDetails.totalPaid || null,
          }),
        });
        return;
      }
    }

    // ✅ ÖDEME TÜRLERİNİ KONTROL ET
    const isPosUsed = Object.values(paymentDetails.paidByPosMap).some((amount: unknown) => (amount as number) > 0);
    const isCashUsed = paymentDetails.paidByCash > 0;
    const isOpenAccountUsed = paymentDetails.paidByOpenAccount > 0;
    
    // PavoPay P2P kontrolü: posMap'te 'pavo_p2p' key'i varsa özel akışa yönlendir
    const pavoP2pEntry = Object.entries(paymentDetails.paidByPosMap).find(([key]) => key === 'pavo_p2p');
    if (pavoP2pEntry && (pavoP2pEntry[1] as number) > 0) {
      console.log("[processPaymentAfterPartialModal] PavoPay P2P ödeme algılandı, cihaz seçim dialog'u açılıyor...");
      setPavoP2pPendingAction('partial');
      setPavoP2pPartialDetails(paymentDetails);
      setPavoP2pSelectOpen(true);
      return;
    }
    
    // ✅ SADECE NAKİT ÖDEME → SQL NAKİT SİSTEMİ
    if (isCashUsed && !isPosUsed && !isOpenAccountUsed) {
      console.log("[processPaymentAfterPartialModal] ✅ SADECE NAKİT ÖDEME ALGILANDI - handleCashPaymentForQuickSale çağrılıyor...");
      await handleCashPaymentForQuickSale();
      return;
    }
    
    // ✅ SADECE POS ÖDEME → SQL POS SİSTEMİ
    if (isPosUsed && !isCashUsed && !isOpenAccountUsed) {
      console.log("[processPaymentAfterPartialModal] ✅ SADECE POS ÖDEME ALGILANDI - handlePosPaymentForQuickSale çağrılıyor...");
      
      // İlk (ve tek) POS seçeneğini al
      const posEntries = Object.entries(paymentDetails.paidByPosMap);
      if (posEntries.length === 0) {
        showAlertModal("Hata", "POS ödeme seçeneği bulunamadı.");
        return;
      }
      
      const [posId, posAmount] = posEntries[0];
      const posOption = posOptions.find(p => p.id === posId);
      
      if (!posOption || !posOption.bankAccountId) {
        showAlertModal("Hata", "POS seçeneği için banka hesap ayarları eksik.");
        return;
      }
      
      // SQL POS fonksiyonunu çağır (zaten çalışan sistem)
      await handlePosPaymentForQuickSale(
        posOption.bankAccountId,
        posOption.subAccountId || null,
        posOption.bankAccountName || null, // ✅ DÜZELTME: label değil, gerçek banka adı (useQuickSaleData'da set ediliyor)
        true // useFiscalDevice: POS için her zaman true
      );
      return;
    }
    
    // ✅ KARMA ÖDEME (NAKİT + POS) → YENİ SQL SİSTEMİ
    // NOT: Karma ödemede ÖNCE POS, SONRA NAKİT işlenir
    if ((isCashUsed && isPosUsed) && !isOpenAccountUsed) {
      console.log("[processPaymentAfterPartialModal] ✅ KARMA ÖDEME (NAKİT + POS) ALGILANDI - Yeni SQL sistemi kullanılıyor...");
      
      // İlk (ve tek) POS seçeneğini al
      const posEntries = Object.entries(paymentDetails.paidByPosMap);
      if (posEntries.length === 0) {
        showAlertModal("Hata", "POS ödeme seçeneği bulunamadı.");
        return;
      }
      
      const [posId, posAmount] = posEntries[0];
      const posOption = posOptions.find(p => p.id === posId);
      
      if (!posOption || !posOption.bankAccountId) {
        showAlertModal("Hata", "POS seçeneği için banka hesap ayarları eksik.");
        return;
      }
      
      // Yeni karma ödeme hook'unu kullan
      // ✅ NUMARA GARANTİSİ: Karma akış (usePartialPayment) cihaz + SQL çağrılarını
      // kendi içinde yapar ve kendisi işaretleyemez — deneme burada işaretlenir.
      markPaymentAttempt();
      await processPartialPayment({
        cashAmount: paymentDetails.paidByCash,
        posAmount: posAmount as number,
        posOption: {
          id: posOption.id,
          label: posOption.label || 'POS',
          bankAccountId: posOption.bankAccountId,
          subAccountId: posOption.subAccountId || null,
          bankAccountName: posOption.bankAccountName || null, // ✅ DÜZELTME: label değil, gerçek banka adı
        },
      });
      
      return;
    }
    
    // ⚠️ AÇIK HESAP ÖDEMESİ - Bu özellik artık desteklenmiyor (SQL'e taşındı)
    if (isOpenAccountUsed) {
      console.log("[processPaymentAfterPartialModal] ⚠️ AÇIK HESAP - Bu özellik Hızlı Satış ekranında desteklenmiyor.");
        showAlertModal(
        "Açık Hesap Desteklenmiyor", 
        "Açık hesap ödemesi bu ekrandan yapılamaz.\n\n" +
        "Lütfen aşağıdaki yöntemlerden birini kullanın:\n" +
        "• Nakit ödeme\n" +
        "• POS ödeme\n" +
        "• Nakit + POS karma ödeme\n\n" +
        "Açık hesap işlemleri için Cari modülünü kullanabilirsiniz."
      );
        return;
      }

    // Bu noktaya ulaşmamalı - tüm ödeme tipleri yukarıda SQL'e yönlendirildi
    console.error("[processPaymentAfterPartialModal] HATA: Beklenmeyen ödeme tipi - bu kod bloğuna ulaşılmamalıydı");
    showAlertModal("Hata", "Beklenmeyen ödeme tipi. Lütfen nakit, POS veya karma ödeme seçin.");
  };

  // Nakit ödeme işleme fonksiyonu
  // skipFiscalDevice: Kullanıcı yazarkasa bağlı değil uyarısını onayladıysa true
  // skipDeptVatCheck: Ingenico departman-KDV uyumsuzluğu modalında "Devam Et" seçildiyse true
  // intentKeyOverride: Ingenico "Yine de Devam Et" yeniden çağrısında ilk denemenin ödeme niyeti anahtarı
  const handleCashPaymentForQuickSaleInner = async (skipFiscalDevice: boolean = false, skipDeptVatCheck: boolean = false, intentKeyOverride?: string) => {
    const startTime = performance.now();
    console.log(`[handleCashPaymentForQuickSale] ⏱️ Başlıyor... (skipFiscalDevice: ${skipFiscalDevice}, skipDeptVatCheck: ${skipDeptVatCheck})`);
    
    if (isProcessingPayment) return;

    // ✅ Beko geç yanıt kontrolü sürüyor: yazarkasa meşgul / önceki fiş belirsiz
    if (useFiscalDeviceForCash && !skipFiscalDevice && bekoLatePollActiveRef.current) {
      showSnackbar('Yazarkasanın son fiş durumu kontrol ediliyor — lütfen birkaç saniye bekleyin.', 'warning');
      return;
    }

    // ✅ Rest-P2P: NAKIT → Pavo cihazına FIRE-AND-FORGET yolla (modal AÇMA)
    // Nakit para zaten fiziksel olarak kasiyerden alınır; Pavo'dan sadece
    // nakit fişi kesilmesini istiyoruz. Pavo'nun cevabını beklemeye gerek yok.
    // Hemen atomik SQL'e geçilir; Pavo arka planda fişi basar.
    //
    // ⚠️ "Nakit ödemelerde ÖKC kullan" ayarı (useFiscalDeviceForCash) KAPALI ise
    // Pavo'ya hiç gönderme — REST entegrasyonundaki davranışın aynısı.
    // (Kasiyer nakit alır, yalnız SQL kaydedilir; hiçbir fiş kesilmez.)
    if (isRestP2PEnabled && useFiscalDeviceForCash && !skipFiscalDevice) {
      const deviceCode = screenSettings?.pavoRestP2PDeviceScreenCode as string | undefined;
      if (!deviceCode) {
        showAlertModal('Rest-P2P Hatası', 'Rest-P2P için Pavo cihazı seçilmemiş. Lütfen ayarlardan cihaz seçin.');
        return;
      }

      // Nakit akışı fire-and-forget olduğu için `runRestP2PFlow`'dan geçmiyor;
      // KDV kontrolü burada ayrıca yapılmalı.
      if (!ensureCartVatRatesResolved()) return;

      // ✅ Kulüp girişi depozito ayrımı (cartRef.current → bayat closure önlenir).
      // Depozito yöntemi tek tiptir: ya tüm depozitolar POS (skipFiscal=false)
      // ya da tümü nakittir (skipFiscal=true).
      const p2pSourceCart = splitCartOverride ?? cartRef.current;
      const hasDeposit = p2pSourceCart.some((ci) => ci.id.startsWith('DEPOSIT-'));
      const posDepositTotal = calculateTotalFromCart(
        p2pSourceCart.filter((ci) => ci.id.startsWith('DEPOSIT-') && !(ci as any).skipFiscal)
      );

      // ───────────────────────────────────────────────
      // Senaryo A: NAKİT giriş + POS depozito → KARMA (mixed) TEK mali fiş.
      //   Nakit giriş ücreti + POS depozito aynı fişte; POS depozito cihazdan
      //   kart okutmayla geçer → tek pavoSaleNumber → karta depositPavoPaySaleNumber
      //   yazılır → eşlenikli iade. (Önceden tümü nakit gidip POS depozito
      //   alınmıyor, fark SQL'de indirim gibi görünüyordu.)
      // ───────────────────────────────────────────────
      if (posDepositTotal > 0) {
        const regularTotal = calculateTotalFromCart(
          p2pSourceCart.filter((ci) => !ci.id.startsWith('DEPOSIT-'))
        );
        const posOption =
          posOptions.find((p) => p.enabled && p.type === 'pos' && p.bankAccountId) ||
          posOptions.find((p) => p.type === 'pos' && p.bankAccountId) ||
          posOptions.find((p) => p.bankAccountId);
        if (!posOption) {
          showAlertModal('Hata', 'POS depozito için tanımlı bir POS/banka hesabı bulunamadı. Lütfen ödeme seçeneklerinde bir POS tanımlayın.');
          return;
        }
        const posId = posOption.id;
        const grand = Number((regularTotal + posDepositTotal).toFixed(2));
        const parts: RestP2PPaymentPart[] = [];
        if (regularTotal > 0) parts.push({ method: 'cash', amount: regularTotal, label: 'Nakit', order: 0 });
        parts.push({ method: 'pos', amount: posDepositTotal, label: posOption.label || 'POS', order: parts.length });
        const mixedMode: 'mixed' | 'pos' = regularTotal > 0 ? 'mixed' : 'pos';

        // Kilit SQL kaydı bitene kadar kalır (sepet o ana kadar ekranda durur).
        setIsProcessingPayment(true);
        try {
          const depositSaveDetails: PaymentDetails = {
            paidByCash: regularTotal,
            paidByPosMap: { [posId]: posDepositTotal },
            paidByOpenAccount: 0,
            totalPaid: grand,
            remainingAmount: 0,
            changeAmount: 0,
            fiscalProcessed: true,
            // Giriş NAKİT butonuyla → aktif girişlerde "Nakit"; depozito POS olarak
            // ayrı (depositPavoPaySaleNumber pavoSaleNumber'dan dolar).
            clubEntryPaymentMethodOverride: 'CASH',
          };
          // ✅ Ödeme niyeti: anahtar + Pavo istek kimliği cihaza gitmeden ÖNCE belli olur
          const depIntentKey = newPaymentIntentKey();
          const depRequestId = createRestP2PRequestId();
          const outcome = await runRestP2PFlow(mixedMode, parts, grand, {
            key: depIntentKey,
            requestId: depRequestId,
            saveDetails: depositSaveDetails,
          });
          if (outcome === 'failed') return;
          const wasFiscalSkipped = outcome === 'continue_without_fiscal';

          await processPaymentAfterPartialModal({
            ...depositSaveDetails,
            fiscalData: wasFiscalSkipped
              ? { source: 'pavo_rest_p2p_skipped_offline', deviceCode, reason: 'user_continued_without_device' }
              : {
                  source: 'pavo_rest_p2p',
                  deviceCode,
                  SaleNumber: lastRestP2PSaleNumberRef.current || undefined,
                  InvoiceNo: lastRestP2PInvoiceNoRef.current || undefined,
                  EInvoiceETTN: lastRestP2PEttnRef.current || undefined,
                  PavoPayInquiryLink: lastRestP2PInquiryLinkRef.current || undefined,
                },
          }, {
            intentKey: depIntentKey,
            pavoRequestId: depRequestId,
            fiscalResult: wasFiscalSkipped ? 'kasiyer-devam-etti' : 'onaylandi',
          });
        } finally {
          setIsProcessingPayment(false);
        }
        return;
      }

      // ───────────────────────────────────────────────
      // Senaryo B: POS depozito YOK. Saf nakit veya nakit + NAKİT depozito.
      //   • Nakit depozito (skipFiscal=true): mali fişe GİRMEZ, SQL'de nakit.
      //   • Hiç depozito yoksa: mevcut davranış (calculateEffectiveTotal) korunur
      //     → normal/restoran nakit satışlarında regresyon olmaz.
      // ───────────────────────────────────────────────
      const cashDepositTotal = calculateTotalFromCart(
        p2pSourceCart.filter((ci) => ci.id.startsWith('DEPOSIT-') && (ci as any).skipFiscal)
      );
      const regularCartForReceipt = p2pSourceCart.filter((ci) => !ci.id.startsWith('DEPOSIT-'));
      // Pavo'ya gidecek mali tutar (nakit depozito hariç): depozito varsa giriş
      // ücreti cartRef'ten; yoksa mevcut effective total.
      const cashFiscalTotal = hasDeposit ? calculateTotalFromCart(regularCartForReceipt) : calculateEffectiveTotal();
      // SQL'e yazılacak toplam nakit = mali tutar + nakit depozito.
      const sqlCashTotal = Number((cashFiscalTotal + cashDepositTotal).toFixed(2));
      // Fişte gösterilecek kalemler: nakit depozito hariç.
      const cashReceiptJson = hasDeposit ? buildRestP2PReceiptItemsJson(regularCartForReceipt) : buildRestP2PReceiptItemsJson();

      if (sqlCashTotal <= 0) {
        showAlertModal('Uyarı', 'Ödeme yapmak için sepette ürün olmalıdır.');
        return;
      }

      setIsProcessingPayment(true);
      let cashFireForgetSucceeded = true;
      const cashSaveDetails: PaymentDetails = {
        paidByCash: sqlCashTotal,
        paidByPosMap: {},
        paidByOpenAccount: 0,
        totalPaid: sqlCashTotal,
        remainingAmount: 0,
        changeAmount: 0,
        fiscalProcessed: true,
        clubEntryPaymentMethodOverride: hasDeposit ? 'CASH' : undefined,
      };
      // ✅ Ödeme niyeti: anahtar + Pavo istek kimliği cihaza gitmeden ÖNCE belli olur
      const cashIntentKey = newPaymentIntentKey();
      const cashRequestId = createRestP2PRequestId();
      // Mali tutar 0 ise (örn. yalnız nakit depozito) Pavo'ya fiş göndermeye
      // gerek yok; doğrudan SQL kaydına geç.
      if (cashFiscalTotal > 0) {
        try {
          // Phase 2: Tek tuş NAKIT yolu — CustomerParty + NotificationPhone
          // pending state'ten oku, fire-and-forget payload'a ekle.
          const pendingCpCash = getPendingCustomerParty();
          const pendingPhoneCash = getPendingNotificationPhone();
          // Mutfak entegrasyonu: Tek tuş nakit fire-and-forget Pavo fişine üst/alt bilgi
          // @ts-ignore - useKitchenScreen screenSettings'de var
          const useKitchenForCashOnly = screenSettings?.useKitchenScreen || false;
          const kitchenOrderCodeForCashOnly = (useKitchenForCashOnly && reservedShortCode && !isRestaurantMode)
            ? reservedShortCode
            : null;
          const receiptCompanyNameForCashOnly = useKitchenForCashOnly
            // @ts-ignore - receiptDisplaySettings screenSettings'de var
            ? (screenSettings?.receiptDisplaySettings?.companyName || companyName || null)
            : null;
          await openRestP2PIntent(cashIntentKey, cashRequestId, cashSaveDetails, deviceCode);
          // ✅ NUMARA GARANTİSİ: Cihaza fire-and-forget gidiyor — deneme başladı
          markPaymentAttempt();
          const cashFfResult = await restP2PPaymentService.sendCashFireAndForget({
            requestId: cashRequestId,
            deviceCode,
            paymentMode: 'cash',
            totalAmount: cashFiscalTotal,
            parts: [{ method: 'cash', amount: cashFiscalTotal, label: 'Nakit', order: 0 }],
            receiptItemsJson: cashReceiptJson,
            companyId: companyId ?? null,
            branchId: activeBranchId ?? null,
            screenId: screenId ? Number(screenId) : null,
            cashierName: effectiveCreatedBy,
            sourceLabel: isRestaurantMode ? tableName || null : null,
            customerParty: pendingCpCash ?? null,
            notificationPhone: pendingPhoneCash && pendingPhoneCash.length > 0 ? pendingPhoneCash : null,
            kitchenOrderCode: kitchenOrderCodeForCashOnly,
            receiptCompanyName: receiptCompanyNameForCashOnly,
          });
          // Kulüp girişi nakit fire-and-forget: satış no asenkron (F6) geldiğinden
          // kart eşleştirmesi için requestId'yi sakla (atama anında okunur).
          try { setLastClubEntryFireForgetRequestId(cashFfResult?.requestId || null); } catch (_) {}
          try { clearPendingCustomerParty(); } catch (_) {}
          console.log('[handleCashPaymentForQuickSale] 💰 Nakit fire-and-forget Pavo\'ya gönderildi, atomik SQL\'e geçiliyor');
        } catch (err: any) {
          cashFireForgetSucceeded = false;
          // ⚠️ Pavo cihazına ulaşılamadı (offline/timeout/network)
          // → "Pavo Hatası" alert atmak yerine kullanıcıya REST-P2P bekleme modal'ı
          //   üzerinden "Yine de Devam Et" / "İptal" seçeneği sun.
          //   (REST entegrasyonundaki "Yazarkasa Bağlı Değil → Evet, Devam Et" eşdeğeri.)
          const continueAnyway = await confirmContinueAfterPavoOffline(
            err?.message || 'Pavo cihazına ulaşılamadı. Nakit fişi iletilemedi.',
            err?.code || 'CASH_FIRE_FORGET_ERROR',
            'cash',
            cashFiscalTotal,
            [{ method: 'cash', amount: cashFiscalTotal, label: 'Nakit', order: 0 }]
          );
          if (!continueAnyway) {
            // Satış kaydedilmeyecek: fiş isteği Pavo'ya kesin ulaşmadıysa niyet terk edilir
            if (isDeviceFailureCertain(err?.message)) {
              abandonPaymentIntent(cashIntentKey, `Pavo nakit fişi iletilemedi, kasiyer iptal etti: ${err?.message || ''}`);
            }
            setIsProcessingPayment(false);
            return;
          }
          console.log('[handleCashPaymentForQuickSale] ⚠️ Cihaz offline, kullanıcı "Yine de Devam Et" seçti — atomik SQL devam ediyor');
          try { clearPendingCustomerParty(); } catch (_) {}
        }
      }
      // Kilit SQL kaydı bitene kadar kalır: sepet o ana kadar ekranda durduğu için
      // erken açılırsa aynı sepet ikinci kez gönderilebiliyor.
      try {
        await processPaymentAfterPartialModal({
          ...cashSaveDetails,
          fiscalData: cashFireForgetSucceeded
            ? {
                source: 'pavo_rest_p2p_cash_fire_forget',
                deviceCode,
              }
            : {
                source: 'pavo_rest_p2p_cash_skipped_offline',
                deviceCode,
                reason: 'user_continued_without_device',
              },
        }, {
          intentKey: cashIntentKey,
          // Mali tutar 0 ise cihaza hiç gidilmedi
          pavoRequestId: cashFiscalTotal > 0 ? cashRequestId : null,
          fiscalResult: cashFiscalTotal <= 0
            ? 'cihaz-yok'
            : cashFireForgetSucceeded ? 'nakit-fis-istegi-iletildi' : 'kasiyer-devam-etti',
        });
      } finally {
        setIsProcessingPayment(false);
      }
      return;
    }

    setIsProcessingPayment(true);
    
    // cartRef.current kullan: closure'dan bağımsız olarak her zaman güncel sepete erişir
    const paymentCart = splitCartOverride ?? cartRef.current;
    console.log(`[handleCashPaymentForQuickSale] 📦 Ödeme yapılacak sepet: ${paymentCart.length} ürün (splitCartOverride: ${splitCartOverride ? 'VAR' : 'YOK'})`);
    
    if (paymentCart.length === 0) {
      showAlertModal("Uyarı", "Ödeme yapmak için sepette ürün olmalıdır.");
      setIsProcessingPayment(false);
      return;
    }
    if (!companyId) {
      showAlertModal("Hata", "Şirket bilgisi bulunamadı. İşlem yapılamıyor.");
      setIsProcessingPayment(false);
      return;
    }
    if (!selectedCustomer) {
      showAlertModal("Hata", "Cari hesap seçilemedi. Lütfen bir cari hesap seçin.");
      setIsProcessingPayment(false);
      return;
    }
    if (!cashPaymentCashboxId) {
      showAlertModal("Hata", "Bu ekran için varsayılan nakit kasa atanmamış. Lütfen ayarlardan kasa seçin.");
      setIsProcessingPayment(false);
      return;
    }
    
    // ✅ YENİ: ÖKC KULLANIMI İÇİN ÖN KONTROL - DEPARTMAN NUMARASI
    if (useFiscalDeviceForCash) {
      console.log(`[handleCashPaymentForQuickSale] 🔍 ÖKC açık, sepetteki ürünlerin departman numaraları kontrol ediliyor...`);
      console.log(`[handleCashPaymentForQuickSale] 🔍 Sepetteki ürün sayısı: ${paymentCart.length}`);
      console.log(`[handleCashPaymentForQuickSale] 🔍 Toplam inventory item sayısı: ${allInventoryItems.length}`);
      
      const productsWithoutDepartment: string[] = [];
      
      for (const cartItem of paymentCart) {
        if (cartItem.id.startsWith('DEPOSIT-')) continue;
        const inventoryItem = allInventoryItems.find(inv => inv.id === cartItem.id);
        if (!inventoryItem) {
          showAlertModal("Hata", `Sepetteki ürün (ID: ${cartItem.id}) bulunamadı.`);
          setIsProcessingPayment(false);
          return;
        }
        
        const departmentNo = inventoryItem.departmentNo;
        console.log(`[handleCashPaymentForQuickSale] 🔍 Ürün kontrol: "${inventoryItem.name}" - Departman No: ${departmentNo}`);
        
        if (departmentNo === undefined || departmentNo === null || departmentNo < 1 || departmentNo > 100) {
          productsWithoutDepartment.push(inventoryItem.name || 'İsimsiz Ürün');
        }
      }
      
      if (productsWithoutDepartment.length > 0) {
        const productList = productsWithoutDepartment.map(p => `• ${p}`).join('\n');
        showAlertModal(
          "Departman Numarası Eksik", 
          `Aşağıdaki ürünler için departman numarası (1-100 arası) tanımlanmamış:\n\n${productList}\n\nLütfen ürün ayarlarından departman numarası ekleyiniz.`
        );
        setIsProcessingPayment(false);
        return;
      }
      
      console.log(`[handleCashPaymentForQuickSale] ✅ Tüm ürünlerin departman numarası mevcut.`);
    }
    
    const validationTime = performance.now();
    console.log(`[handleCashPaymentForQuickSale] ⏱️ Validasyon süresi: ${(validationTime - startTime).toFixed(2)}ms`);

    // ✅ Ödeme niyeti: işlem anahtarı cihaza gitmeden ÖNCE belli olur; aynı anahtar niyet,
    // cihaz sonucu, kayıt, "Yeniden Dene", bekleyen kayıt kuyruğu ve telemetride kullanılır.
    // (Ingenico "Yine de Devam Et" yeniden çağrısında aynı anahtar devralınır.)
    const cashIntentKey = intentKeyOverride || newPaymentIntentKey();
    const cashboxIdForRequest: string = cashPaymentCashboxId;
    /**
     * Nakit atomik isteği (process-cash-with-invoice): niyet için cihazdan ÖNCE (mali alanlar
     * boş), kayıt için cihazdan SONRA (mali alanlar dolu) AYNI fonksiyonla üretilir.
     * paymentItems fiş kalemleri için de kullanılır. Ürün/depo bulunamazsa hata fırlatır.
     */
    const buildCashRequest = (fiscal: any) => {
      const calculatedGrandTotal = calculateTotalFromCart(paymentCart);
      // Deposito items'ları SQL API'den filtrele (stok hareketi gerekmez)
      const regularCartItems = paymentCart.filter(item => !item.id.startsWith('DEPOSIT-'));

      const paymentItems = regularCartItems.map(cartItem => {
        const inventoryItem = allInventoryItems.find(item => item.id === cartItem.id);
        
        if (!inventoryItem) {
          throw new Error(`[SQL] Ürün detayı bulunamadı: ${cartItem.id} - ${cartItem.name}`);
        }

          const unit = inventoryItem.unit || 'adet';
          const vatRate = inventoryItem.vatRate || 0;
          const sctRate = inventoryItem.sctRate || 0;
          const unitPrice = cartItem.price;
          const quantity = cartItem.quantity;
          const lineTotal = quantity * unitPrice;

        // ✅ KDV Dahil/Hariç durumuna göre doğru hesaplama
        const isVatIncluded = cartItem.vatStatus === 'included';
        let netTotal: number;
        let vatAmount: number;
        let sctAmount: number;
        let grossTotal: number;

        if (isVatIncluded) {
          // KDV DAHİL: Fiyat zaten KDV içeriyor, KDV'yi çıkar
          grossTotal = lineTotal;
          vatAmount = Math.round((grossTotal - (grossTotal / (1 + vatRate / 100))) * 100) / 100;
          sctAmount = Math.round((grossTotal - (grossTotal / (1 + sctRate / 100))) * 100) / 100;
          netTotal = Math.round((grossTotal - vatAmount - sctAmount) * 100) / 100;
        } else {
          // KDV HARİÇ: Fiyat KDV içermiyor, KDV ekle
          netTotal = lineTotal;
          vatAmount = Math.round((netTotal * (vatRate / 100)) * 100) / 100;
          sctAmount = Math.round((netTotal * (sctRate / 100)) * 100) / 100;
          grossTotal = Math.round((netTotal + vatAmount + sctAmount) * 100) / 100;
        }

        const warehouseId = resolveEffectiveWarehouseId(cartItem.id, inventoryItem.categoryId, screenSettings, branchWarehouseId);
        
        if (!warehouseId) {
          console.error(`[SQL] HATA: Ürün için warehouseId bulunamadı:`, inventoryItem);
          throw new Error(`[SQL] Ürün için depo bilgisi bulunamadı: ${cartItem.name}. Lütfen ürün ayarlarını kontrol edin.`);
        }

        // ✅ KDV Dahil/Hariç bilgisi: Sepetteki vatStatus'a göre PriceType belirle
        // vatStatus='included' → Fiyat KDV dahil → PriceType='VAT_INCLUDED'
        // vatStatus='excluded' → Fiyat KDV hariç → PriceType='VAT_EXCLUDED'
        const priceType = cartItem.vatStatus === 'included' ? 'VAT_INCLUDED' : 'VAT_EXCLUDED';

        return {
          ProductId: cartItem.id,
          ProductName: cartItem.name,
          StockCode: inventoryItem.stockCode || '',
          WarehouseId: warehouseId,
          WarehouseName: '',
          Quantity: quantity,
          Unit: unit,
          UnitPrice: unitPrice,
          LineTotal: lineTotal,
          VatRate: vatRate,
          SctRate: sctRate,
          VatAmount: vatAmount,
          SctAmount: sctAmount,
          GrossTotal: grossTotal,
          // ✅ KDV Dahil/Hariç bilgisi (fatura hesaplaması için)
          PriceType: priceType,
          // ✅ Reçete/Menü bazlı stok düşme için
          HasRecipe: inventoryItem.hasRecipe || false,
          HasMenu: inventoryItem.hasMenu || false,
          // ✅ YENİ: Opsiyonlu menü için seçilen opsiyonlar
          SelectedOptions: (cartItem as any).selectedOptions || null,
          TotalPriceDifference: (cartItem as any).totalPriceDifference || 0,
          // ✅ YENİ: İndirim kaydı (faturaya gerçek indirim için)
          ...buildItemDiscountFields(cartItem)
        };
      });

      // ✅ YENİ: Atomik Request - Fatura dahil!
      // ✅ Restoran modunda Description "Restoran Satışı" olmalı
      const saleDescription = isRestaurantMode ? 'Restoran Satışı (Nakit)' : 'Hızlı Satış (Nakit)';
      
      const request: QuickSaleCashWithInvoiceRequest = {
        IdempotencyKey: cashIntentKey,

        CompanyName: companyName || null,
        ScreenId: screenId ? parseInt(screenId) : null,
        AccountId: parseInt(selectedCustomer.id),
        AccountName: selectedCustomer.companyName,
        CashboxId: parseInt(cashboxIdForRequest),
        CashboxName: cashPaymentCashboxName || null, // ✅ YENİ: Gün sonu raporu için kasa adı
        TotalAmount: calculatedGrandTotal,
        Currency: 'TRY',
        Description: saleDescription,
        CreatedBy: effectiveCreatedBy, // ✅ Kasiyer varsa kasiyer adı kullanılır
        BranchId: userDetails?.branchesId ? parseInt(userDetails.branchesId, 10) : null, // ✅ Gün sonu raporu için kritik! (string → int)
        BranchName: userDetails?.branchesId ? getBranchName(userDetails.branchesId) : null, // ✅ YENİ: Kasa hareket raporu için şube adı
        IsRestaurantMode: isRestaurantMode, // ✅ YENİ: Restoran modu için RS- prefix
        CashierId: currentCashierId || null, // ✅ YENİ: Kasiyer ID (gün sonu raporu için)
        CashierName: currentCashierName || null, // ✅ YENİ: Kasiyer adı (gün sonu raporu için)
        IsFiscalReceipt: useFiscalDeviceForCash,
        PavoPaySaleNumber: fiscal?.SaleNumber || null,
        PavoPayInvoiceNo: fiscal?.InvoiceNo || null,
        EInvoiceETTN: fiscal?.EInvoiceETTN || null,
        PavoPayInquiryLink: fiscal?.PavoPayInquiryLink || null,
        // ✅ YENİ: Sepete uygulanan indirim kaydı (fiş başlığı)
        ...buildHeaderDiscountFields(cart, appliedDiscountType, lastAppliedDiscountAmount),
        Items: paymentItems.map(item => ({
          ProductId: item.ProductId,
          ProductName: item.ProductName,
          StockCode: item.StockCode,
          WarehouseId: item.WarehouseId,
          WarehouseName: item.WarehouseName,
          Quantity: item.Quantity,
          Unit: item.Unit,
          UnitPrice: item.UnitPrice,
          VatRate: item.VatRate,
          SctRate: item.SctRate,
          PriceType: item.PriceType,
          HasRecipe: item.HasRecipe,
          HasMenu: item.HasMenu,
          SelectedOptions: item.SelectedOptions,
          TotalPriceDifference: item.TotalPriceDifference,
          // ✅ YENİ: İndirim kaydı (fatura kalemi)
          OriginalPrice: item.OriginalPrice,
          DiscountRate: item.DiscountRate,
          DiscountAmount: item.DiscountAmount
        }))
      };
      return { paymentItems, request };
    };

    /** Nakit niyeti: yazarkasaya gitmeden ÖNCE (mali alanlar boş istekle). Hata fırlatmaz. */
    const openCashIntent = async (device: string): Promise<void> => {
      let payload: QuickSaleCashWithInvoiceRequest;
      try {
        payload = buildCashRequest(null).request;
      } catch (e) {
        console.warn('[PaymentIntent] Nakit niyet isteği hazırlanamadı (ödeme devam ediyor):', e);
        return;
      }
      await openPaymentIntent({
        key: cashIntentKey,
        endpoint: 'process-cash-with-invoice',
        payload,
        amount: payload.TotalAmount,
        paymentKind: 'nakit',
        fiscalDevice: device,
      });
    };

    // Yazarkasa İşlemleri Başlangıcı
    let fiscalOperationSuccessful = true; // Varsayılan olarak başarılı kabul et (ÖKC kullanılmayacaksa)
    let documentId: string | null = null; 
    let cashFiscalData: any = null;
    let fiscalDeviceConnected = false;

    // YENİ KONTROL: useFiscalDeviceForCash true ise ÖKC işlemlerini yap
    console.log(`[handleCashPaymentForQuickSale] 🔍 ÖKC Kullanım Ayarı: ${useFiscalDeviceForCash ? 'AÇIK ✅' : 'KAPALI ❌'}`);
    console.log(`[handleCashPaymentForQuickSale] 🔍 Seçilen Yazarkasa: ${fiscalDeviceName} (${selectedFiscalDevice}), Aktif: ${isFiscalDeviceActive}`);
    console.log(`[handleCashPaymentForQuickSale] 🔍 skipFiscalDevice: ${skipFiscalDevice ? 'EVET (Kullanıcı onayladı)' : 'HAYIR'}`);
    
    // ✅ skipFiscalDevice true ise yazarkasa kontrollerini atla (kullanıcı zaten onaylamış)
    if (useFiscalDeviceForCash && !skipFiscalDevice) {
      // ✅ Önce cihazın desteklenip desteklenmediğini kontrol et
      if (!isFiscalDeviceActive) {
        console.log(`[handleCashPaymentForQuickSale] ⚠️ ${fiscalDeviceName} entegrasyonu mevcut değil, yazarkasasız devam ediliyor...`);
        // Yazarkasasız devam et - uyarı vermeden
      } else {
        // ✅ YENİ: Yazarkasa bağlantısını kontrol et
        console.log(`[handleCashPaymentForQuickSale] 🔌 Yazarkasa bağlantısı kontrol ediliyor...`);
        try {
          fiscalDeviceConnected = fiscalDevice.checkConnection ? await fiscalDevice.checkConnection() : false;
          console.log(`[handleCashPaymentForQuickSale] 🔌 Bağlantı durumu: ${fiscalDeviceConnected ? 'BAĞLI ✅' : 'BAĞLI DEĞİL ❌'}`);
        } catch (connError) {
          console.error(`[handleCashPaymentForQuickSale] 🔌 Bağlantı kontrolü hatası:`, connError);
          fiscalDeviceConnected = false;
        }

        // ✅ Yazarkasa BAĞLI ise işlem yap
        if (fiscalDeviceConnected) {
          // ✅ Ingenico 5000F: Ödeme öncesi departman-KDV uyumluluğu kontrolü
          if (selectedFiscalDevice === 'ingenico_5000f' && !skipDeptVatCheck && onIngenicoDeptVatMismatch) {
            const syncProducts = paymentCart.filter(ci => !ci.id.startsWith('DEPOSIT-')).map((cartItem) => {
              const inv = allInventoryItems.find(item => item.id === cartItem.id);
              return {
                departmentNo: (inv?.departmentNo ?? 0) as number,
                vatRate: inv?.vatRate ?? 0,
                name: cartItem.name || inv?.name || '',
              };
            }).filter(p => p.departmentNo >= 1);
            if (syncProducts.length > 0) {
              const syncResult = await syncDepartmentVatMapping(syncProducts);
              if (syncResult.hasMismatches) {
                console.warn('[handleCashPaymentForQuickSale] Ingenico departman-KDV uyumsuzluğu tespit edildi. Ödeme DURDURULDU.');
                if (onIngenicoDeptVatMismatch) {
                  onIngenicoDeptVatMismatch(syncResult);
                } else {
                  showAlertModal(
                    'Departman-KDV Uyumsuzluğu',
                    'Yazarkasadaki departman KDV oranları ürünlerinizle eşleşmiyor. Lütfen ayarlardan "KDV/Departman Tablosu" ile kontrol edin.'
                  );
                }
                setIsProcessingPayment(false);
                return;
              }
            }
          }

          fiscalOperationSuccessful = false; // ÖKC kullanılacaksa, başlangıçta başarısız kabul et
          try {
            console.log(`[handleCashPaymentForQuickSale] ${fiscalDeviceName} ile nakit ödeme başlıyor...`);

            // ✅ Ürünleri FiscalProductItem formatına çevir
            // PavoPay ve Beko KDV DAHİL fiyat bekler, Inpos/Hugin KDV HARİÇ fiyat bekler
            const usesVatIncludedPricing = selectedFiscalDevice === 'pavopay' || selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr';
            
            // Fiscal ürün listesi: POS depositoları dahil (skipFiscal=false), nakit depositoları hariç (skipFiscal=true)
            const cashFiscalCart = paymentCart.filter(ci => !ci.id.startsWith('DEPOSIT-') || !(ci as any).skipFiscal);

            const fiscalProducts: FiscalProductItem[] = cashFiscalCart.map((cartItem) => {
              const isDeposit = cartItem.id.startsWith('DEPOSIT-');
              const inventoryItem = isDeposit ? null : allInventoryItems.find(item => item.id === cartItem.id);
              if (!inventoryItem && !isDeposit) {
                throw new Error(`Ürün detayı bulunamadı: ${cartItem.name}.`); 
              }
              
              // `inventoryItem!.vatRate || 0` idi: kartta oran boş/tanınmayan bir
              // değerse fiş sessizce KDV'siz kesiliyordu. Artık çözülemeyen oran
              // hata veriyor; aşağıdaki fiyat hesabı da yanlış orana dayanmasın.
              const vatRate = resolveCartItemVatRate(cartItem);
              if (vatRate === undefined) {
                throw new Error(
                  `"${cartItem.name}" ürününün KDV oranı belirlenemedi. Fişin KDV'siz ` +
                  `kesilmemesi için işlem durduruldu. Ürünün stok kartındaki KDV oranını kontrol edin.`
                );
              }
              const isVatIncluded = cartItem.vatStatus === 'included';
              
              let fiscalPrice: number;
              
              if (usesVatIncludedPricing) {
                if (isVatIncluded) {
                  fiscalPrice = cartItem.price;
                  console.log(`[NAKİT-${selectedFiscalDevice?.toUpperCase()}] ${cartItem.name}: ${fiscalPrice.toFixed(2)} TL (KDV dahil, olduğu gibi)`);
                } else {
                  fiscalPrice = cartItem.price * (1 + vatRate / 100);
                  console.log(`[NAKİT-${selectedFiscalDevice?.toUpperCase()}] ${cartItem.name}: ${cartItem.price} TL (KDV hariç) → ${fiscalPrice.toFixed(2)} TL (KDV dahil)`);
                }
              } else {
                if (isVatIncluded) {
                  fiscalPrice = cartItem.price / (1 + vatRate / 100);
                  console.log(`[NAKİT-INPOS/HUGIN] ${cartItem.name}: ${cartItem.price} TL (KDV dahil) → ${fiscalPrice.toFixed(2)} TL (KDV hariç)`);
                } else {
                  fiscalPrice = cartItem.price;
                  console.log(`[NAKİT-INPOS/HUGIN] ${cartItem.name}: ${fiscalPrice.toFixed(2)} TL (KDV hariç, olduğu gibi)`);
                }
              }
              
              return {
                id: cartItem.id,
                ...toPackageFiscalLine(cartItem, { name: cartItem.name, quantity: cartItem.quantity, price: fiscalPrice }),
                vatRate,
                departmentNo: isDeposit ? 1 : inventoryItem!.departmentNo!,
                barcode: isDeposit ? '' : (inventoryItem!.barcode || ''),
              };
            });

            // Sadece nakit depositoları (skipFiscal=true) fiscal toplamdan çıkar
            const cashOnlyDepositTotal = paymentCart
              .filter(ci => ci.id.startsWith('DEPOSIT-') && (ci as any).skipFiscal)
              .reduce((sum, ci) => sum + ci.price * ci.quantity, 0);

            let fiscalTotalForDevice: number;
            
            if (selectedFiscalDevice === 'inpos_m530') {
              let inposTotalKurus = 0;
              for (const fp of fiscalProducts) {
                const priceWithVat = fp.price * (1 + fp.vatRate / 100);
                const unitPriceKurus = Math.round(priceWithVat * 100);
                inposTotalKurus += unitPriceKurus * fp.quantity;
              }
              fiscalTotalForDevice = inposTotalKurus / 100;
              console.log(`[NAKİT-INPOS] 🔢 Inpos toplam hesaplandı: ${inposTotalKurus} kuruş = ${fiscalTotalForDevice.toFixed(2)} TL`);
            } else {
              fiscalTotalForDevice = parseFloat((calculateTotalFromCart(paymentCart) - cashOnlyDepositTotal).toFixed(2));
              console.log(`[NAKİT-${selectedFiscalDevice?.toUpperCase()}] 🔢 Hizmet toplamı: ${fiscalTotalForDevice.toFixed(2)} TL (nakit deposito hariç: ${cashOnlyDepositTotal.toFixed(2)} TL)`);
            }

            // ✅ YENİ: PavoPay için mutfak modu sipariş numarası ve şirket adı
            // @ts-ignore - useKitchenScreen screenSettings'de var
            const useKitchenScreenForFiscal = screenSettings?.useKitchenScreen || false;
            const kitchenOrderCodeForFiscal = (selectedFiscalDevice === 'pavopay' && useKitchenScreenForFiscal && reservedShortCode) 
              ? reservedShortCode 
              : undefined;
            // @ts-ignore - receiptDisplaySettings screenSettings'de var
            const receiptCompanyNameForFiscal = (selectedFiscalDevice === 'pavopay' && useKitchenScreenForFiscal) 
              ? (screenSettings?.receiptDisplaySettings?.companyName || companyName || undefined)
              : undefined;
            
            // ✅ Ödeme niyeti: yazarkasaya gitmeden ÖNCE (en fazla 4 sn; yazılamazsa devam)
            await openCashIntent(telemetryFiscalDevice(selectedFiscalDevice));

            // ✅ Yazarkasa entegrasyonunu çağır
            const useExternalPrinterForPavo = screenSettings?.useExternalPrinterForPavo || false;
            // ✅ NUMARA GARANTİSİ: Yazarkasaya istek gidiyor — deneme başladı
            markPaymentAttempt();
            const result = await fiscalDevice.processCashPayment(
              fiscalProducts,
              fiscalTotalForDevice,
              showAlertModal,
              kitchenOrderCodeForFiscal,
              receiptCompanyNameForFiscal,
              useExternalPrinterForPavo
            );

            if (!result.success) {
              throw new Error(result.errorMessage || 'Yazarkasa işlemi başarısız oldu');
            }

            documentId = result.documentId || null;
            fiscalOperationSuccessful = true;
            cashFiscalData = result.fiscalData;
            console.log(`[handleCashPaymentForQuickSale] ✅ ${fiscalDeviceName} işlemi başarılı. Belge ID: ${documentId}`);

            // ✅ PavoPay nakit satışını da iade/iptal listesine kaydet (PavoPayRefundModal
            //    "günün satışları"nda yalnız kartlı işlemler görünüyordu). savePavoPaySale
            //    saleNumber'a göre tekilleştirdiği için Rest-P2P (F6 dinleyicisi) ile
            //    çakışma/çift kayıt olmaz.
            if (selectedFiscalDevice === 'pavopay' && companyId) {
              const cashSaleNumber = cashFiscalData?.SaleNumber || documentId || '';
              if (cashSaleNumber) {
                try {
                  savePavoPaySale({
                    saleNumber: cashSaleNumber,
                    invoiceNo: cashFiscalData?.InvoiceNo || '',
                    amount: fiscalTotalForDevice,
                    date: new Date().toISOString(),
                    paymentType: 'CASH',
                    items: fiscalProducts.map(p => ({ name: p.name, quantity: p.quantity, price: p.price })),
                    fiscalData: cashFiscalData,
                  }, companyId);
                } catch (saveErr) {
                  console.warn('[handleCashPaymentForQuickSale] Nakit satış kaydı eklenemedi:', saveErr);
                }
              }
            }
            
            // Beko: Cihaz üzerinde ödeme türü değiştirilmiş olabilir (örn: Nakit→POS)
            if (result.fiscalData?.PaymentTypeChanged && result.fiscalData?.ActualPaymentItems) {
              const actualTypes = result.fiscalData.ActualPaymentItems.map((p: any) => p.type);
              const typeName = actualTypes.map((t: number) => {
                const names: { [k: number]: string } = { 1: 'Nakit', 3: 'Kredi Kartı', 7: 'Yemek Kartı' };
                return names[t] || `Tip ${t}`;
              }).join(', ');
              console.log(`[handleCashPaymentForQuickSale] ⚠️ BEKO: Cihazda ödeme türü değiştirildi → ${typeName}`);
              console.log('[handleCashPaymentForQuickSale] ℹ️ Yazarkasa fişi kesildi, SQL kaydı gönderilen türe göre devam eder.');
            }
            
          } catch (fiscalError: any) {
            console.error(`[handleCashPaymentForQuickSale] ❌ ${fiscalDeviceName} hatası:`, fiscalError);
            
            // ✅ YENİ (Ingenico): Hata modalında HER ZAMAN "Yine de Devam Et" seçeneği sun.
            // GetTicket doğrulaması ödemeyi bulamasa bile kasiyer parayı fiilen aldıysa
            // satışı sisteme kaydedebilmeli (gün sonu tutarlılığı için kritik).
            if (selectedFiscalDevice === 'ingenico_5000f') {
              // Ödeme alınmış olabilir (belirsiz) → niyet terk EDİLMEZ; devam edilirse aynı anahtar kullanılır
              setFiscalErrorContinueMessage(fiscalError.message || 'Bilinmeyen hata');
              setFiscalErrorContinueCallback(() => async () => {
                console.log('[handleCashPaymentForQuickSale] 👤 Kullanıcı "Yine de Devam Et" dedi, satış yazarkasasız kaydediliyor...');
                await handleCashPaymentForQuickSale(true, false, cashIntentKey); // skipFiscalDevice=true
              });
              setIsFiscalErrorContinueOpen(true);
              setIsProcessingPayment(false);
              return; // Modal açıldı, kullanıcı kararı bekleniyor
            }

            // Satış kaydedilmeyecek: yazarkasa kesin reddettiyse niyet terk edilir (zaman aşımında edilmez)
            if (isDeviceFailureCertain(fiscalError?.message)) {
              abandonPaymentIntent(cashIntentKey, `Yazarkasa hatası (nakit): ${fiscalError?.message || ''}`);
            }
            showAlertModal("Yazarkasa Entegrasyon Hatası", fiscalError.message || 'Bilinmeyen hata');
            fiscalOperationSuccessful = false;
          }
        } else {
          // ✅ Yazarkasa BAĞLI DEĞİL
          // warnIfFiscalNotConnected AÇIKSA → Onay modalı göster
          // warnIfFiscalNotConnected KAPALIYSA → Sessizce devam et
          if (warnIfFiscalNotConnected) {
            console.log(`[handleCashPaymentForQuickSale] ⚠️ Yazarkasa bağlı değil! warnIfFiscalNotConnected=true, onay modalı gösteriliyor...`);
            
            // Callback'i kaydet - kullanıcı onaylarsa yazarkasasız devam edilecek
            setPendingPosPaymentCallback(() => async () => {
              console.log(`[handleCashPaymentForQuickSale] 👤 Kullanıcı onayladı, yazarkasa OLMADAN devam ediliyor...`);
              await handleCashPaymentForQuickSale(true); // skipFiscalDevice=true ile tekrar çağır
            });
            
            // Onay modalını aç
            setIsFiscalDisconnectedConfirmOpen(true);
            
            setIsProcessingPayment(false);
            return; // Modal açıldı, fonksiyondan çık
          } else {
            // ✅ YENİ: warnIfFiscalNotConnected KAPALI - uyarı göstermeden devam et
            console.log(`[handleCashPaymentForQuickSale] ⚠️ Yazarkasa bağlı değil ama warnIfFiscalNotConnected=false, sessizce devam ediliyor...`);
            // fiscalOperationSuccessful true olarak bırak ve SQL kayıtlarına devam et
          }
        }
      }
    } else if (skipFiscalDevice) {
      // Kullanıcı onay verdi, yazarkasa atlanıyor
      console.log("[handleCashPaymentForQuickSale] Kullanıcı onayladı - Yazarkasa ATLANIYOR ve SQL kayıtlarına devam ediliyor.");
    } else {
      console.log("[handleCashPaymentForQuickSale] Nakit ödeme için ÖKC kullanımı kapalı (useFiscalDeviceForCash: false). SQL kayıtlarına devam ediliyor.");
      // fiscalOperationSuccessful zaten true olarak ayarlandı.
    }
    
    // Eğer ÖKC kullanılmadıysa veya ÖKC işlemi başarılıysa SQL kayıtlarına devam et
    if (fiscalOperationSuccessful) {
      const sqlStartTime = performance.now();
      console.log(`[handleCashPaymentForQuickSale] ⏱️ ÖKC işlemi süresi: ${(sqlStartTime - validationTime).toFixed(2)}ms`);
      console.log("[handleCashPaymentForQuickSale] SQL kayıtları başlıyor (ÖKC Durumu: " + (useFiscalDeviceForCash ? "Başarılı" : "Kullanılmadı") + ")...");
      
      // Genel Cari kontrolü
      const isGeneralCustomer = selectedCustomer.companyName === 'Genel Cari';

      let paymentRequest: QuickSaleCashWithInvoiceRequest | undefined;
      // Kayıt sunucuda başarılı oldu mu? (Sonraki adımlardan biri düşerse kayıt hatası penceresi AÇILMAZ)
      let cashSaveSucceeded = false;
      // Kayıt sonrası adımlar — ilk denemede de "Yeniden Dene"de de aynı fonksiyon (en fazla 1 kez)
      let cashCompleteAfterSave: ((response: QuickSaleWithInvoiceResponse) => Promise<void>) | null = null;

      try {
        const dataPreparationStart = performance.now();
        const currentDate = new Date();
        // closure-safe: paymentCart'tan toplam hesapla
        const calculatedGrandTotal = calculateTotalFromCart(paymentCart);

        console.log("[SQL] Nakit ödeme SQL API'ye gönderiliyor...");
        console.log("[SQL] Sepetteki ürün sayısı:", paymentCart.length);
        console.log("[SQL] Toplam tutar:", calculatedGrandTotal);
        console.log('🏢 [ŞİRKET] Cache\'den alınan şirket adı:', companyName || 'DEFAULT');

        // ===== ATOMİK API'YE GÖNDERİLECEK VERİYİ HAZIRLA (NAKİT + FATURA) =====
        // Deposito items'ları SQL API'den filtrele (stok hareketi gerekmez)
        const regularCartItems = paymentCart.filter(item => !item.id.startsWith('DEPOSIT-'));

        // ✅ Şube deposu aktifse ürünleri o depoya kaydet (qty=0 ile)
        if (branchWarehouseId && companyId) {
          await enrichCartItemsForBranchWarehouse(regularCartItems, branchWarehouseId, companyId);
        }

        const { paymentItems, request: builtCashRequest } = buildCashRequest(cashFiscalData);
        paymentRequest = builtCashRequest;

        const dataPreparationEnd = performance.now();
        console.log(`[SQL] ⏱️ Veri hazırlama süresi: ${(dataPreparationEnd - dataPreparationStart).toFixed(2)}ms`);
        console.log(`🏷️ [DEBUG] isRestaurantMode değeri: ${isRestaurantMode}, request.IsRestaurantMode: ${paymentRequest.IsRestaurantMode}`);
        console.log("[ATOMİK] 🔄 Nakit + Fatura isteği gönderiliyor:", paymentRequest);

        // ✅ Kayıt sonrası adımlar (para çekmecesi, mutfak siparişi, fiş, masa kapatma,
        // kulüp kartı, sepet) TEK fonksiyonda: ilk denemede de "Yeniden Dene" başarısında
        // da aynısı çalışır, runOnce sayesinde en fazla BİR kez (mutfak fişi iki kez basılmaz).
        cashCompleteAfterSave = runOnce(async (response: QuickSaleWithInvoiceResponse) => {
        if (response.InvoiceNumber) {
          console.log(`[ATOMİK] 📄 Fatura oluşturuldu: ${response.InvoiceNumber}`);
        }

        console.log("[SQL] ✅ Tüm işlemler başarıyla tamamlandı!");
        console.log("[SQL] Referans No:", response.ReferenceNumber);
        console.log("[SQL] Cari Hareket ID:", response.AccountTransactionId);
        console.log("[SQL] Stok Hareket ID'leri:", response.StockMovementIds);

        // 💰 Para çekmecesi tetiği: saf nakit ödeme SQL'e başarıyla kaydedildi
        try {
          console.log(`💰 [CashDrawer@usePayment:cashSuccess] callback çağrılıyor, paidByCash=${calculatedGrandTotal}`);
          onCashPaymentSuccess?.(calculatedGrandTotal);
        } catch (cbErr) {
          console.warn('[usePayment] onCashPaymentSuccess callback hatası:', cbErr);
        }

        // 🍽️ MUTFAK EKRANI ENTEGRASYONU - Sipariş Gönderme
        let kitchenShortCode: string | null = null; // ✅ Fişte kullanılacak kısa kod
        try {
          // @ts-ignore - useKitchenScreen ve selectedQRMenuId screenSettings'de var
          const useKitchenScreen = screenSettings?.useKitchenScreen || false;
          // @ts-ignore
          const selectedQRMenuId = screenSettings?.selectedQRMenuId || null;

          // ✅ DÜZELTME: Mutfak ekranı SADECE normal hızlı satış modunda çalışır
          // Restoran modu: Sipariş "Mutfağa Yazdır" butonu ile gönderilir, ödeme alındığında GÖNDERİLMEZ
          // Çünkü restoran modunda müşteri ödeme yaptığında yemeği bitirmiş demektir (A la carte)
          if (useKitchenScreen && selectedQRMenuId && !isRestaurantMode) {
            console.log('[MUTFAK EKRANI] 🍽️ Mutfak ekranı entegrasyonu AKTIF (Normal Hızlı Satış), sipariş gönderiliyor...');
            console.log('[MUTFAK EKRANI] QR Menü ID:', selectedQRMenuId);

            // Sipariş detaylarını hazırla (deposito items hariç)
            const siparisDetaylari = paymentCart.filter(i => !i.id.startsWith('DEPOSIT-')).map(item => {
              const inventoryItem = allInventoryItems.find(inv => inv.id === item.id);
              return {
                UrunId: item.id,
                UrunAdi: item.name,
                BirimFiyat: item.price,
                Miktar: item.quantity,
                ToplamFiyat: item.price * item.quantity,
                UrunResmi: inventoryItem?.imageUrl || null,
                Aciklama: null,
                CategoryId: inventoryItem?.categoryId || null,
                VatRate: inventoryItem?.vatRate || null
              };
            });

            const kitchenOrderRequest = {
    
              QRMenuId: selectedQRMenuId,
              PersonelFirebaseUid: userDetails?.uid || null,
              PersonelName: userDetails ? `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() || 'Hızlı Satış' : 'Hızlı Satış',
              ToplamTutar: calculateTotal(),
              SiparisDetaylari: siparisDetaylari,
              SiparisNotu: null,
              OdemeYontemi: 'Nakit',
              IslemId: documentId || null,
              DekontId: documentId || null,
              QuickSaleScreenId: screenId ? parseInt(screenId) : null,
              ReferenceNumber: response.ReferenceNumber || null,
              IsRestaurantMode: false,
              ReservedLongCode: reservedLongCode || null,
              ReservedShortCode: reservedShortCode || null,
              BranchId: activeBranchId || null
            };

            console.log('[MUTFAK EKRANI] 📤 Sipariş request:', kitchenOrderRequest);

            // Mutfak ekranına sipariş gönder (arka planda - hata olsa bile satış devam eder)
            const { default: salesService } = await import('../../../../services/salesService');
            const kitchenResponse = await salesService.createKitchenOrderFromQuickSale(kitchenOrderRequest);

            if (kitchenResponse.success) {
              console.log('[MUTFAK EKRANI] ✅ Sipariş mutfak ekranına başarıyla gönderildi!');
              console.log('[MUTFAK EKRANI] 🆔 SİPARİŞ ID:', kitchenResponse.data?.siparisId);
              console.log('[MUTFAK EKRANI] 🔢 SİPARİŞ NO:', kitchenResponse.data?.siparisNo);
              console.log('[MUTFAK EKRANI] 📋 KISA KOD:', kitchenResponse.data?.shortCode);
              
              // ✅ Kısa kodu fişte kullanmak için kaydet
              kitchenShortCode = kitchenResponse.data?.shortCode || null;
              
              // Kullanıcıya bilgi ver (opsiyonel - snackbar ile)
              showSnackbar(`Sipariş mutfak ekranına gönderildi (${kitchenResponse.data?.shortCode})`, 'info');
            } else {
              console.error('[MUTFAK EKRANI] ❌ Sipariş gönderilemedi:', kitchenResponse.message);
              // Satış başarılı olduğu için hata gösterme, sadece logla
            }
          } else if (isRestaurantMode && useKitchenScreen && selectedQRMenuId) {
            console.log('[MUTFAK EKRANI] 🍽️ Restoran modu (NAKİT) - Mutfak entegrasyonu AKTIF, ödeme sonrası güncelleme/oluşturma...');

            const siparisDetaylari = paymentCart.filter(i => !i.id.startsWith('DEPOSIT-')).map(item => {
              const inventoryItem = allInventoryItems.find(inv => inv.id === item.id);
              return {
                UrunId: item.id,
                UrunAdi: item.name,
                BirimFiyat: item.price,
                Miktar: item.quantity,
                ToplamFiyat: item.price * item.quantity,
                UrunResmi: inventoryItem?.imageUrl || null,
                Aciklama: null,
                CategoryId: inventoryItem?.categoryId || null,
                VatRate: inventoryItem?.vatRate || null
              };
            });

            const kitchenOrderRequest = {
              QRMenuId: selectedQRMenuId,
              PersonelFirebaseUid: userDetails?.uid || null,
              PersonelName: userDetails ? `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() || 'Restoran Satışı' : 'Restoran Satışı',
              ToplamTutar: calculateTotal(),
              SiparisDetaylari: siparisDetaylari,
              SiparisNotu: null,
              OdemeYontemi: 'Nakit',
              IslemId: documentId || null,
              DekontId: documentId || null,
              QuickSaleScreenId: screenId ? parseInt(screenId) : null,
              ReferenceNumber: response.ReferenceNumber || null,
              IsRestaurantMode: true,
              ReservedLongCode: reservedLongCode || null,
              ReservedShortCode: reservedShortCode || null,
              BranchId: activeBranchId || null,
              IsPaymentUpdate: true,
              TableName: tableName || null,
              CustomerAccountName: selectedCustomer?.companyName || null
            };

            console.log('[MUTFAK EKRANI] 📤 Restoran ödeme güncelleme request (NAKİT):', kitchenOrderRequest);

            const { default: salesService } = await import('../../../../services/salesService');
            const kitchenResponse = await salesService.createKitchenOrderFromQuickSale(kitchenOrderRequest);

            if (kitchenResponse.success) {
              console.log('[MUTFAK EKRANI] ✅ Restoran mutfak kaydı güncellendi/oluşturuldu (NAKİT)');
              console.log('[MUTFAK EKRANI] 📋 KISA KOD:', kitchenResponse.data?.shortCode);
              kitchenShortCode = kitchenResponse.data?.shortCode || null;
            } else {
              console.error('[MUTFAK EKRANI] ❌ Restoran mutfak güncelleme başarısız (NAKİT):', kitchenResponse.message);
            }
          } else if (isRestaurantMode) {
            console.log('[MUTFAK EKRANI] ℹ️ Restoran modu - Mutfak entegrasyonu kapalı, atlanıyor');
          } else {
            console.log('[MUTFAK EKRANI] ℹ️ Mutfak ekranı entegrasyonu kapalı veya QR menü seçilmemiş');
          }
        } catch (kitchenError) {
          console.error('[MUTFAK EKRANI] ❌ Mutfak ekranı entegrasyonu hatası:', kitchenError);
          // Satış başarılı olduğu için kullanıcıya hata gösterme
        }

        // ✅ YENİ: Fiş yazdırma - Yazarkasa BAĞLI DEĞİLSE VEYA "Hem yazarkasa hem fiş yazıcısı" ayarı AKTİFSE yazdır
        console.log(`[FİŞ YAZDIRMA] 🔍 Kontrol - fiscalDeviceConnected: ${fiscalDeviceConnected}, useFiscalDeviceForCash: ${useFiscalDeviceForCash}, useBothFiscalAndReceiptPrinter: ${useBothFiscalAndReceiptPrinter}`);
        const shouldPrintReceipt = !fiscalDeviceConnected || useBothFiscalAndReceiptPrinter;
        if (shouldPrintReceipt) {
          console.log(`[FİŞ YAZDIRMA] 🖨️ Fiş yazdırılacak... (yazarkasa bağlı değil: ${!fiscalDeviceConnected}, hem yazarkasa hem fiş: ${useBothFiscalAndReceiptPrinter})`);
          try {
            const printStart = performance.now();
            let remainingBalance: number | undefined = undefined;
            if (prepaidSystemEnabled && !isGeneralCustomer) {
              // SQL'den güncellenmiş bakiyeyi çek
              try {
                // ✅ MULTI-TENANT: companyId parametresi eklendi
                if (!companyId) {
                  throw new Error('CompanyId bulunamadı');
                }
                
                const balanceStart = performance.now();
                const accountData = await accountingService.getAccountById(parseInt(selectedCustomer.id));
                const balanceEnd = performance.now();
                console.log(`[SQL] ⏱️ Bakiye çekme süresi: ${(balanceEnd - balanceStart).toFixed(2)}ms`);
                
                if (accountData) {
                  const currentCredit = accountData.CreditBalance || 0;
                  const currentDebit = accountData.DebitBalance || 0;
                  remainingBalance = currentCredit - currentDebit;
                  console.log("[SQL] Ön ödemeli sistem aktif, kalan bakiye fişe ekleniyor:", remainingBalance);
                }
              } catch (balanceError) {
                console.error("[SQL] Bakiye çekilirken hata:", balanceError);
                // Devam et, bakiye bilgisi olmadan
              }
            }

            // Fatura kalemlerini hazırla (fiş yazdırma için)
            const invoiceItems = paymentItems.map(item => ({
              id: `qs-${item.ProductId}-${Date.now()}`,
              itemId: item.ProductId,
              itemName: item.ProductName,
              quantity: item.Quantity,
              unit: item.Unit,
              unitPrice: item.UnitPrice,
              vatRate: item.VatRate,
              sctRate: item.SctRate,
              discountRate: 0,
              lineTotal: item.LineTotal,
              netTotal: item.LineTotal,
              vatAmount: item.VatAmount,
              sctAmount: item.SctAmount,
              grossTotal: item.GrossTotal
            }));

            // ✅ Fişte gösterilecek numara: Mutfak ekranı aktifse kısa kod, değilse referans numarası
            const receiptNumber = kitchenShortCode || response.ReferenceNumber || 'N/A';
            console.log('[FIŞ YAZDIRMA] 🎫 Fişte gösterilecek numara:', receiptNumber);

            await printReceiptAfterPayment(
              receiptNumber, 
              'NAKİT', 
              invoiceItems, 
              remainingBalance,
              kitchenShortCode // ✅ Mutfak ekranı kısa kodunu gönder (varsa fişte SİPARİŞ bölümü yazdırılır)
            );
            
            const printEnd = performance.now();
            console.log(`[SQL] ⏱️ Fiş yazdırma süresi: ${(printEnd - printStart).toFixed(2)}ms`);
          } catch (printError) {
            console.error('[SQL] Fiş yazdırma hatası:', printError);
            showSnackbar('Fiş yazdırılırken bir hata oluştu, ancak satış işlemi tamamlandı.', 'warning');
          }
        } else {
          console.log('[FIŞ YAZDIRMA] ✅ Yazarkasa bağlı ve "Hem yazarkasa hem fiş" ayarı KAPALI - fiş yazdırılmayacak');
        }

        // ✅ YENİ: Restoran modunda adisyonu kapat (CompletedSales'e taşı)
        if (isRestaurantMode && tableId) {
          const effectiveCloseTotal = calculateEffectiveTotal();
          console.log('[RESTORAN-NAKİT] 🍽️ Restoran modu aktif, adisyon kapatılıyor...', {
            tableId,
            isRestaurantMode,
            effectiveCloseTotal,
            cashPaymentCashboxId,
            cashPaymentCashboxName,
            restaurantActiveSaleIdOverride,
          });
          try {
            const { restaurantActiveSaleApi } = await import('../../../../services/restaurantApiService');

            // ✅ SEPET BÖLME FIX: Override varsa onu kullan (sol=main, sağ=splitChild).
            // Yoksa klasik davranış — tableId ile ana satışı bul.
            let targetActiveSaleId: number | null = restaurantActiveSaleIdOverride ?? null;
            const numericTableId = parseInt(tableId);
            if (!targetActiveSaleId) {
              console.log('[RESTORAN-NAKİT] 📡 getByTableId çağrılıyor, tableId:', numericTableId);
              const activeSale = await restaurantActiveSaleApi.getByTableId(numericTableId);
              console.log('[RESTORAN-NAKİT] 📡 getByTableId yanıtı:', activeSale ? { Id: activeSale.Id, BranchId: activeSale.BranchId, TotalAmount: activeSale.TotalAmount, TableId: activeSale.TableId } : 'NULL');
              targetActiveSaleId = activeSale?.Id ?? null;
            } else {
              console.log('[RESTORAN-NAKİT] 🎯 Sepet bölme override — kapatılacak ActiveSale Id:', targetActiveSaleId);
            }

            if (targetActiveSaleId) {
              console.log('[RESTORAN-NAKİT] ✅ Aktif satış bulundu, ID:', targetActiveSaleId);
              
              const cashPaymentDetails = JSON.stringify({
                cashAmount: effectiveCloseTotal,
                cashboxId: cashPaymentCashboxId,
                cashboxName: cashPaymentCashboxName || null,
              });
              
              const completePayload = {
                paymentMethod: 'Nakit',
                paymentDetailsJson: cashPaymentDetails,
                totalAmount: effectiveCloseTotal,
                completedBy: userDetails?.uid || null,
                completedByName: effectiveCreatedBy,
                saleNumber: response.ReferenceNumber || undefined,
                invoiceNumber: response.InvoiceNumber || undefined,
                referenceNumber: response.ReferenceNumber || undefined,
                closedByWaiterName: waiterName || undefined,
                isFiscalReceipt: useFiscalDeviceForCash,
                payments: [{
                  paymentMethod: 'cash',
                  amount: effectiveCloseTotal,
                  cashboxId: cashPaymentCashboxId ? parseInt(cashPaymentCashboxId) : undefined,
                  cashboxName: cashPaymentCashboxName || undefined,
                }],
              };
              console.log('[RESTORAN-NAKİT] 📤 completeSale çağrılıyor...', { activeSaleId: targetActiveSaleId, payload: completePayload });
              
              const completeResult = await restaurantActiveSaleApi.completeSale(targetActiveSaleId, completePayload);
              
              console.log('[RESTORAN-NAKİT] 📥 completeSale yanıtı:', JSON.stringify(completeResult));
              
              if (completeResult.success) {
                console.log('[RESTORAN-NAKİT] ✅ Adisyon başarıyla kapatıldı!', completeResult.data);
                showSnackbar(`Masa kapatıldı - ${completeResult.data?.saleNumber}`, 'success');
              } else {
                console.error('[RESTORAN-NAKİT] ❌ Adisyon kapatılamadı:', completeResult.message);
                showSnackbar(`Uyarı: Adisyon kapatılamadı - ${completeResult.message}`, 'warning');
              }
            } else {
              console.warn('[RESTORAN-NAKİT] ⚠️ Bu masa için aktif satış bulunamadı, tableId:', numericTableId);
            }
          } catch (restaurantError: any) {
            console.error('[RESTORAN-NAKİT] ❌ Restoran adisyon kapatma hatası:', restaurantError);
            console.error('[RESTORAN-NAKİT] ❌ Hata detayı:', restaurantError?.response?.data || restaurantError?.message);
            showSnackbar('Uyarı: Adisyon kapatılırken hata oluştu, ancak ödeme başarılı.', 'warning');
          }
        }

        // İşlemi tamamla
        const endTime = performance.now();
        console.log(`[SQL] ⏱️ TOPLAM İŞLEM SÜRESİ: ${(endTime - startTime).toFixed(2)}ms`);
        showSnackbar(`Satış (${response.ReferenceNumber}) başarıyla tamamlandı.`);
        
        // Kulüp girişi: ödeme sonrası kart atama
        if (onClubEntryPaymentComplete && response.QuickSaleReceiptId) {
          onClubEntryPaymentComplete({
            receiptId: response.QuickSaleReceiptId,
            referenceNumber: response.ReferenceNumber || '',
            paymentMethod: 'CASH',
            // Nakit PavoPay satış no'su → iade listesindeki kayda kart numarası iliştirilebilsin.
            pavoPaySaleNumber: cashFiscalData?.SaleNumber || documentId || undefined,
            cart: [...paymentCart],
          });
        }
        
        // ✅ Sepet bölme için: onPaymentSuccessOverride varsa onu çağır, yoksa clearCart
        if (onPaymentSuccessOverride) {
          onPaymentSuccessOverride();
        } else if (!skipClearCartAfterPayment) {
          clearCart();
        }
        
        // ✅ YENİ: Ödeme tamamlandı callback'i - yeni numara al (sadece hızlı satış modunda)
        // 🎫 NUMARA: Yenileme artık merkezi olarak endPaymentScope (finally) içinde yapılır.
        
        // ✅ YENİ: Restoran modundaysa RestaurantScreen'e geri dön (sepet bölme değilse)
        if (!skipNavigationAfterPayment && isRestaurantMode && restaurantScreenId && navigateToRestaurantScreen) {
          console.log('[RESTORAN] 🔙 Nakit ödeme tamamlandı, RestaurantScreen\'e geri dönülüyor...');
          setTimeout(() => {
            navigateToRestaurantScreen();
          }, 500);
        }
        }, 'handleCashPaymentForQuickSale', paymentRequest.IdempotencyKey);

        // ✅ Ödeme niyeti: cihazsız akışta kayıttan hemen önce açılır; ardından son istek
        // "cihaz sonucu" olarak bildirilir (kayıt isteğinden ÖNCE başlar, beklenmez).
        {
          const cashDeviceUsed = useFiscalDeviceForCash && !skipFiscalDevice && fiscalDeviceConnected;
          if (!wasPaymentIntentAttempted(cashIntentKey)) {
            await openPaymentIntent({
              key: cashIntentKey,
              endpoint: 'process-cash-with-invoice',
              payload: paymentRequest,
              amount: paymentRequest.TotalAmount,
              paymentKind: 'nakit',
              fiscalDevice: cashDeviceUsed ? telemetryFiscalDevice(selectedFiscalDevice) : 'yok',
            });
          }
          void reportDeviceResult(cashIntentKey, paymentRequest, {
            fiscalReference: cashFiscalData?.SaleNumber || documentId || null,
            fiscalResult: cashDeviceUsed ? 'onaylandi' : skipFiscalDevice ? 'kasiyer-devam-etti' : 'cihaz-yok',
          });
        }

        // Kurtarma kuyruğu: istek gitmeden önce yazılır, başarıda silinir
        savePendingAtomicPayment('process-cash-with-invoice', paymentRequest, companyId);

        // ===== ATOMİK API ÇAĞRISI (CARİ + KASA + STOK + FATURA) =====
        const apiCallStart = performance.now();
        // ✅ NUMARA GARANTİSİ: Atomik SQL kaydı deneniyor — deneme başladı
        markPaymentAttempt();
        const response = await salesService.processCashWithInvoice(paymentRequest);
        const apiCallEnd = performance.now();
        console.log(`[SQL] ⏱️ API çağrısı süresi: ${(apiCallEnd - apiCallStart).toFixed(2)}ms`);

        console.log("[ATOMİK] ✅ API yanıtı:", response);

        if (!response.Success) {
          throw new AtomicSaveError(
            response.Message || response.ErrorDetails || 'Nakit ödeme işlemi başarısız oldu (tüm işlemler geri alındı)',
            response
          );
        }

        cashSaveSucceeded = true;
        removePendingAtomicPayment(paymentRequest.IdempotencyKey);
        await cashCompleteAfterSave(response);

      } catch (error: any) {
        console.error("[SQL] Nakit atomik işlem hatası:", error);
        if (cashSaveSucceeded) {
          // Satış kayıtlı; yalnızca sonraki adımlardan biri düştü → kayıt tekrarlanmaz
          showSnackbar('Satış kaydedildi ancak sonraki adımlardan biri (masa/fiş/mutfak) tamamlanamadı. Lütfen kontrol edin.', 'warning');
        } else {
          const cashFiscalUsed = useFiscalDeviceForCash && !skipFiscalDevice && fiscalDeviceConnected;
          handleAtomicSaveFailure(error, {
            showAlertModal,
            showSnackbar,
            request: paymentRequest,
            send: (req) => salesService.processCashWithInvoice(req),
            onSaved: async (resp) => { if (cashCompleteAfterSave) await cashCompleteAfterSave(resp); },
            intro: 'Nakit ödeme alındı ancak kayıt yapılamadı.',
            logLabel: 'Nakit atomik işlem',
            telemetry: buildSaveTelemetry({
              endpoint: '/api/QuickSaleWithInvoice/process-cash-with-invoice',
              paymentKind: 'nakit',
              fiscalDevice: cashFiscalUsed ? telemetryFiscalDevice(selectedFiscalDevice) : 'yok',
              fiscalDone: cashFiscalUsed ? !!(documentId || cashFiscalData) : false,
              fiscalReference: cashFiscalData?.SaleNumber || documentId || null,
              amount: paymentRequest?.TotalAmount ?? null,
            }),
          });
        }
      } finally {
        setIsProcessingPayment(false);
      }
              } else {
      // ÖKC işlemi başarısız oldu
      setIsProcessingPayment(false);
    }
  };

  // POS ödeme işleme fonksiyonu (SQL + Yazarkasa)
  // skipDeptVatCheck: Ingenico departman-KDV uyumsuzluğu modalında "Devam Et" seçildiyse true
  const handlePosPaymentForQuickSaleInner = async (
    bankAccountId: number,
    bankSubAccountId: number | null,
    bankAccountName: string,
    useFiscalDevice: boolean,
    skipDeptVatCheck: boolean = false,
    // Beko geç onayı / Ingenico "Yine de Devam Et" yeniden çağrısında ilk denemenin ödeme niyeti anahtarı
    intentKeyOverride?: string
  ) => {
    const startTime = performance.now();
    console.log("[handlePosPaymentForQuickSale] ⏱️ Başlıyor...");

    if (isProcessingPayment) return;

    // ✅ Beko geç yanıt kontrolü sürüyor: cihaz yeniden çalıştırılırsa çift çekim olabilir.
    // (Kontrolün kendi otomatik kaydı useFiscalDevice=false ile gelir, bu kapıdan geçer.)
    if (useFiscalDevice && bekoLatePollActiveRef.current) {
      showSnackbar('Yazarkasanın son fiş durumu kontrol ediliyor — lütfen birkaç saniye bekleyin.', 'warning');
      return;
    }

    // ✅ Harici POS: fiscal adım yok — doğrudan SQL kayıtlarına geçilir.
    if (isExternalPosDevice && useFiscalDevice) {
      console.log("[handlePosPaymentForQuickSale] ℹ️ Harici POS seçili — yazarkasa adımları atlanıyor.");
      useFiscalDevice = false;
    }

    // ✅ Rest-P2P: tek tuş POS → önce Pavo cihazına yolla, başarı sonrası
    // PartialPayment akışını (fiscalProcessed=true) tetikle.
    if (isRestP2PEnabled && useFiscalDevice) {
      // ✅ Fiscal/POS tutar HER ZAMAN güncel sepetten (cartRef.current) hesaplanır.
      // `calculateEffectiveTotal` closure'dan bayat sepet okuyabiliyordu: kulüp girişinde
      // depozito ("Kart Ücreti"), bu çağrıdan hemen önce setTimeout içinde sepete ekleniyor;
      // bu yüzden Pavo'ya depozito hariç eksik tutar gidiyor, kalan kısım SQL'de indirim
      // gibi görünüyordu. Ayrıca depozitolar skipFiscal'e göre ayrılır (non-Rest-P2P
      // posFiscalCart mantığıyla aynı):
      //   • POS depozito (skipFiscal=false) → Pavo'ya dahil (kart ücreti fişte görünür)
      //   • Nakit depozito (skipFiscal=true) → Pavo'dan hariç, SQL'de nakit olarak kaydedilir
      const p2pSourceCart = splitCartOverride ?? cartRef.current;
      const posFiscalCart = p2pSourceCart.filter(
        (ci) => !ci.id.startsWith('DEPOSIT-') || !(ci as any).skipFiscal
      );
      const cashDepositTotal = Number(
        p2pSourceCart
          .filter((ci) => ci.id.startsWith('DEPOSIT-') && (ci as any).skipFiscal)
          .reduce((s, ci) => s + ci.price * ci.quantity, 0)
          .toFixed(2)
      );
      const posTotal = calculateTotalFromCart(posFiscalCart);
      const grandTotal = Number((posTotal + cashDepositTotal).toFixed(2));
      if (grandTotal <= 0) {
        showAlertModal('Uyarı', 'Ödeme yapmak için sepette ürün olmalıdır.');
        return;
      }
      // Kullanılan POS seçeneğinin id'sini bul (bankAccountId üzerinden eşleştir)
      // ⚠️ posOptions.bankAccountId string, gelen parametre number → String() ile normalize
      const matchedPosOption = posOptions.find(
        (p) => String(p.bankAccountId) === String(bankAccountId)
      );
      // Fallback: eşleşmese bile Rest-P2P akışı bankAccountId üzerinden çalışabilir
      const posId = matchedPosOption?.id || `pos_${bankAccountId}`;
      // ⚠️ Rest-P2P akışında SADECE PavoRestP2PWaitModal gösterilir;
      //    eski mavi "Ödeme Bekleniyor" Backdrop (isProcessingPosPayment)
      //    AYNI ANDA açılmasın diye burada SET ETMİYORUZ.
      // Kilit SQL kaydı bitene kadar kalır (sepet o ana kadar ekranda durur).
      setIsProcessingPayment(true);
      try {
        // Pavo'ya YALNIZCA fiscal (POS) tutar gider; nakit depozito Pavo'ya gönderilmez,
        // SQL tarafında paidByCash olarak kaydedilir.
        let outcome: 'completed' | 'continue_without_fiscal' | 'failed' = 'completed';
        const posSaveDetails: PaymentDetails = {
          paidByCash: cashDepositTotal,
          paidByPosMap: posTotal > 0 ? { [posId]: posTotal } : {},
          paidByOpenAccount: 0,
          totalPaid: grandTotal,
          remainingAmount: 0,
          changeAmount: 0,
          fiscalProcessed: true,
          // Kulüp girişi: POS butonuyla giriş → aktif girişlerde "POS" gösterilsin
          // (atomik akış aksi halde 'PARTIAL' kaydedip "Nakit" gösteriyordu).
          clubEntryPaymentMethodOverride: 'POS',
        };
        // ✅ Ödeme niyeti: anahtar + Pavo istek kimliği cihaza gitmeden ÖNCE belli olur
        const posIntentKey = newPaymentIntentKey();
        const posRequestId = createRestP2PRequestId();
        if (posTotal > 0) {
          const parts: RestP2PPaymentPart[] = [
            { method: 'pos', amount: posTotal, label: bankAccountName || matchedPosOption?.label || 'POS', order: 0 },
          ];
          outcome = await runRestP2PFlow('pos', parts, posTotal, {
            key: posIntentKey,
            requestId: posRequestId,
            saveDetails: posSaveDetails,
          });
          if (outcome === 'failed') return;
        }
        // ✅ "completed" veya "continue_without_fiscal": atomik SQL kaydını yap.
        //   - completed                → mali fiş Pavo'da kesildi
        //   - continue_without_fiscal  → kullanıcı "Yine de Devam Et" dedi
        const wasFiscalSkippedPos = outcome === 'continue_without_fiscal';
        await processPaymentAfterPartialModal({
          ...posSaveDetails,
          fiscalData: wasFiscalSkippedPos
            ? {
                source: 'pavo_rest_p2p_skipped_offline',
                deviceCode: screenSettings?.pavoRestP2PDeviceScreenCode,
                reason: 'user_continued_without_device',
              }
            : {
                source: 'pavo_rest_p2p',
                deviceCode: screenSettings?.pavoRestP2PDeviceScreenCode,
                SaleNumber: lastRestP2PSaleNumberRef.current || undefined,
                InvoiceNo: lastRestP2PInvoiceNoRef.current || undefined,
                EInvoiceETTN: lastRestP2PEttnRef.current || undefined,
                PavoPayInquiryLink: lastRestP2PInquiryLinkRef.current || undefined,
              },
        }, {
          intentKey: posIntentKey,
          // POS tutarı 0 ise (yalnız nakit depozito) cihaza hiç gidilmedi
          pavoRequestId: posTotal > 0 ? posRequestId : null,
          fiscalResult: posTotal <= 0 ? 'cihaz-yok' : wasFiscalSkippedPos ? 'kasiyer-devam-etti' : 'onaylandi',
        });
      } finally {
        setIsProcessingPayment(false);
      }
      return;
    }

    setIsProcessingPayment(true);
    setIsProcessingPosPayment(true); // POS ödeme başladı
    
    // cartRef.current kullan: closure'dan bağımsız olarak her zaman güncel sepete erişir
    const paymentCart = splitCartOverride ?? cartRef.current;
    console.log(`[handlePosPaymentForQuickSale] 📦 Ödeme yapılacak sepet: ${paymentCart.length} ürün (splitCartOverride: ${splitCartOverride ? 'VAR' : 'YOK'})`);
    
    // Validasyon
    if (paymentCart.length === 0) {
      showAlertModal("Uyarı", "Ödeme yapmak için sepette ürün olmalıdır.");
      setIsProcessingPayment(false);
      setIsProcessingPosPayment(false);
      return;
    }
    if (!companyId) {
      showAlertModal("Hata", "Şirket bilgisi bulunamadı. İşlem yapılamıyor.");
      setIsProcessingPayment(false);
      setIsProcessingPosPayment(false);
      return;
    }
    if (!selectedCustomer) {
      showAlertModal("Hata", "Cari hesap seçilemedi. Lütfen bir cari hesap seçin.");
      setIsProcessingPayment(false);
      setIsProcessingPosPayment(false);
      return;
    }

    console.log("[handlePosPaymentForQuickSale] ⏱️ Validasyon süresi: " + (performance.now() - startTime).toFixed(2) + "ms");

    // ✅ ÖKC KULLANIMI İÇİN ÖN KONTROL - DEPARTMAN NUMARASI
    if (useFiscalDevice) {
      console.log("[handlePosPaymentForQuickSale] 🔍 ÖKC açık, sepetteki ürünlerin departman numaraları kontrol ediliyor...");
      console.log("[handlePosPaymentForQuickSale] 🔍 Sepetteki ürün sayısı:", paymentCart.length);
      console.log("[handlePosPaymentForQuickSale] 🔍 Toplam inventory item sayısı:", allInventoryItems.length);

      const productsWithoutDeptNo = [];
      
      for (const cartItem of paymentCart) {
        if (cartItem.id.startsWith('DEPOSIT-')) continue;
        const product = allInventoryItems.find((inv: QuickSaleInventoryItem) => inv.id === cartItem.id);
        console.log(`[handlePosPaymentForQuickSale] 🔍 Ürün kontrol: "${product?.name}" - Departman No: ${product?.departmentNo}`);
        
        if (!product?.departmentNo || product.departmentNo < 1 || product.departmentNo > 100) {
          productsWithoutDeptNo.push(product?.name || 'Bilinmeyen Ürün');
        }
      }

      if (productsWithoutDeptNo.length > 0) {
        showAlertModal(
          "Departman Numarası Eksik",
          `Aşağıdaki ürünler için departman numarası (1-100 arası) tanımlanmamış:\n\n` +
          productsWithoutDeptNo.map(name => `• ${name}`).join('\n') +
          `\n\nLütfen ürün ayarlarından departman numarası ekleyiniz.`
        );
        setIsProcessingPayment(false);
        setIsProcessingPosPayment(false);
        return;
      }

      console.log("[handlePosPaymentForQuickSale] ✅ Tüm ürünlerin departman numarası mevcut.");
    }

    console.log("[handlePosPaymentForQuickSale] ⏱️ Validasyon süresi: " + (performance.now() - startTime).toFixed(2) + "ms");

    // ✅ Ödeme niyeti: işlem anahtarı cihaza gitmeden ÖNCE belli olur; aynı anahtar niyet,
    // cihaz sonucu, kayıt, "Yeniden Dene", bekleyen kayıt kuyruğu ve telemetride kullanılır.
    // (Beko geç onayı / Ingenico "Yine de Devam Et" yeniden çağrısında aynı anahtar devralınır.)
    const posIntentKey = intentKeyOverride || newPaymentIntentKey();
    /**
     * POS atomik isteği (process-pos-with-invoice): niyet için cihazdan ÖNCE (mali alanlar
     * boş), kayıt için cihazdan SONRA (mali alanlar dolu) AYNI fonksiyonla üretilir.
     * Depo bulunamazsa hata fırlatır.
     */
    const buildPosRequest = (fiscal: any): QuickSalePosWithInvoiceRequest => {
      // closure-safe: paymentCart'tan toplam hesapla
      const posEffectiveTotal = calculateTotalFromCart(paymentCart);
      // Deposito items'ları SQL API'den filtrele (stok hareketi gerekmez)
      const posRegularCartItems = paymentCart.filter(item => !item.id.startsWith('DEPOSIT-'));

      const cartItemsForApi = posRegularCartItems.map((item) => {
        const product = allInventoryItems.find((inv: QuickSaleInventoryItem) => inv.id === item.id);
        
        const warehouseId = resolveEffectiveWarehouseId(item.id, product?.categoryId, screenSettings, branchWarehouseId);
        
        if (!warehouseId) {
          console.error(`[SQL] HATA: Ürün için warehouseId bulunamadı:`, product);
          throw new Error(`[SQL] Ürün için depo bilgisi bulunamadı: ${item.name}. Lütfen ürün ayarlarını kontrol edin.`);
        }
        
        // ✅ KDV Dahil/Hariç bilgisi: Sepetteki vatStatus'a göre PriceType belirle
        const priceType = item.vatStatus === 'included' ? 'VAT_INCLUDED' : 'VAT_EXCLUDED';
        
        console.log('[SQL] Product warehouseId:', warehouseId, 'type:', typeof warehouseId);
        return {
          ProductId: item.id,
          WarehouseId: warehouseId, // ✅ Artık yedek depo ID'si de kullanılıyor
          ProductName: product?.name || 'Ürün',
          Quantity: item.quantity,
          UnitPrice: item.price,
          TotalPrice: item.quantity * item.price,
          VatRate: product?.vatRate || 0,  // ✅ KDV oranı eklendi
          SctRate: product?.sctRate || 0,   // ✅ ÖTV oranı eklendi
          // ✅ KDV Dahil/Hariç bilgisi (fatura hesaplaması için)
          PriceType: priceType,
          // ✅ Reçete/Menü bazlı stok düşme için
          HasRecipe: product?.hasRecipe || false,
          HasMenu: product?.hasMenu || false,
          // ✅ YENİ: Opsiyonlu menü için seçilen opsiyonlar
          SelectedOptions: (item as any).selectedOptions || null,
          TotalPriceDifference: (item as any).totalPriceDifference || 0,
          // ✅ YENİ: İndirim kaydı (faturaya gerçek indirim için)
          ...buildItemDiscountFields(item)
        };
      });

      console.log('[ATOMİK] CartItemsForApi:', cartItemsForApi);

      // ✅ YENİ: Atomik Request - Fatura dahil!
      // ✅ Restoran modunda Description "Restoran Satışı" olmalı
      const posDescription = isRestaurantMode 
        ? `Restoran Satışı (POS) - ${selectedCustomer.companyName}`
        : `POS Satış - ${selectedCustomer.companyName}`;
      
      const builtRequest: QuickSalePosWithInvoiceRequest = {
        IdempotencyKey: posIntentKey,

        CompanyName: companyName || null,
        ScreenId: screenId ? parseInt(screenId.toString(), 10) : null,
        AccountId: parseInt(selectedCustomer.id.toString(), 10),
        AccountName: selectedCustomer.companyName,
        BankAccountId: bankAccountId,
        BankSubAccountId: bankSubAccountId,
        BankAccountName: bankAccountName,
        TotalAmount: posEffectiveTotal, // ✅ FIX: Split cart modunda doğru toplam
        Currency: 'TRY',
        Description: posDescription,
        AuthCode: fiscal?.AuthCode,
        BatchNo: fiscal?.BatchNo,
        MaskedCardNumber: fiscal?.MaskedCardNumber,
        // ✅ PavoPay'de SaleNumber fallback: POS iadesinde ilişkili fatura/gün sonu
        // fişinin backend'de bulunabilmesi için kritik (FiscalTransactionId eşleşmesi).
        FiscalTransactionId: fiscal?.FiscalTransactionId || fiscal?.SaleNumber,
        CardType: fiscal?.CardType,
        CreatedBy: effectiveCreatedBy, // ✅ Kasiyer varsa kasiyer adı kullanılır
        BranchId: userDetails?.branchesId ? parseInt(userDetails.branchesId, 10) : null, // ✅ Gün sonu raporu için kritik! (string → int)
        BranchName: userDetails?.branchesId ? getBranchName(userDetails.branchesId) : null, // ✅ YENİ: Kasa hareket raporu için şube adı
        IsRestaurantMode: isRestaurantMode, // ✅ YENİ: Restoran modu için RS- prefix
        CashierId: currentCashierId || null, // ✅ YENİ: Kasiyer ID (gün sonu raporu için)
        CashierName: currentCashierName || null, // ✅ YENİ: Kasiyer adı (gün sonu raporu için)
        IsFiscalReceipt: true,
        PavoPaySaleNumber: fiscal?.SaleNumber || null,
        PavoPayInvoiceNo: fiscal?.InvoiceNo || null,
        EInvoiceETTN: fiscal?.EInvoiceETTN || null,
        PavoPayInquiryLink: fiscal?.PavoPayInquiryLink || null,
        // ✅ YENİ: Sepete uygulanan indirim kaydı (fiş başlığı)
        ...buildHeaderDiscountFields(cart, appliedDiscountType, lastAppliedDiscountAmount),
        Items: cartItemsForApi.map(item => ({
          ProductId: item.ProductId,
          ProductName: item.ProductName,
          WarehouseId: item.WarehouseId,
          Quantity: item.Quantity,
          UnitPrice: item.UnitPrice,
          VatRate: item.VatRate,
          SctRate: item.SctRate,
          PriceType: item.PriceType,
          HasRecipe: item.HasRecipe,
          HasMenu: item.HasMenu,
          SelectedOptions: item.SelectedOptions,
          TotalPriceDifference: item.TotalPriceDifference,
          // ✅ YENİ: İndirim kaydı (fatura kalemi)
          OriginalPrice: item.OriginalPrice,
          DiscountRate: item.DiscountRate,
          DiscountAmount: item.DiscountAmount
        }))
      };
      return builtRequest;
    };

    // ÖKC işlemi
    let fiscalSuccess = false;
    let fiscalData: any = null;

    if (useFiscalDevice) {
      console.log("[handlePosPaymentForQuickSale] 🔍 ÖKC Kullanım Ayarı: AÇIK ✅");
      console.log(`[handlePosPaymentForQuickSale] 🔍 Seçilen Yazarkasa: ${fiscalDeviceName} (${selectedFiscalDevice}), Aktif: ${isFiscalDeviceActive}`);

      // ✅ Önce cihazın desteklenip desteklenmediğini kontrol et
      if (!isFiscalDeviceActive) {
        setPosPaymentError(
          `Seçili yazarkasa (${fiscalDeviceName}) için entegrasyon henüz geliştirilmemiştir.\n\n` +
          `Lütfen Ayarlar > Ödeme Yöntemleri bölümünden "Hugin T300" yazarkasasını seçiniz.`
        );
        setIsPosErrorModalOpen(true);
        setPosRetryCallback(() => () => handlePosPaymentForQuickSale(bankAccountId, bankSubAccountId, bankAccountName, useFiscalDevice));
        
        setIsProcessingPayment(false);
        setIsProcessingPosPayment(false);
        return;
      }

      // ✅ YENİ: Yazarkasa bağlantısını kontrol et
      console.log(`[handlePosPaymentForQuickSale] 🔌 Yazarkasa bağlantısı kontrol ediliyor...`);
      let fiscalDeviceConnected = false;
      try {
        fiscalDeviceConnected = fiscalDevice.checkConnection ? await fiscalDevice.checkConnection() : false;
        console.log(`[handlePosPaymentForQuickSale] 🔌 Bağlantı durumu: ${fiscalDeviceConnected ? 'BAĞLI ✅' : 'BAĞLI DEĞİL ❌'}`);
      } catch (connError) {
        console.error(`[handlePosPaymentForQuickSale] 🔌 Bağlantı kontrolü hatası:`, connError);
        fiscalDeviceConnected = false;
      }

      // ✅ Yazarkasa BAĞLI DEĞİLSE
      if (!fiscalDeviceConnected) {
        // warnIfFiscalNotConnected AÇIKSA → Onay modalı göster
        // warnIfFiscalNotConnected KAPALIYSA → Sessizce devam et
        if (warnIfFiscalNotConnected) {
          console.log(`[handlePosPaymentForQuickSale] ⚠️ Yazarkasa bağlı değil! warnIfFiscalNotConnected=true, onay modalı gösteriliyor...`);
          
          // Callback'i kaydet - kullanıcı onaylarsa SQL'e devam edilecek
          setPendingPosPaymentCallback(() => async () => {
            console.log(`[handlePosPaymentForQuickSale] 👤 Kullanıcı onayladı, yazarkasa OLMADAN SQL'e devam ediliyor...`);
            // useFiscalDevice=false ile devam et (yazarkasa atlanacak)
            await handlePosPaymentForQuickSale(bankAccountId, bankSubAccountId, bankAccountName, false);
          });
          
          // Onay modalını aç
          setIsFiscalDisconnectedConfirmOpen(true);
          
          setIsProcessingPayment(false);
          setIsProcessingPosPayment(false);
          return;
        } else {
          // ✅ YENİ: warnIfFiscalNotConnected KAPALI - uyarı göstermeden devam et
          console.log(`[handlePosPaymentForQuickSale] ⚠️ Yazarkasa bağlı değil ama warnIfFiscalNotConnected=false, yazarkasa ATLANIYOR...`);
          useFiscalDevice = false; // Yazarkasayı atla, SQL'e devam et
        }
      }

      // ✅ YENİ: Ingenico 5000F - POS ödemesi öncesi departman-KDV uyumluluğu kontrolü.
      // Nakit ve karma ödemede zaten vardı; POS-only akışta eksikti — uyumsuz departman
      // KDV'siyle mali fiş kesilme riski vardı. Cihaz bağlıysa kontrol edilir.
      if (useFiscalDevice && fiscalDeviceConnected && selectedFiscalDevice === 'ingenico_5000f' && !skipDeptVatCheck && onIngenicoDeptVatMismatch) {
        const syncProducts = paymentCart.filter(ci => !ci.id.startsWith('DEPOSIT-')).map((cartItem) => {
          const inv = allInventoryItems.find(item => item.id === cartItem.id);
          return {
            departmentNo: (inv?.departmentNo ?? 0) as number,
            vatRate: inv?.vatRate ?? 0,
            name: cartItem.name || inv?.name || '',
          };
        }).filter(p => p.departmentNo >= 1);
        if (syncProducts.length > 0) {
          const syncResult = await syncDepartmentVatMapping(syncProducts);
          if (syncResult.hasMismatches) {
            console.warn('[handlePosPaymentForQuickSale] Ingenico departman-KDV uyumsuzluğu tespit edildi. Ödeme DURDURULDU.');
            onIngenicoDeptVatMismatch(syncResult);
            setIsProcessingPayment(false);
            setIsProcessingPosPayment(false);
            return;
          }
        }
      }

      const fiscalStartTime = performance.now();

      try {
        console.log(`[handlePosPaymentForQuickSale] ${fiscalDeviceName} ile POS ödeme başlıyor...`);

        // ✅ Ürünleri FiscalProductItem formatına çevir
        // PavoPay ve Beko KDV DAHİL fiyat bekler, Inpos/Hugin KDV HARİÇ fiyat bekler
        const usesVatIncludedPricing = selectedFiscalDevice === 'pavopay' || selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr';
        
        // Fiscal ürün listesi: POS depositoları dahil (skipFiscal=false), nakit depositoları hariç (skipFiscal=true)
        const posFiscalCart = paymentCart.filter(ci => !ci.id.startsWith('DEPOSIT-') || !(ci as any).skipFiscal);

        const fiscalProducts: FiscalProductItem[] = posFiscalCart.map((cartItem) => {
          const isDeposit = cartItem.id.startsWith('DEPOSIT-');
          const product = isDeposit ? null : allInventoryItems.find((inv: QuickSaleInventoryItem) => inv.id === cartItem.id);
          if (!product && !isDeposit) {
            throw new Error(`Ürün bulunamadı: ${cartItem.id}`);
          }

          // Nakit akışındaki ile aynı gerekçe: çözülemeyen oranı 0'a çevirmek
          // fişi KDV'siz kestiriyordu.
          const vatRate = resolveCartItemVatRate(cartItem);
          if (vatRate === undefined) {
            throw new Error(
              `"${cartItem.name}" ürününün KDV oranı belirlenemedi. Fişin KDV'siz ` +
              `kesilmemesi için işlem durduruldu. Ürünün stok kartındaki KDV oranını kontrol edin.`
            );
          }
          const isVatIncluded = cartItem.vatStatus === 'included';
          
          let fiscalPrice: number;
          
          if (usesVatIncludedPricing) {
            if (isVatIncluded) {
              fiscalPrice = cartItem.price;
              console.log(`[POS-${selectedFiscalDevice?.toUpperCase()}] ${cartItem.name}: ${fiscalPrice.toFixed(2)} TL (KDV dahil, olduğu gibi)`);
            } else {
              fiscalPrice = cartItem.price * (1 + vatRate / 100);
              console.log(`[POS-${selectedFiscalDevice?.toUpperCase()}] ${cartItem.name}: ${cartItem.price} TL (KDV hariç) → ${fiscalPrice.toFixed(2)} TL (KDV dahil)`);
            }
          } else {
            if (isVatIncluded) {
              fiscalPrice = cartItem.price / (1 + vatRate / 100);
              console.log(`[POS-INPOS/HUGIN] ${cartItem.name}: ${cartItem.price} TL (KDV dahil) → ${fiscalPrice.toFixed(2)} TL (KDV hariç)`);
            } else {
              fiscalPrice = cartItem.price;
              console.log(`[POS-INPOS/HUGIN] ${cartItem.name}: ${fiscalPrice.toFixed(2)} TL (KDV hariç, olduğu gibi)`);
            }
          }
          
          return {
            id: cartItem.id,
            ...toPackageFiscalLine(cartItem, { name: cartItem.name, quantity: cartItem.quantity, price: fiscalPrice }),
            vatRate,
            departmentNo: isDeposit ? 1 : product!.departmentNo!,
            barcode: isDeposit ? '' : (product!.barcode || ''),
          };
        });

        // Sadece nakit depositoları (skipFiscal=true) fiscal toplamdan çıkar; POS depositoları dahil kalır
        const posCashOnlyDepositTotal = paymentCart
          .filter(ci => ci.id.startsWith('DEPOSIT-') && (ci as any).skipFiscal)
          .reduce((sum, ci) => sum + ci.price * ci.quantity, 0);

        let fiscalTotalForPosDevice: number;
        
        if (selectedFiscalDevice === 'inpos_m530') {
          let inposTotalKurus = 0;
          const effectiveTotal = calculateTotalFromCart(paymentCart) - posCashOnlyDepositTotal;
          
          for (const fp of fiscalProducts) {
            const priceWithVat = fp.price * (1 + fp.vatRate / 100);
            const unitPriceKurus = Math.round(priceWithVat * 100);
            const itemTotalKurus = unitPriceKurus * fp.quantity;
            inposTotalKurus += itemTotalKurus;
          }
          
          const farkKurus = Math.round(effectiveTotal * 100) - inposTotalKurus;
          if (farkKurus !== 0 && Math.abs(farkKurus) <= 50) {
            inposTotalKurus += farkKurus;
          }
          
          fiscalTotalForPosDevice = inposTotalKurus / 100;
          console.log(`[POS-INPOS] 🔢 Inpos toplam: ${fiscalTotalForPosDevice.toFixed(2)} TL (nakit deposito hariç: ${posCashOnlyDepositTotal.toFixed(2)} TL)`);
        } else {
          fiscalTotalForPosDevice = parseFloat((calculateTotalFromCart(paymentCart) - posCashOnlyDepositTotal).toFixed(2));
          console.log(`[POS-${selectedFiscalDevice?.toUpperCase()}] 🔢 Hizmet toplamı: ${fiscalTotalForPosDevice.toFixed(2)} TL (nakit deposito hariç: ${posCashOnlyDepositTotal.toFixed(2)} TL)`);
        }

        // ✅ YENİ: PavoPay için mutfak modu sipariş numarası ve şirket adı
        // @ts-ignore - useKitchenScreen screenSettings'de var
        const useKitchenScreenForPos = screenSettings?.useKitchenScreen || false;
        const kitchenOrderCodeForPos = (selectedFiscalDevice === 'pavopay' && useKitchenScreenForPos && reservedShortCode) 
          ? reservedShortCode 
          : undefined;
        // @ts-ignore - receiptDisplaySettings screenSettings'de var
        const receiptCompanyNameForPos = (selectedFiscalDevice === 'pavopay' && useKitchenScreenForPos) 
          ? (screenSettings?.receiptDisplaySettings?.companyName || companyName || undefined)
          : undefined;
        
        // ✅ Ödeme niyeti: yazarkasaya/POS'a gitmeden ÖNCE (en fazla 4 sn; yazılamazsa devam)
        {
          let posIntentPayload: QuickSalePosWithInvoiceRequest | null = null;
          try {
            posIntentPayload = buildPosRequest(null);
          } catch (e) {
            console.warn('[PaymentIntent] POS niyet isteği hazırlanamadı (ödeme devam ediyor):', e);
          }
          if (posIntentPayload) {
            await openPaymentIntent({
              key: posIntentKey,
              endpoint: 'process-pos-with-invoice',
              payload: posIntentPayload,
              amount: posIntentPayload.TotalAmount,
              paymentKind: 'pos',
              fiscalDevice: telemetryFiscalDevice(selectedFiscalDevice),
            });
          }
        }

        // ✅ Yazarkasa entegrasyonunu çağır
        const useExternalPrinterForPos = screenSettings?.useExternalPrinterForPavo || false;
        // ✅ NUMARA GARANTİSİ: Yazarkasaya POS isteği gidiyor — deneme başladı
        markPaymentAttempt();
        const result = await fiscalDevice.processPosPayment(
          fiscalProducts,
          fiscalTotalForPosDevice,
          showAlertModal,
          undefined, // cashAmount - POS için yok
          companyId || undefined,
          kitchenOrderCodeForPos,
          receiptCompanyNameForPos,
          useExternalPrinterForPos
        );

        if (!result.success) {
          throw new Error(result.errorMessage || 'Yazarkasa işlemi başarısız oldu');
        }

        fiscalData = result.fiscalData || null;
        fiscalSuccess = true;
        console.log(`[handlePosPaymentForQuickSale] ✅ ${fiscalDeviceName} işlemi başarılı. Belge ID: ${result.documentId}`);

        // Beko: Cihaz üzerinde ödeme türü değiştirilmiş olabilir (örn: POS→Nakit)
        if (fiscalData?.PaymentTypeChanged && fiscalData?.ActualPaymentItems) {
          const allCash = fiscalData.ActualPaymentItems.every((p: any) => p.type === 1);
          if (allCash) {
            console.log('[handlePosPaymentForQuickSale] ⚠️ BEKO: Kasiyerden ödeme türü NAKİT olarak değiştirildi! Nakit ödeme akışına yönlendiriliyor...');
            
            const fiscalEndTime = performance.now();
            console.log(`[handlePosPaymentForQuickSale] ⏱️ ÖKC işlemi süresi: ${(fiscalEndTime - fiscalStartTime).toFixed(2)}ms`);
            
            setIsProcessingPosPayment(false);
            setIsProcessingPayment(false);
            // POS niyeti kapanır (kart çekilmedi); satış nakit akışında KENDİ niyetiyle kaydedilir
            abandonPaymentIntent(posIntentKey, 'Beko: ödeme türü cihazda NAKİT olarak değiştirildi — satış nakit niyetiyle kaydediliyor');
            await handleCashPaymentForQuickSale(true);
            return;
          }
          
          console.log(`[handlePosPaymentForQuickSale] ⚠️ BEKO: Ödeme türü değişti. Yeni tür: ${fiscalData.PaymentType}`);
        }

        const fiscalEndTime = performance.now();
        console.log(`[handlePosPaymentForQuickSale] ⏱️ ÖKC işlemi süresi: ${(fiscalEndTime - fiscalStartTime).toFixed(2)}ms`);

      } catch (fiscalError: any) {
        const errorMessage = fiscalError.message || 'Bilinmeyen hata';
        const isTimeoutError = errorMessage.includes('zaman aşımı') || errorMessage.includes('zaman asimi') || errorMessage.toLowerCase().includes('timeout');
        const isBekoDevice = selectedFiscalDevice === 'beko_x30tr' || selectedFiscalDevice === 'beko_300tr';
        
        if (isTimeoutError) {
          console.warn(`[handlePosPaymentForQuickSale] ⏱️ ${fiscalDeviceName} - Zaman aşımı:`, errorMessage);
        } else {
          console.error(`[handlePosPaymentForQuickSale] ❌ ${fiscalDeviceName} hatası:`, fiscalError);
        }
        
        // ✅ Ödeme niyeti: kart KESİN çekilmediyse (zaman aşımı değil, Ingenico "devam" seçeneği
        // sunulmuyor) niyet terk edilir. Belirsiz sonuçta açık kalır (Beko geç onayı aynı anahtarla kaydeder).
        if (!isTimeoutError && selectedFiscalDevice !== 'ingenico_5000f' && isDeviceFailureCertain(errorMessage)) {
          abandonPaymentIntent(posIntentKey, `Yazarkasa/POS hatası: ${errorMessage}`);
        }

        // Hata modalını aç
        // ✅ Beko zaman aşımında arka planda son fiş kontrolü (aşağıda) sürerken "Yeniden Dene"
        // cihazı YENİDEN ÇALIŞTIRMAZ: kontrol fişi bulursa satış tek sefer kaydedilir; bulamazsa
        // pencere yeniden açılır ve karar yine kasiyere kalır. (Eskiden ikisi birden çalışıp
        // çift çekim + iki satış oluşabiliyordu.)
        const rerunPosDevice = () => handlePosPaymentForQuickSale(bankAccountId, bankSubAccountId, bankAccountName, useFiscalDevice);
        let retryRequestedDuringBekoPoll = false;
        setPosPaymentError(errorMessage);
        setIsPosErrorModalOpen(true);
        setPosRetryCallback(() => () => {
          if (bekoLatePollActiveRef.current) {
            retryRequestedDuringBekoPoll = true;
            showSnackbar('Yazarkasanın son fiş durumu kontrol ediliyor — cihaz tekrar çalıştırılmadı. Sonuç birkaç saniye içinde gelecek.', 'warning');
            return;
          }
          rerunPosDevice();
        });

        // ✅ YENİ (Ingenico): "Yine de Devam Et" — ödeme cihazdan alınmış ama sistem hata
        // sanıyorsa kasiyer satışı yazarkasasız kaydedebilmeli (gün sonu tutarlılığı).
        if (selectedFiscalDevice === 'ingenico_5000f') {
          setPosContinueCallback(() => () => {
            console.log('[handlePosPaymentForQuickSale] 👤 Kullanıcı "Yine de Devam Et" dedi, satış yazarkasasız kaydediliyor...');
            handlePosPaymentForQuickSale(bankAccountId, bankSubAccountId, bankAccountName, false, false, posIntentKey);
          });
        } else {
          setPosContinueCallback(null);
        }
        
        setIsProcessingPayment(false);
        setIsProcessingPosPayment(false);

        // Beko timeout sonrası 10 saniye arka plan polling
        if (isBekoDevice && isTimeoutError) {
          console.log('[handlePosPaymentForQuickSale] 🔍 Beko geç yanıt polling başlatılıyor (10sn)...');
          bekoLatePollActiveRef.current = true;
          (async () => {
            let confirmed = false;
            try {
              for (let i = 0; i < 10; i++) {
                await new Promise(r => setTimeout(r, 1000));
                try {
                  const lateResult = await checkBekoLastReceiptStatus();
                  if (lateResult && lateResult.hasResult && lateResult.receiptStatus === 0) {
                    console.log(`[handlePosPaymentForQuickSale] ✅ Beko geç yanıt BAŞARILI! (${i + 1}sn sonra) Ödeme cihazda tamamlanmış.`);
                    confirmed = true;
                    break;
                  }
                } catch (pollErr) {
                  console.warn('[handlePosPaymentForQuickSale] Geç yanıt polling hatası:', pollErr);
                }
              }
            } finally {
              bekoLatePollActiveRef.current = false;
            }

            if (confirmed) {
              // Hata modalını kapat
              setIsPosErrorModalOpen(false);
              setPosPaymentError(null);
              setPosRetryCallback(null);
              setPosContinueCallback(null);
              // Yazarkasa zaten fiş bastı, sadece SQL kaydı için TEK SEFER çağır (cihaz çalıştırılmaz).
              // Aynı ödeme niyeti anahtarıyla kaydedilir (sunucu ikinci satış açmaz).
              handlePosPaymentForQuickSale(bankAccountId, bankSubAccountId, bankAccountName, false, false, posIntentKey);
              return;
            }

            console.log('[handlePosPaymentForQuickSale] 🔍 Beko geç yanıt polling tamamlandı, yanıt gelmedi (10sn)');
            if (retryRequestedDuringBekoPoll) {
              // Kasiyer kontrol sürerken "Yeniden Dene"ye bastı (pencere kapandı): onay gelmedi,
              // kararı yeniden kasiyere bırak — artık cihaz yeniden çalıştırılabilir.
              setPosPaymentError('Yazarkasadan ödeme onayı gelmedi. Cihazda ödeme alınmadığından eminseniz yeniden deneyebilirsiniz.');
              setPosRetryCallback(() => rerunPosDevice);
              setPosContinueCallback(null);
              setIsPosErrorModalOpen(true);
            }
          })();
        }

        return;
      }
    } else {
      console.log("[handlePosPaymentForQuickSale] 🔍 ÖKC Kullanım Ayarı: KAPALI ⚠️");
      console.log("[handlePosPaymentForQuickSale] POS ödeme için ÖKC kullanımı kapalı. SQL kayıtlarına devam ediliyor.");
      fiscalSuccess = true; // ÖKC yoksa direk SQL'e geç
      
      const fiscalEndTime = performance.now();
      console.log(`[handlePosPaymentForQuickSale] ⏱️ ÖKC işlemi süresi: ${(fiscalEndTime - startTime).toFixed(2)}ms`);
    }

    // SQL kayıtları
    if (fiscalSuccess) {
      console.log(`[handlePosPaymentForQuickSale] SQL kayıtları başlıyor (ÖKC Durumu: ${useFiscalDevice ? 'Başarılı' : 'Kullanılmadı'})...`);

      let request: QuickSalePosWithInvoiceRequest | undefined;
      // Kayıt sunucuda başarılı oldu mu? (Sonraki adımlardan biri düşerse kayıt hatası penceresi AÇILMAZ)
      let posSaveSucceeded = false;
      // Kayıt sonrası adımlar — ilk denemede de "Yeniden Dene"de de aynı fonksiyon (en fazla 1 kez)
      let posCompleteAfterSave: ((response: QuickSaleWithInvoiceResponse) => Promise<void>) | null = null;

      try {
        // closure-safe: paymentCart'tan toplam hesapla
        const posEffectiveTotal = calculateTotalFromCart(paymentCart);
        console.log("[ATOMİK] 🔄 POS + Fatura SQL API'ye gönderiliyor...");
        console.log("[ATOMİK] Sepetteki ürün sayısı:", paymentCart.length);
        console.log("[ATOMİK] Toplam tutar:", posEffectiveTotal);
        console.log('🏢 [ŞİRKET] Cache\'den alınan şirket adı:', companyName || 'DEFAULT');

        const sqlStartTime = performance.now();

        // Cart items'ı ATOMİK API formatına çevir
        // Deposito items'ları SQL API'den filtrele (stok hareketi gerekmez)
        const posRegularCartItems = paymentCart.filter(item => !item.id.startsWith('DEPOSIT-'));

        // ✅ Şube deposu aktifse ürünleri o depoya kaydet (qty=0 ile)
        if (branchWarehouseId && companyId) {
          await enrichCartItemsForBranchWarehouse(posRegularCartItems, branchWarehouseId, companyId);
        }

        request = buildPosRequest(fiscalData);

        const dataReadyTime = performance.now();
        console.log(`[ATOMİK] ⏱️ Veri hazırlama süresi: ${(dataReadyTime - sqlStartTime).toFixed(2)}ms`);
        console.log("[ATOMİK] 🔄 POS + Fatura isteği gönderiliyor:", request);

        // ✅ Kayıt sonrası adımlar (mutfak siparişi, fiş, masa kapatma, kulüp kartı, sepet)
        // TEK fonksiyonda: ilk denemede de "Yeniden Dene" başarısında da aynısı çalışır,
        // runOnce sayesinde en fazla BİR kez (mutfak fişi iki kez basılmaz).
        posCompleteAfterSave = runOnce(async (response: QuickSaleWithInvoiceResponse) => {
        if (response.InvoiceNumber) {
          console.log(`[ATOMİK] 📄 Fatura oluşturuldu: ${response.InvoiceNumber}`);
        }

        console.log("[SQL] ✅ Tüm işlemler başarıyla tamamlandı!");
        console.log("[SQL] Referans No:", response.ReferenceNumber);
        console.log("[SQL] Cari Hareket ID:", response.AccountTransactionId);
        console.log("[SQL] Banka Hareket ID:", response.BankTransactionId);
        console.log("[SQL] Stok Hareket ID'leri:", response.StockMovementIds);

        // 🍽️ MUTFAK EKRANI ENTEGRASYONU - Sipariş Gönderme (POS)
        let kitchenShortCodePos: string | null = null; // ✅ Fişte kullanılacak kısa kod (POS)
        try {
          // @ts-ignore - useKitchenScreen ve selectedQRMenuId screenSettings'de var
          const useKitchenScreen = screenSettings?.useKitchenScreen || false;
          // @ts-ignore
          const selectedQRMenuId = screenSettings?.selectedQRMenuId || null;

          // ✅ DÜZELTME: Mutfak ekranı SADECE normal hızlı satış modunda çalışır (POS)
          // Restoran modu: Sipariş "Mutfağa Yazdır" butonu ile gönderilir, ödeme alındığında GÖNDERİLMEZ
          // Çünkü restoran modunda müşteri ödeme yaptığında yemeği bitirmiş demektir (A la carte)
          if (useKitchenScreen && selectedQRMenuId && !isRestaurantMode) {
            console.log('[MUTFAK EKRANI] 🍽️ Mutfak ekranı entegrasyonu AKTIF (Normal Hızlı Satış - POS), sipariş gönderiliyor...');
            console.log('[MUTFAK EKRANI] QR Menü ID:', selectedQRMenuId);

            // Sipariş detaylarını hazırla (deposito items hariç)
            const siparisDetaylari = paymentCart.filter(i => !i.id.startsWith('DEPOSIT-')).map(item => {
              const inventoryItem = allInventoryItems.find(inv => inv.id === item.id);
              return {
                UrunId: item.id,
                UrunAdi: item.name,
                BirimFiyat: item.price,
                Miktar: item.quantity,
                ToplamFiyat: item.price * item.quantity,
                UrunResmi: inventoryItem?.imageUrl || null,
                Aciklama: null,
                CategoryId: inventoryItem?.categoryId || null,
                VatRate: inventoryItem?.vatRate || null
              };
            });

            const kitchenOrderRequest = {
    
              QRMenuId: selectedQRMenuId,
              PersonelFirebaseUid: userDetails?.uid || null,
              PersonelName: userDetails ? `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() || 'Hızlı Satış' : 'Hızlı Satış',
              ToplamTutar: calculateTotal(),
              SiparisDetaylari: siparisDetaylari,
              SiparisNotu: null,
              OdemeYontemi: 'Kredi Kartı',
              IslemId: fiscalData?.FiscalTransactionId || null,
              DekontId: fiscalData?.FiscalTransactionId || null,
              QuickSaleScreenId: screenId ? parseInt(screenId) : null,
              ReferenceNumber: response.ReferenceNumber || null,
              IsRestaurantMode: false,
              ReservedLongCode: reservedLongCode || null,
              ReservedShortCode: reservedShortCode || null,
              BranchId: activeBranchId || null
            };

            console.log('[MUTFAK EKRANI] 📤 Sipariş request (POS):', kitchenOrderRequest);

            // Mutfak ekranına sipariş gönder (arka planda - hata olsa bile satış devam eder)
            const kitchenResponse = await salesService.createKitchenOrderFromQuickSale(kitchenOrderRequest);

            if (kitchenResponse.success) {
              console.log('[MUTFAK EKRANI] ✅ Sipariş mutfak ekranına başarıyla gönderildi! (POS)');
              console.log('[MUTFAK EKRANI] Sipariş ID:', kitchenResponse.data?.siparisId);
              console.log('[MUTFAK EKRANI] Kısa Kod:', kitchenResponse.data?.shortCode);
              
              // ✅ Kısa kodu fişte kullanmak için kaydet
              kitchenShortCodePos = kitchenResponse.data?.shortCode || null;
              
              // Kullanıcıya bilgi ver (opsiyonel - snackbar ile)
              showSnackbar(`Sipariş mutfak ekranına gönderildi (${kitchenResponse.data?.shortCode})`, 'info');
            } else {
              console.error('[MUTFAK EKRANI] ❌ Sipariş gönderilemedi:', kitchenResponse.message);
              // Satış başarılı olduğu için hata gösterme, sadece logla
            }
          } else if (isRestaurantMode && useKitchenScreen && selectedQRMenuId) {
            console.log('[MUTFAK EKRANI] 🍽️ Restoran modu (POS) - Mutfak entegrasyonu AKTIF, ödeme sonrası güncelleme/oluşturma...');

            const siparisDetaylari = paymentCart.filter(i => !i.id.startsWith('DEPOSIT-')).map(item => {
              const inventoryItem = allInventoryItems.find(inv => inv.id === item.id);
              return {
                UrunId: item.id,
                UrunAdi: item.name,
                BirimFiyat: item.price,
                Miktar: item.quantity,
                ToplamFiyat: item.price * item.quantity,
                UrunResmi: inventoryItem?.imageUrl || null,
                Aciklama: null,
                CategoryId: inventoryItem?.categoryId || null,
                VatRate: inventoryItem?.vatRate || null
              };
            });

            const kitchenOrderRequest = {
              QRMenuId: selectedQRMenuId,
              PersonelFirebaseUid: userDetails?.uid || null,
              PersonelName: userDetails ? `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() || 'Restoran Satışı' : 'Restoran Satışı',
              ToplamTutar: calculateTotal(),
              SiparisDetaylari: siparisDetaylari,
              SiparisNotu: null,
              OdemeYontemi: 'Kredi Kartı',
              IslemId: fiscalData?.FiscalTransactionId || null,
              DekontId: fiscalData?.FiscalTransactionId || null,
              QuickSaleScreenId: screenId ? parseInt(screenId) : null,
              ReferenceNumber: response.ReferenceNumber || null,
              IsRestaurantMode: true,
              ReservedLongCode: reservedLongCode || null,
              ReservedShortCode: reservedShortCode || null,
              BranchId: activeBranchId || null,
              IsPaymentUpdate: true,
              TableName: tableName || null,
              CustomerAccountName: selectedCustomer?.companyName || null
            };

            console.log('[MUTFAK EKRANI] 📤 Restoran ödeme güncelleme request (POS):', kitchenOrderRequest);

            const kitchenResponse = await salesService.createKitchenOrderFromQuickSale(kitchenOrderRequest);

            if (kitchenResponse.success) {
              console.log('[MUTFAK EKRANI] ✅ Restoran mutfak kaydı güncellendi/oluşturuldu (POS)');
              console.log('[MUTFAK EKRANI] 📋 KISA KOD:', kitchenResponse.data?.shortCode);
              kitchenShortCodePos = kitchenResponse.data?.shortCode || null;
            } else {
              console.error('[MUTFAK EKRANI] ❌ Restoran mutfak güncelleme başarısız (POS):', kitchenResponse.message);
            }
          } else if (isRestaurantMode) {
            console.log('[MUTFAK EKRANI] ℹ️ Restoran modu (POS) - Mutfak entegrasyonu kapalı, atlanıyor');
          } else {
            console.log('[MUTFAK EKRANI] ℹ️ Mutfak ekranı entegrasyonu kapalı veya QR menü seçilmemiş (POS)');
          }
        } catch (kitchenError) {
          console.error('[MUTFAK EKRANI] ❌ Mutfak ekranı entegrasyonu hatası (POS):', kitchenError);
          // Satış başarılı olduğu için kullanıcıya hata gösterme
        }

        // ✅ YENİ: Fiş yazdır - Yazarkasa KULLANILMADIYSA VEYA "Hem yazarkasa hem fiş yazıcısı" ayarı AKTİFSE yazdır
        console.log(`[FİŞ YAZDIRMA - POS] 🔍 Kontrol - useFiscalDevice: ${useFiscalDevice}, useBothFiscalAndReceiptPrinter: ${useBothFiscalAndReceiptPrinter}`);
        const shouldPrintReceiptPOS = !useFiscalDevice || useBothFiscalAndReceiptPrinter;
        if (shouldPrintReceiptPOS) {
          console.log(`[FİŞ YAZDIRMA - POS] 🖨️ Fiş yazdırılacak... (yazarkasa kullanılmadı: ${!useFiscalDevice}, hem yazarkasa hem fiş: ${useBothFiscalAndReceiptPrinter})`);
          try {
            const printStart = performance.now();

            const invoiceItems: QuickSaleInvoiceItem[] = paymentCart.map((item) => {
              const invItem = item.id.startsWith('DEPOSIT-')
                ? null
                : allInventoryItems.find((inv: QuickSaleInventoryItem) => inv.id === item.id);
              // ✅ FIX: KDV oranını üründen al (depozito → 20). Sabit %20 yerine
              // gerçek orana göre hesapla; KDV 0 ürünlerde %20 yazılmasını önler.
              const vatRate = item.id.startsWith('DEPOSIT-') ? 20 : (invItem?.vatRate || 0);
              const sctRate = invItem?.sctRate || 0;
              const lineTotal = item.quantity * item.price;
              const isVatIncluded = item.vatStatus === 'included';
              let netTotal: number;
              let vatAmount: number;
              let sctAmount: number;
              let grossTotal: number;
              if (isVatIncluded) {
                grossTotal = lineTotal;
                vatAmount = Math.round((grossTotal - (grossTotal / (1 + vatRate / 100))) * 100) / 100;
                sctAmount = Math.round((grossTotal - (grossTotal / (1 + sctRate / 100))) * 100) / 100;
                netTotal = Math.round((grossTotal - vatAmount - sctAmount) * 100) / 100;
              } else {
                netTotal = lineTotal;
                vatAmount = Math.round((netTotal * (vatRate / 100)) * 100) / 100;
                sctAmount = Math.round((netTotal * (sctRate / 100)) * 100) / 100;
                grossTotal = Math.round((netTotal + vatAmount + sctAmount) * 100) / 100;
              }
              return {
                id: item.id,
                itemId: item.id,
                itemName: item.name,
                unit: 'adet',
                quantity: item.quantity,
                unitPrice: item.price,
                lineTotal,
                vatRate,
                sctRate,
                discountRate: 0,
                vatAmount,
                sctAmount,
                netTotal,
                grossTotal,
              };
            });

            await printReceiptAfterPayment(
              response.ReferenceNumber || 'N/A', 
              'KART', 
              invoiceItems, 
              undefined, // remainingBalance (POS ödemede kullanılmıyor)
              kitchenShortCodePos // ✅ Mutfak ekranı kısa kodunu gönder (varsa fişte SİPARİŞ bölümü yazdırılır)
            );
            
            const printEnd = performance.now();
            console.log(`[SQL] ⏱️ Fiş yazdırma süresi: ${(printEnd - printStart).toFixed(2)}ms`);
          } catch (printError) {
            console.error('[SQL] Fiş yazdırma hatası:', printError);
            showSnackbar('Fiş yazdırılırken bir hata oluştu, ancak satış işlemi tamamlandı.', 'warning');
          }
        } else {
          console.log('[FİŞ YAZDIRMA - POS] ✅ Yazarkasa kullanıldı ve birlikte yazıcı ayarı kapalı, fiş YAZDIRILMAYACAK (POS)');
        }

        // ✅ YENİ: Restoran modunda adisyonu kapat (CompletedSales'e taşı)
        if (isRestaurantMode && tableId) {
          const effectiveCloseTotal = calculateEffectiveTotal();
          console.log('[RESTORAN-POS] 🍽️ Restoran modu aktif (POS), adisyon kapatılıyor...', {
            tableId,
            totalAmount: effectiveCloseTotal,
            bankAccountId,
            bankSubAccountId,
            bankAccountName,
            restaurantActiveSaleIdOverride,
          });
          try {
            const { restaurantActiveSaleApi } = await import('../../../../services/restaurantApiService');

            // ✅ SEPET BÖLME FIX: Override varsa onu kullan (sol=main, sağ=splitChild).
            // Yoksa klasik davranış — tableId ile ana satışı bul.
            let targetActiveSaleId: number | null = restaurantActiveSaleIdOverride ?? null;
            const numericTableId = parseInt(tableId);
            if (!targetActiveSaleId) {
              console.log('[RESTORAN-POS] 📡 getByTableId çağrılıyor, tableId:', numericTableId);
              const activeSale = await restaurantActiveSaleApi.getByTableId(numericTableId);
              console.log('[RESTORAN-POS] 📡 getByTableId yanıtı:', activeSale ? { Id: activeSale.Id, BranchId: activeSale.BranchId, TotalAmount: activeSale.TotalAmount, TableId: activeSale.TableId } : 'NULL');
              targetActiveSaleId = activeSale?.Id ?? null;
            } else {
              console.log('[RESTORAN-POS] 🎯 Sepet bölme override — kapatılacak ActiveSale Id:', targetActiveSaleId);
            }

            if (targetActiveSaleId) {
              console.log('[RESTORAN-POS] ✅ Aktif satış bulundu (POS), ID:', targetActiveSaleId);
              
              const posPaymentDetails = JSON.stringify({
                posAmount: effectiveCloseTotal,
                bankAccountId,
                bankSubAccountId,
                bankAccountName,
                transactionId: response.BankTransactionId,
              });
              
              const completePayload = {
                paymentMethod: 'Kredi Kartı',
                paymentDetailsJson: posPaymentDetails,
                totalAmount: effectiveCloseTotal,
                completedBy: userDetails?.uid || null,
                completedByName: effectiveCreatedBy,
                saleNumber: response.ReferenceNumber || undefined,
                invoiceNumber: response.InvoiceNumber || undefined,
                referenceNumber: response.ReferenceNumber || undefined,
                closedByWaiterName: waiterName || undefined,
                isFiscalReceipt: true,
                payments: [{
                  paymentMethod: 'pos',
                  amount: effectiveCloseTotal,
                  bankAccountId: bankAccountId || undefined,
                  bankAccountName: bankAccountName || undefined,
                  bankSubAccountId: bankSubAccountId || undefined,
                  bankTransactionId: response.BankTransactionId || undefined,
                }],
              };
              console.log('[RESTORAN-POS] 📤 completeSale çağrılıyor...', { activeSaleId: targetActiveSaleId, payload: completePayload });
              
              const completeResult = await restaurantActiveSaleApi.completeSale(targetActiveSaleId, completePayload);
              
              console.log('[RESTORAN-POS] 📥 completeSale yanıtı:', JSON.stringify(completeResult));
              
              if (completeResult.success) {
                console.log('[RESTORAN-POS] ✅ Adisyon başarıyla kapatıldı! (POS)', completeResult.data);
                showSnackbar(`Masa kapatıldı - ${completeResult.data?.saleNumber}`, 'success');
              } else {
                console.error('[RESTORAN-POS] ❌ Adisyon kapatılamadı (POS):', completeResult.message || completeResult);
                showSnackbar(`Uyarı: Adisyon kapatılamadı - ${completeResult.message || 'Bilinmeyen hata'}`, 'warning');
              }
            } else {
              console.warn('[RESTORAN-POS] ⚠️ Bu masa için aktif satış bulunamadı (POS), tableId:', numericTableId);
            }
          } catch (restaurantError: any) {
            console.error('[RESTORAN-POS] ❌ Restoran adisyon kapatma hatası (POS):', restaurantError);
            console.error('[RESTORAN-POS] ❌ Hata detayı:', restaurantError?.response?.data || restaurantError?.message);
            showSnackbar('Uyarı: Adisyon kapatılırken hata oluştu, ancak ödeme başarılı.', 'warning');
          }
        }

        // İşlemi tamamla
        const endTime = performance.now();
        console.log(`[SQL] ⏱️ TOPLAM İŞLEM SÜRESİ: ${(endTime - startTime).toFixed(2)}ms`);
        showSnackbar(`POS Satışı (${response.ReferenceNumber}) başarıyla tamamlandı.`);
        
        // Kulüp girişi: ödeme sonrası kart atama
        if (onClubEntryPaymentComplete && response.QuickSaleReceiptId) {
          onClubEntryPaymentComplete({
            receiptId: response.QuickSaleReceiptId,
            referenceNumber: response.ReferenceNumber || '',
            paymentMethod: 'POS',
            pavoPaySaleNumber: fiscalData?.SaleNumber,
            cart: [...paymentCart],
          });
        }
        
        // ✅ Sepet bölme için: onPaymentSuccessOverride varsa onu çağır, yoksa clearCart
        if (onPaymentSuccessOverride) {
          onPaymentSuccessOverride();
        } else if (!skipClearCartAfterPayment) {
          clearCart();
        }
        
        // 🎫 NUMARA: Yenileme artık merkezi olarak endPaymentScope (finally) içinde yapılır.
        
        // ✅ YENİ: Restoran modundaysa RestaurantScreen'e geri dön (sepet bölme değilse)
        if (!skipNavigationAfterPayment && isRestaurantMode && restaurantScreenId && navigateToRestaurantScreen) {
          console.log('[RESTORAN] 🔙 POS ödeme tamamlandı, RestaurantScreen\'e geri dönülüyor...');
          setTimeout(() => {
            navigateToRestaurantScreen();
          }, 500);
        }
        }, 'handlePosPaymentForQuickSale', request.IdempotencyKey);

        // ✅ Ödeme niyeti: cihazsız akışta kayıttan hemen önce açılır; ardından son istek
        // "cihaz sonucu" olarak bildirilir (kayıt isteğinden ÖNCE başlar, beklenmez).
        if (!wasPaymentIntentAttempted(posIntentKey)) {
          await openPaymentIntent({
            key: posIntentKey,
            endpoint: 'process-pos-with-invoice',
            payload: request,
            amount: request.TotalAmount,
            paymentKind: 'pos',
            fiscalDevice: useFiscalDevice ? telemetryFiscalDevice(selectedFiscalDevice) : 'yok',
          });
        }
        void reportDeviceResult(posIntentKey, request, {
          fiscalReference: fiscalData?.SaleNumber || fiscalData?.FiscalTransactionId || null,
          fiscalResult: useFiscalDevice ? 'onaylandi' : intentKeyOverride ? 'kasiyer-devam-etti' : 'cihaz-yok',
        });

        // Kurtarma kuyruğu: istek gitmeden önce yazılır, başarıda silinir
        savePendingAtomicPayment('process-pos-with-invoice', request, companyId);

        // ===== ATOMİK API ÇAĞRISI (CARİ + BANKA + STOK + FATURA) =====
        // ✅ NUMARA GARANTİSİ: Atomik SQL kaydı deneniyor — deneme başladı
        markPaymentAttempt();
        const response = await salesService.processPosWithInvoice(request);

        const apiEndTime = performance.now();
        console.log(`[ATOMİK] ⏱️ API çağrısı süresi: ${(apiEndTime - dataReadyTime).toFixed(2)}ms`);

        console.log("[ATOMİK] ✅ API yanıtı:", response);

        if (!response.Success) {
          throw new AtomicSaveError(
            response.Message || response.ErrorDetails || "POS ödeme işlemi başarısız oldu (tüm işlemler geri alındı).",
            response
          );
        }

        posSaveSucceeded = true;
        removePendingAtomicPayment(request.IdempotencyKey);
        await posCompleteAfterSave(response);

      } catch (error: any) {
        console.error("[SQL] POS atomik işlem hatası:", error);
        if (posSaveSucceeded) {
          // Satış kayıtlı; yalnızca sonraki adımlardan biri düştü → kayıt tekrarlanmaz
          showSnackbar('Satış kaydedildi ancak sonraki adımlardan biri (masa/fiş/mutfak) tamamlanamadı. Lütfen kontrol edin.', 'warning');
        } else {
          handleAtomicSaveFailure(error, {
            showAlertModal,
            showSnackbar,
            // İstek oluşmadan düşen hatada (ör. depo bulunamadı) "Yeniden Dene" yok —
            // eski kod bu durumda `undefined` isteği POST ediyordu.
            request,
            send: (req) => salesService.processPosWithInvoice(req),
            onSaved: async (resp) => { if (posCompleteAfterSave) await posCompleteAfterSave(resp); },
            intro: 'POS ödeme alındı ancak kayıt yapılamadı.',
            logLabel: 'POS atomik işlem',
            telemetry: buildSaveTelemetry({
              endpoint: '/api/QuickSaleWithInvoice/process-pos-with-invoice',
              paymentKind: 'pos',
              fiscalDevice: useFiscalDevice ? telemetryFiscalDevice(selectedFiscalDevice) : 'yok',
              fiscalDone: useFiscalDevice ? !!fiscalData : false,
              fiscalReference: fiscalData?.SaleNumber || fiscalData?.FiscalTransactionId || null,
              amount: request?.TotalAmount ?? null,
            }),
          });
        }
      } finally {
        setIsProcessingPayment(false);
        setIsProcessingPosPayment(false);
      }
    } else {
      // ÖKC işlemi başarısız oldu
      setIsProcessingPayment(false);
      setIsProcessingPosPayment(false); // POS ödeme bitti
    }
  };

  // ========== AÇIK HESAP ÖDEME ==========
  const handleOpenAccountPaymentInner = async () => {
    if (isProcessingPayment) return;
    setIsProcessingPayment(true);

    const paymentCart = splitCartOverride ?? cartRef.current;

    try {
      if (paymentCart.length === 0) {
        showAlertModal("Uyarı", "Ödeme yapmak için sepette ürün olmalıdır.");
        return;
      }
      if (!companyId) {
        showAlertModal("Hata", "Şirket bilgisi bulunamadı. İşlem yapılamıyor.");
        return;
      }
      if (!selectedCustomer) {
        showAlertModal("Hata", "Cari hesap seçilemedi. Lütfen bir cari hesap seçin.");
        return;
      }
      if (selectedCustomer.companyName === 'Genel Cari') {
        showAlertModal("Hata", "Açık hesap ödemesi için Genel Cari dışında bir cari seçmelisiniz.");
        return;
      }

      const calculatedGrandTotal = calculateTotalFromCart(paymentCart);
      const regularCartItems = paymentCart.filter(item => !item.id.startsWith('DEPOSIT-'));

      if (branchWarehouseId && companyId) {
        await enrichCartItemsForBranchWarehouse(regularCartItems, branchWarehouseId, companyId);
      }

      const paymentItems = regularCartItems.map(cartItem => {
        const inventoryItem = allInventoryItems.find(item => item.id === cartItem.id);
        if (!inventoryItem) {
          throw new Error(`Ürün detayı bulunamadı: ${cartItem.id} - ${cartItem.name}`);
        }

        const unit = inventoryItem.unit || 'adet';
        const vatRate = inventoryItem.vatRate || 0;
        const sctRate = inventoryItem.sctRate || 0;
        const unitPrice = cartItem.price;
        const quantity = cartItem.quantity;
        const lineTotal = quantity * unitPrice;

        const isVatIncluded = cartItem.vatStatus === 'included';
        let netTotal: number, vatAmount: number, sctAmount: number, grossTotal: number;

        if (isVatIncluded) {
          grossTotal = lineTotal;
          vatAmount = Math.round((grossTotal - (grossTotal / (1 + vatRate / 100))) * 100) / 100;
          sctAmount = Math.round((grossTotal - (grossTotal / (1 + sctRate / 100))) * 100) / 100;
          netTotal = Math.round((grossTotal - vatAmount - sctAmount) * 100) / 100;
        } else {
          netTotal = lineTotal;
          vatAmount = Math.round((netTotal * (vatRate / 100)) * 100) / 100;
          sctAmount = Math.round((netTotal * (sctRate / 100)) * 100) / 100;
          grossTotal = Math.round((netTotal + vatAmount + sctAmount) * 100) / 100;
        }

        const warehouseId = resolveEffectiveWarehouseId(cartItem.id, inventoryItem.categoryId, screenSettings, branchWarehouseId);
        if (!warehouseId) {
          throw new Error(`Ürün için depo bilgisi bulunamadı: ${cartItem.name}. Lütfen ürün ayarlarını kontrol edin.`);
        }

        const priceType = cartItem.vatStatus === 'included' ? 'VAT_INCLUDED' : 'VAT_EXCLUDED';

        return {
          ProductId: cartItem.id,
          ProductName: cartItem.name,
          StockCode: inventoryItem.stockCode || '',
          WarehouseId: warehouseId,
          WarehouseName: '',
          Quantity: quantity,
          Unit: unit,
          UnitPrice: unitPrice,
          LineTotal: lineTotal,
          VatRate: vatRate,
          SctRate: sctRate,
          VatAmount: vatAmount,
          SctAmount: sctAmount,
          GrossTotal: grossTotal,
          PriceType: priceType,
          HasRecipe: inventoryItem.hasRecipe || false,
          HasMenu: inventoryItem.hasMenu || false,
          SelectedOptions: (cartItem as any).selectedOptions || null,
          TotalPriceDifference: (cartItem as any).totalPriceDifference || 0,
          // ✅ YENİ: İndirim kaydı (faturaya gerçek indirim için)
          ...buildItemDiscountFields(cartItem)
        };
      });

      const saleDescription = isRestaurantMode ? 'Restoran Satışı (Açık Hesap)' : 'Hızlı Satış (Açık Hesap)';

      const openAccountIntentKey = newPaymentIntentKey();
      const openAccountRequest: QuickSaleOpenAccountWithInvoiceRequest = {
        IdempotencyKey: openAccountIntentKey,
        CompanyName: companyName || null,
        ScreenId: screenId ? parseInt(screenId) : null,
        AccountId: parseInt(selectedCustomer.id),
        AccountName: selectedCustomer.companyName,
        TotalAmount: calculatedGrandTotal,
        Currency: 'TRY',
        Description: saleDescription,
        CreatedBy: effectiveCreatedBy,
        BranchId: userDetails?.branchesId ? parseInt(userDetails.branchesId, 10) : null,
        BranchName: userDetails?.branchesId ? getBranchName(userDetails.branchesId) : null,
        IsRestaurantMode: isRestaurantMode,
        CashierId: currentCashierId || null,
        CashierName: currentCashierName || null,
        // ✅ YENİ: Sepete uygulanan indirim kaydı (fiş başlığı)
        ...buildHeaderDiscountFields(cart, appliedDiscountType, lastAppliedDiscountAmount),
        Items: paymentItems.map(item => ({
          ProductId: item.ProductId,
          ProductName: item.ProductName,
          StockCode: item.StockCode,
          WarehouseId: item.WarehouseId,
          WarehouseName: item.WarehouseName,
          Quantity: item.Quantity,
          Unit: item.Unit,
          UnitPrice: item.UnitPrice,
          VatRate: item.VatRate,
          SctRate: item.SctRate,
          PriceType: item.PriceType,
          HasRecipe: item.HasRecipe,
          HasMenu: item.HasMenu,
          SelectedOptions: item.SelectedOptions,
          TotalPriceDifference: item.TotalPriceDifference,
          // ✅ YENİ: İndirim kaydı (fatura kalemi)
          OriginalPrice: item.OriginalPrice,
          DiscountRate: item.DiscountRate,
          DiscountAmount: item.DiscountAmount
        }))
      };

      // ✅ Ödeme niyeti (cihaz yok): kayıttan hemen önce açılır + son istek bildirilir
      await openPaymentIntent({
        key: openAccountIntentKey,
        endpoint: 'process-open-account-with-invoice',
        payload: openAccountRequest,
        amount: calculatedGrandTotal,
        paymentKind: 'acik-hesap',
        fiscalDevice: 'yok',
      });
      void reportDeviceResult(openAccountIntentKey, openAccountRequest, { fiscalResult: 'cihaz-yok' });

      console.log('[AÇIK HESAP] 🔄 İstek gönderiliyor:', openAccountRequest);
      // ✅ NUMARA GARANTİSİ: Atomik SQL kaydı deneniyor — deneme başladı
      markPaymentAttempt();
      const response = await salesService.processOpenAccountWithInvoice(openAccountRequest);
      console.log('[AÇIK HESAP] ✅ API yanıtı:', response);

      if (!response.Success) {
        // Açık hesapta para alınmaz; kayıt kesin başarısızsa niyet terk edilir (sunucu sonradan
        // kasiyerin bilmediği bir satış açmasın). Zaman aşımı / ağ kopmasında sonuç belirsiz → açık kalır.
        const uncertain = response.ClientErrorKind === 'zaman-asimi' || response.ClientErrorKind === 'ag';
        if (!uncertain) {
          abandonPaymentIntent(openAccountIntentKey, `Açık hesap kaydı başarısız: ${response.Message || ''}`);
        }
        throw new Error(response.Message || 'Açık hesap işlemi başarısız oldu');
      }

      // Fiş yazdır (yazarkasa olmadan, sadece fiş yazıcısı)
      try {
        const invoiceItems: QuickSaleInvoiceItem[] = paymentItems.map(item => ({
          id: `qs-${item.ProductId}-${Date.now()}`,
          itemId: item.ProductId,
          itemName: item.ProductName || '',
          quantity: item.Quantity,
          unit: item.Unit || 'adet',
          unitPrice: item.UnitPrice,
          vatRate: item.VatRate || 0,
          sctRate: item.SctRate || 0,
          discountRate: 0,
          lineTotal: item.LineTotal,
          netTotal: item.LineTotal,
          vatAmount: item.VatAmount,
          sctAmount: item.SctAmount,
          grossTotal: item.GrossTotal
        }));

        const receiptNumber = response.ReferenceNumber || 'N/A';
        await printReceiptAfterPayment(receiptNumber, 'AÇIK HESAP', invoiceItems);
      } catch (printError) {
        console.error('[AÇIK HESAP] Fiş yazdırma hatası:', printError);
        showSnackbar('Fiş yazdırılırken bir hata oluştu, ancak satış işlemi tamamlandı.', 'warning');
      }

      // Restoran modunda adisyonu kapat
      if (isRestaurantMode && tableId) {
        try {
          const { restaurantActiveSaleApi } = await import('../../../../services/restaurantApiService');
          const activeSale = await restaurantActiveSaleApi.getByTableId(Number(tableId));

          if (activeSale && activeSale.Id) {
            const openAccountPaymentDetails = JSON.stringify({
              openAccountAmount: calculatedGrandTotal,
              accountId: selectedCustomer.id,
              accountName: selectedCustomer.companyName,
            });

            const completeResult = await restaurantActiveSaleApi.completeSale(activeSale.Id, {
              paymentMethod: 'Açık Hesap',
              paymentDetailsJson: openAccountPaymentDetails,
              totalAmount: calculatedGrandTotal,
              completedBy: userDetails?.uid || null,
              completedByName: effectiveCreatedBy,
              saleNumber: response.ReferenceNumber || undefined,
              invoiceNumber: response.InvoiceNumber || undefined,
              referenceNumber: response.ReferenceNumber || undefined,
              closedByWaiterName: waiterName || undefined,
              isFiscalReceipt: false,
              payments: [{
                paymentMethod: 'open_account',
                amount: calculatedGrandTotal,
                notes: `Cari: ${selectedCustomer.companyName}`,
              }],
            });

            if (completeResult.success) {
              showSnackbar(`Masa kapatıldı - ${completeResult.data?.saleNumber}`, 'success');
            } else {
              showSnackbar(`Uyarı: Adisyon kapatılamadı - ${completeResult.message}`, 'warning');
            }
          }
        } catch (restaurantError: any) {
          console.error('[AÇIK HESAP] Restoran adisyon kapatma hatası:', restaurantError);
          showSnackbar('Uyarı: Adisyon kapatılırken hata oluştu, ancak ödeme başarılı.', 'warning');
        }
      }

      showSnackbar(`Açık hesap satışı (${response.ReferenceNumber}) başarıyla tamamlandı — ${selectedCustomer.companyName} borçlandırıldı.`);

      if (onPaymentSuccessOverride) {
        onPaymentSuccessOverride();
      } else if (!skipClearCartAfterPayment) {
        clearCart();
      }

      // 🎫 NUMARA: Yenileme artık merkezi olarak endPaymentScope (finally) içinde yapılır.

      if (!skipNavigationAfterPayment && isRestaurantMode && restaurantScreenId && navigateToRestaurantScreen) {
        setTimeout(() => navigateToRestaurantScreen(), 500);
      }

    } catch (error: any) {
      console.error('[AÇIK HESAP] ❌ Hata:', error);
      showAlertModal(
        "Açık Hesap Hatası",
        error.message || 'Açık hesap işlemi sırasında bir hata oluştu'
      );
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // PavoPay P2P cihaz seçim dialog state
  const [pavoP2pSelectOpen, setPavoP2pSelectOpen] = useState(false);
  const [pavoP2pPendingAction, setPavoP2pPendingAction] = useState<'direct' | 'partial' | null>(null);
  const [pavoP2pPartialDetails, setPavoP2pPartialDetails] = useState<PaymentDetails | null>(null);
  const [pavoP2pPaymentPending, setPavoP2pPaymentPending] = useState(false);
  const [activePavoPayment, setActivePavoPayment] = useState<any>(null);

  // Restoran modunda masaya girildiğinde aktif Pavo ödemesi var mı kontrol et
  useEffect(() => {
    if (!isRestaurantMode || !tableName || !companyId) return;
    let cancelled = false;
    
    const checkActivePavoPayment = async () => {
      try {
        const payments = await pavoApiService.getActivePayments(companyId, activeBranchId || undefined);
        if (cancelled) return;
        const match = payments.find(p => {
          if (!p.sourceReference) return false;
          const ref = p.sourceReference.includes('|') ? p.sourceReference.split('|')[0].trim() : p.sourceReference.trim();
          return ref === tableName;
        });
        if (match) {
          setPavoP2pPaymentPending(true);
          setActivePavoPayment(match);
        }
      } catch (err) {
        console.warn('[PavoP2P] Aktif ödeme kontrol hatası:', err);
      }
    };
    
    checkActivePavoPayment();
    return () => { cancelled = true; };
  }, [isRestaurantMode, tableName, companyId, activeBranchId]);

  const cancelActivePavoPayment = async () => {
    if (!activePavoPayment?.requestId) return;
    try {
      await pavoApiService.cancelPayment(activePavoPayment.requestId);
      // SignalR ile cihaza da iptal bildirimi gönder
      try {
        await pavoSignalRService.cancelPaymentRequest(activePavoPayment.deviceCode, activePavoPayment.requestId);
      } catch (_) {}
      setPavoP2pPaymentPending(false);
      setActivePavoPayment(null);
      showSnackbar('Pavo ödemesi iptal edildi', 'info');
    } catch (err: any) {
      showAlertModal('İptal Hatası', err.message || 'Ödeme iptal edilemedi');
    }
  };

  const openPavoP2pSelect = () => {
    if (pavoP2pPaymentPending && activePavoPayment) {
      setPavoP2pSelectOpen(true);
      return;
    }
    setPavoP2pPendingAction('direct');
    setPavoP2pSelectOpen(true);
  };

  const handlePavoP2pDeviceSelected = async (deviceCode: string) => {
    setPavoP2pSelectOpen(false);
    setPavoP2pPendingAction(null);
    setPavoP2pPartialDetails(null);
    await handlePavoP2pPayment(deviceCode);
  };

  // PavoPay P2P ile ödeme gönderme
  const handlePavoP2pPaymentInner = async (pavoDeviceCode: string) => {
    console.log('[PavoP2P] Ödeme başlıyor, cihaz:', pavoDeviceCode);
    
    if (!pavoDeviceCode) {
      showAlertModal('PavoPay P2P Hatası', 'Lütfen bir PavoPay cihazı seçin.');
      return;
    }
    
    if (isProcessingPayment) return;
    setIsProcessingPayment(true);
    
    const paymentCart = splitCartOverride ?? cartRef.current;
    if (paymentCart.length === 0) {
      showAlertModal('Uyarı', 'Ödeme yapmak için sepette ürün olmalıdır.');
      setIsProcessingPayment(false);
      return;
    }
    
    const totalAmount = calculateTotal();
    if (totalAmount <= 0) {
      showAlertModal('Uyarı', 'Ödeme tutarı sıfırdan büyük olmalıdır.');
      setIsProcessingPayment(false);
      return;
    }
    
    // Restoran modunda bekleme modalı açılmayacak, sadece snackbar gösterilecek
    if (!isRestaurantMode) {
      if (setPaymentWaitingModalOpen) setPaymentWaitingModalOpen(true);
      if (setPaymentWaitingStatus) setPaymentWaitingStatus('waiting');
      if (setPaymentWaitingMessage) setPaymentWaitingMessage('PavoPay cihazında ödeme bekleniyor...');
    }
    
    try {
      // SignalR bağlantısını kontrol et
      if (!pavoSignalRService.isConnected && companyId) {
        await pavoSignalRService.connect(companyId, activeBranchId || undefined);
      }
      
      // Ürün listesini hazırla. Pavo KDV dahil fiyat bekler: KDV hariç fiyatlı
      // ürün (ör. Soda 5,00 + %10) ham fiyatla giderse kalemler toplamı ödeme
      // tutarından düşük kalır ve cihaz "Sipariş kalemleri toplam ücreti ...
      // eşit olmalıdır" hatası verir.
      const items = paymentCart.map((item: CartItem) => {
        const price = Number(cartItemGrossUnitPrice(item).toFixed(2));
        return {
          name: item.name,
          price,
          quantity: item.quantity,
          amount: Number((price * item.quantity).toFixed(2)),
        };
      });
      // Kuruş yuvarlaması toplamı kaydırırsa farkı adedi 1 olan bir kaleme yansıt.
      const itemsSum = Number(items.reduce((s, x) => s + x.amount, 0).toFixed(2));
      const sumDiff = Number((totalAmount - itemsSum).toFixed(2));
      if (sumDiff !== 0 && Math.abs(sumDiff) <= 0.05) {
        const line = items.find((x) => x.quantity === 1 && x.price + sumDiff > 0);
        if (line) {
          line.price = Number((line.price + sumDiff).toFixed(2));
          line.amount = line.price;
        }
      } else if (sumDiff !== 0) {
        console.warn(`[PavoP2P] Kalem toplamı (${itemsSum}) ≠ ödeme tutarı (${totalAmount}), fark ${sumDiff}`);
      }
      
      // API üzerinden ödeme talebi oluştur
      let pavoSourceRef: string | undefined;
      if (isRestaurantMode && tableName) {
        const shortCode = reservedShortCode || reservedLongCode || '';
        const senderLabel = loginType === 'cashier' ? 'Kasiyer' : 'Garson';
        const senderInfo = waiterName ? `${senderLabel}:${waiterName}` : '';
        const parts = [tableName, shortCode, senderInfo].filter(Boolean);
        pavoSourceRef = parts.join('|');
      } else {
        pavoSourceRef = reservedShortCode || reservedLongCode || undefined;
      }
      const numericTableId = isRestaurantMode && tableId ? parseInt(tableId) : undefined;
      // ✅ NUMARA GARANTİSİ: Pavo cihazına ödeme talebi gidiyor — deneme başladı
      markPaymentAttempt();
      const paymentRequest = await pavoApiService.createPaymentRequest(
        companyId || '',
        activeBranchId || '',
        pavoDeviceCode,
        totalAmount,
        items,
        isRestaurantMode ? 'restaurant' : 'quicksale',
        pavoSourceRef,
        numericTableId,
      );
      
      const requestId = paymentRequest.requestId;

      /**
       * Pavo sipariş modu (restoran) atomik isteği — anahtar `pavo_{RequestId}`. Ödeme yöntemi
       * (nakit/kart/karma) cihazda seçildiği için cihaz öncesi ödeme niyetinde kart (POS) isteği
       * kullanılır; kesin istek ve uç nokta cihaz sonucunda bildirilir.
       */
      const buildPavoRestaurantRequest = (
        pavoPaymentMethod: string,
        pavoSaleNumber: string,
        totalAmount: number,
        resultData: any
      ) => {
        const regularCartItems = paymentCart.filter((item: CartItem) => !item.id?.startsWith('DEPOSIT-'));

        const cashInvoiceItems: QuickSaleCashWithInvoiceItem[] = regularCartItems.map((item: CartItem) => {
          const product = allInventoryItems.find((inv) => inv.id === item.id);
          const warehouseId = resolveEffectiveWarehouseId(item.id, product?.categoryId, screenSettings, branchWarehouseId);
          const priceType = item.vatStatus === 'included' ? 'VAT_INCLUDED' : 'VAT_EXCLUDED';
          return {
            ProductId: item.id,
            ProductName: product?.name || item.name,
            StockCode: product?.stockCode || '',
            WarehouseId: warehouseId || '',
            WarehouseName: '',
            Quantity: item.quantity,
            UnitPrice: item.price,
            VatRate: item.vatRate ?? product?.vatRate ?? 0,
            SctRate: product?.sctRate ?? 0,
            PriceType: priceType,
            HasRecipe: item.hasRecipe ?? false,
            HasMenu: item.hasMenu ?? false,
            // ✅ YENİ: İndirim kaydı (faturaya gerçek indirim için)
            ...buildItemDiscountFields(item),
          };
        });

        const posInvoiceItems: QuickSalePosWithInvoiceItem[] = cashInvoiceItems.map(ci => ({
          ProductId: ci.ProductId,
          ProductName: ci.ProductName,
          WarehouseId: ci.WarehouseId,
          Quantity: ci.Quantity,
          UnitPrice: ci.UnitPrice,
          VatRate: ci.VatRate,
          SctRate: ci.SctRate,
          PriceType: ci.PriceType,
          HasRecipe: ci.HasRecipe,
          HasMenu: ci.HasMenu,
          // ✅ YENİ: İndirim kaydı (fatura kalemi)
          OriginalPrice: ci.OriginalPrice,
          DiscountRate: ci.DiscountRate,
          DiscountAmount: ci.DiscountAmount,
        }));

        // ✅ Deterministik işlem anahtarı: sunucudaki Pavo işi (paket ödemeleri) de
        // `pavo_{RequestId}` kullanır → aynı Pavo ödemesi iki yoldan/iki kez kaydedilmeye
        // çalışılsa bile sunucu tek satış tutar. Eskiden anahtar YOKTU.
        const pavoIdempotencyKey = `pavo_${requestId}`;
        const isPavoCash = pavoPaymentMethod === 'Nakit' || pavoPaymentMethod === 'cash';
        const isPavoCard = pavoPaymentMethod === 'Kart' || pavoPaymentMethod === 'card';
        let pavoEndpoint: string;
        let pavoRequest: any;
        let pavoSend: (req: any) => Promise<QuickSaleWithInvoiceResponse>;
        let pavoLogTag: string;

        if (isPavoCash) {
          const cashRequest: QuickSaleCashWithInvoiceRequest = {
            IdempotencyKey: pavoIdempotencyKey,
            CompanyName: companyName || null,
            ScreenId: screenId ? parseInt(screenId) : null,
            AccountId: parseInt(selectedCustomer?.id?.toString() || '0', 10),
            AccountName: selectedCustomer?.companyName || null,
            CashboxId: cashPaymentCashboxId ? parseInt(cashPaymentCashboxId) : 0,
            CashboxName: cashPaymentCashboxName || null,
            TotalAmount: totalAmount,
            ...buildHeaderDiscountFields(paymentCart, appliedDiscountType, lastAppliedDiscountAmount),
            Items: cashInvoiceItems,
            CreatedBy: userDetails?.uid || null,
            Description: `PavoPay P2P Nakit - ${pavoSaleNumber}`,
            BranchId: userDetails?.branchesId ? parseInt(userDetails.branchesId, 10) : null,
            BranchName: userDetails?.branchesId ? getBranchName(userDetails.branchesId) : null,
            IsRestaurantMode: true,
            IsFiscalReceipt: false,
            PavoPaySaleNumber: pavoSaleNumber || null,
          };
          pavoEndpoint = 'process-cash-with-invoice';
          pavoRequest = cashRequest;
          pavoSend = (req) => salesService.processCashWithInvoice(req);
          pavoLogTag = 'PavoP2P-NAKİT';
        } else if (isPavoCard) {
          const posRequest: QuickSalePosWithInvoiceRequest = {
            IdempotencyKey: pavoIdempotencyKey,
            CompanyName: companyName || null,
            ScreenId: screenId ? parseInt(screenId) : null,
            AccountId: parseInt(selectedCustomer?.id?.toString() || '0', 10),
            AccountName: selectedCustomer?.companyName || null,
            BankAccountId: 0,
            BankSubAccountId: null,
            BankAccountName: 'PavoPay POS',
            TotalAmount: totalAmount,
            ...buildHeaderDiscountFields(paymentCart, appliedDiscountType, lastAppliedDiscountAmount),
            Items: posInvoiceItems,
            CreatedBy: userDetails?.uid || null,
            Description: `PavoPay P2P Kart - ${pavoSaleNumber}`,
            BranchId: userDetails?.branchesId ? parseInt(userDetails.branchesId, 10) : null,
            BranchName: userDetails?.branchesId ? getBranchName(userDetails.branchesId) : null,
            IsRestaurantMode: true,
            IsFiscalReceipt: false,
            PavoPaySaleNumber: pavoSaleNumber || null,
          };
          pavoEndpoint = 'process-pos-with-invoice';
          pavoRequest = posRequest;
          pavoSend = (req) => salesService.processPosWithInvoice(req);
          pavoLogTag = 'PavoP2P-POS';
        } else {
          const payments = resultData?.Payments || resultData?.payments || [];
          const partialRequest: QuickSalePartialWithInvoiceRequest = {
            IdempotencyKey: pavoIdempotencyKey,
            CompanyName: companyName || null,
            ScreenId: screenId ? parseInt(screenId) : null,
            AccountId: parseInt(selectedCustomer?.id?.toString() || '0', 10),
            AccountName: selectedCustomer?.companyName || null,
            CashboxId: cashPaymentCashboxId ? parseInt(cashPaymentCashboxId) : 0,
            CashboxName: cashPaymentCashboxName || null,
            BankAccountId: 0,
            BankSubAccountId: null,
            BankAccountName: 'PavoPay POS',
            CashAmount: payments.filter((p: any) => (p.Method || p.method) === 'Nakit' || (p.Method || p.method) === 'cash').reduce((sum: number, p: any) => sum + ((p.Amount || p.amount) || 0), 0),
            PosAmount: payments.filter((p: any) => (p.Method || p.method) === 'Kart' || (p.Method || p.method) === 'card').reduce((sum: number, p: any) => sum + ((p.Amount || p.amount) || 0), 0),
            ...buildHeaderDiscountFields(paymentCart, appliedDiscountType, lastAppliedDiscountAmount),
            Items: posInvoiceItems,
            CreatedBy: userDetails?.uid || null,
            Description: `PavoPay P2P Karma - ${pavoSaleNumber}`,
            BranchId: userDetails?.branchesId ? parseInt(userDetails.branchesId, 10) : null,
            BranchName: userDetails?.branchesId ? getBranchName(userDetails.branchesId) : null,
            IsRestaurantMode: true,
            IsFiscalReceipt: false,
            PavoPaySaleNumber: pavoSaleNumber || null,
          };
          pavoEndpoint = 'process-partial-with-invoice';
          pavoRequest = partialRequest;
          pavoSend = (req) => salesService.processPartialWithInvoice(req);
          pavoLogTag = 'PavoP2P-KARMA';
        }
        return { pavoIdempotencyKey, isPavoCash, isPavoCard, pavoEndpoint, pavoRequest, pavoSend, pavoLogTag };
      };

      // ✅ Ödeme niyeti — yalnız restoran modu (hızlı satış P2P akışı atomik kayıt yapmaz;
      // niyet açmak sunucuya bugün yapılmayan bir satışı kaydettirebilirdi). Cihaza istek
      // gönderilmeden ÖNCE yazılır; sunucu sonucu cihazdan PavoRequestId ile de öğrenir.
      if (isRestaurantMode) {
        let p2pIntentPayload: any = null;
        try {
          p2pIntentPayload = buildPavoRestaurantRequest('Kart', 'P2P', totalAmount, null).pavoRequest;
        } catch (e) {
          console.warn('[PaymentIntent] Pavo sipariş modu niyet isteği hazırlanamadı (ödeme devam ediyor):', e);
        }
        if (p2pIntentPayload) {
          await openPaymentIntent({
            key: `pavo_${requestId}`,
            endpoint: 'process-pos-with-invoice',
            payload: p2pIntentPayload,
            amount: totalAmount,
            paymentKind: 'pos',
            fiscalDevice: 'pavo_p2p',
            deviceCode: pavoDeviceCode,
            pavoRequestId: requestId,
          });
        }
      }
      console.log('[PavoP2P] Ödeme talebi oluşturuldu:', requestId);
      
      // SignalR üzerinden cihaza gönder
      try {
        await pavoSignalRService.sendPaymentRequest(pavoDeviceCode, {
          RequestId: requestId,
          TotalAmount: totalAmount,
          Items: items,
          SourceType: isRestaurantMode ? 'restaurant' : 'quicksale',
          SourceReference: pavoSourceRef || '',
        });
      } catch (signalRError) {
        console.warn('[PavoP2P] SignalR gönderim hatası (API zaten talebi oluşturdu):', signalRError);
      }
      
      // Restoran modunda: snackbar göster, masayı kilitle, restoran ekranına dön
      if (isRestaurantMode) {
        setPavoP2pPaymentPending(true);
        setActivePavoPayment(paymentRequest);
        showSnackbar('Ödeme Pavo\'ya gönderildi', 'success');
        setIsProcessingPayment(false);
        
        // Arka planda SignalR ile sonucu dinle
        const callbackId = `pavo_p2p_${requestId}`;
        
        pavoSignalRService.onPaymentCompleted(callbackId, async (completedRequestId, resultData) => {
          if (completedRequestId === requestId) {
            pavoSignalRService.removeCallbacks(callbackId);
            setPavoP2pPaymentPending(false);
            console.log('[PavoP2P] Restoran ödeme başarılı:', resultData);

            const pavoPaymentMethod = resultData?.PaymentMethod || resultData?.paymentMethod || 'Nakit';
            const pavoSaleNumber = resultData?.PavoSaleNumber || resultData?.pavoSaleNumber || 'P2P';
            const totalAmount = calculateTotal();

            const regularCartItems = paymentCart.filter((item: CartItem) => !item.id?.startsWith('DEPOSIT-'));

            if (branchWarehouseId && companyId) {
              await enrichCartItemsForBranchWarehouse(regularCartItems, branchWarehouseId, companyId);
            }

            const invoiceItems: QuickSaleInvoiceItem[] = paymentCart.map((item: CartItem) => ({
              id: item.id,
              itemId: item.id,
              itemName: item.name,
              quantity: item.quantity,
              unit: 'Adet',
              unitPrice: item.price,
              vatRate: 0,
              sctRate: 0,
              discountRate: 0,
              lineTotal: item.price * item.quantity,
              netTotal: item.price * item.quantity,
              vatAmount: 0,
              sctAmount: 0,
              grossTotal: item.price * item.quantity,
            }));

            const {
              pavoIdempotencyKey,
              isPavoCash,
              isPavoCard,
              pavoEndpoint,
              pavoRequest,
              pavoSend,
              pavoLogTag,
            } = buildPavoRestaurantRequest(pavoPaymentMethod, pavoSaleNumber, totalAmount, resultData);

            // ✅ Kayıt sonrası adımlar (masa kapatma, fiş, sepet): ilk denemede de
            // "Yeniden Dene" başarısında da aynısı, en fazla BİR kez.
            const finishPavoRestaurantSale = runOnce(async (atomicResponse: QuickSaleWithInvoiceResponse) => {
            // Restoran masasını kapat (server-side da kapatır, burada idempotent)
            if (isRestaurantMode && tableId) {
              try {
                const { restaurantActiveSaleApi } = await import('../../../../services/restaurantApiService');
                const numericTableId = parseInt(tableId);
                let activeSale: any = null;
                try {
                  activeSale = await restaurantActiveSaleApi.getByTableId(numericTableId);
                } catch (e: any) {
                  console.log('[PavoP2P-RESTORAN] Masa zaten server tarafından kapatılmış (getByTableId 404)');
                }

                if (activeSale && activeSale.Id) {
                  const paymentMethodLabel = pavoPaymentMethod === 'Kart' || pavoPaymentMethod === 'card' ? 'POS' : (pavoPaymentMethod === 'Karma' ? 'Karma' : 'Nakit');
                  const paymentsArray: any[] = [];

                  if (pavoPaymentMethod === 'Karma' || pavoPaymentMethod === 'mixed') {
                    const payments = resultData?.Payments || resultData?.payments || [];
                    for (const p of payments) {
                      const method = (p.Method || p.method || '').toLowerCase();
                      if (method === 'nakit' || method === 'cash') {
                        paymentsArray.push({
                          paymentMethod: 'cash',
                          amount: p.Amount || p.amount,
                          cashboxId: cashPaymentCashboxId ? parseInt(cashPaymentCashboxId) : undefined,
                          cashboxName: cashPaymentCashboxName || undefined,
                        });
                      } else {
                        paymentsArray.push({
                          paymentMethod: 'pos',
                          amount: p.Amount || p.amount,
                          bankAccountName: 'PavoPay POS',
                        });
                      }
                    }
                  } else if (pavoPaymentMethod === 'Kart' || pavoPaymentMethod === 'card') {
                    paymentsArray.push({
                      paymentMethod: 'pos',
                      amount: totalAmount,
                      bankAccountName: 'PavoPay POS',
                    });
                  } else {
                    paymentsArray.push({
                      paymentMethod: 'cash',
                      amount: totalAmount,
                      cashboxId: cashPaymentCashboxId ? parseInt(cashPaymentCashboxId) : undefined,
                      cashboxName: cashPaymentCashboxName || undefined,
                    });
                  }

                  const completePayload = {
                    paymentMethod: paymentMethodLabel,
                    totalAmount,
                    completedBy: userDetails?.uid || null,
                    completedByName: effectiveCreatedBy,
                    saleNumber: atomicResponse?.ReferenceNumber || pavoSaleNumber,
                    invoiceNumber: atomicResponse?.InvoiceNumber || undefined,
                    referenceNumber: atomicResponse?.ReferenceNumber || pavoSaleNumber,
                    closedByWaiterName: waiterName || undefined,
                    isFiscalReceipt: false,
                    payments: paymentsArray,
                  };

                  console.log('[PavoP2P-RESTORAN] Masa kapatılıyor...', { activeSaleId: activeSale.Id });
                  const completeResult = await restaurantActiveSaleApi.completeSale(activeSale.Id, completePayload);

                  if (completeResult.success) {
                    console.log('[PavoP2P-RESTORAN] Masa başarıyla kapatıldı!');
                  } else if (completeResult.message?.includes('bulunamadı') || completeResult.message?.includes('not found')) {
                    console.log('[PavoP2P-RESTORAN] Masa zaten server tarafından kapatılmış');
                  } else {
                    console.error('[PavoP2P-RESTORAN] Masa kapatılamadı:', completeResult.message);
                    showSnackbar(`Uyarı: Masa kapatılamadı - ${completeResult.message}`, 'warning');
                  }
                } else {
                  console.log('[PavoP2P-RESTORAN] Aktif satış bulunamadı - masa zaten kapatılmış olabilir');
                }
              } catch (restaurantError: any) {
                if (restaurantError?.message?.includes('404') || restaurantError?.message?.includes('bulunamadı')) {
                  console.log('[PavoP2P-RESTORAN] Masa zaten server tarafından kapatılmış');
                } else {
                  console.error('[PavoP2P-RESTORAN] Masa kapatma hatası:', restaurantError);
                  showSnackbar('Ödeme alındı ancak masa kapatılırken hata oluştu.', 'warning');
                }
              }
            }

            // Fiş yazdır
            try {
              await printReceiptAfterPayment(
                atomicResponse?.ReferenceNumber || pavoSaleNumber,
                pavoPaymentMethod === 'Kart' || pavoPaymentMethod === 'card' ? 'POS' : 'Nakit',
                invoiceItems,
              );
            } catch (printError) {
              console.warn('[PavoP2P] Fiş yazdırma hatası:', printError);
            }

            showSnackbar('PavoPay ödeme başarılı, masa kapatıldı!', 'success');
            if (onPaymentSuccessOverride) {
              onPaymentSuccessOverride();
            } else if (!skipClearCartAfterPayment) {
              clearCart();
            }
            }, 'PavoP2P-RESTORAN', pavoIdempotencyKey);

            // ✅ Ödeme niyeti: cihaz onayladı → kesin istek (cihazda seçilen yönteme göre uç nokta
            // değişebilir) kayıttan ÖNCE bildirilir.
            void reportDeviceResult(pavoIdempotencyKey, pavoRequest, {
              fiscalReference: pavoSaleNumber || null,
              fiscalResult: 'onaylandi',
              endpoint: pavoEndpoint,
              paymentKind: isPavoCash ? 'nakit' : isPavoCard ? 'pos' : 'karma',
            });

            // Kurtarma kuyruğu: istek gitmeden önce yazılır, başarıda silinir
            savePendingAtomicPayment(pavoEndpoint, pavoRequest, companyId);

            let pavoSaved = false;
            try {
              console.log(`[${pavoLogTag}] Atomik endpoint çağrılıyor...`);
              const atomicResponse = await pavoSend(pavoRequest);
              console.log(`[${pavoLogTag}] Atomik yanıt:`, atomicResponse?.Success === true);
              if (!atomicResponse?.Success) {
                throw new AtomicSaveError(
                  atomicResponse?.Message || atomicResponse?.ErrorDetails || 'PavoPay ödeme kaydı başarısız',
                  atomicResponse
                );
              }
              pavoSaved = true;
              removePendingAtomicPayment(pavoIdempotencyKey);
              await finishPavoRestaurantSale(atomicResponse);
            } catch (atomicError: any) {
              console.error('[PavoP2P] Atomik endpoint hatası:', atomicError);
              if (pavoSaved) {
                showSnackbar('Satış kaydedildi ancak sonraki adımlardan biri (masa/fiş) tamamlanamadı. Lütfen kontrol edin.', 'warning');
              } else {
                // ⚠️ Eskiden bu durumda sessiz kalınıp "PavoPay ödeme alındı!" (başarı) gösteriliyordu.
                // Para Pavo'da alındı ama satış kaydı YOK: kasiyere gerçek hata + "Yeniden Dene".
                showSnackbar('Ödeme PavoPay\'de alındı ancak sistem kaydı yapılamadı! Lütfen "Yeniden Dene" ile kaydı tamamlayın.', 'error');
                handleAtomicSaveFailure(atomicError, {
                  showAlertModal,
                  showSnackbar,
                  request: pavoRequest,
                  send: pavoSend,
                  onSaved: finishPavoRestaurantSale,
                  intro: `PavoPay ödemesi alındı${tableName ? ` (${tableName})` : ''} ancak kayıt yapılamadı.`,
                  logLabel: 'PavoP2P atomik işlem',
                  telemetry: buildSaveTelemetry({
                    endpoint: `/api/QuickSaleWithInvoice/${pavoEndpoint}`,
                    screenMode: 'restoran',
                    tableName: tableName || null,
                    paymentKind: isPavoCash ? 'nakit' : isPavoCard ? 'pos' : 'karma',
                    fiscalDevice: 'pavo_p2p',
                    fiscalDone: true,
                    fiscalReference: pavoSaleNumber || null,
                    pavoRequestId: requestId,
                    amount: totalAmount,
                  }),
                });
              }
            }
          }
        });

        pavoSignalRService.onPaymentFailed(callbackId, (failedRequestId, _errorCode, message) => {
          if (failedRequestId === requestId) {
            pavoSignalRService.removeCallbacks(callbackId);
            setPavoP2pPaymentPending(false);
            // Cihaz ödemeyi reddetti → para alınmadı, niyet terk edilir
            abandonPaymentIntent(`pavo_${requestId}`, `Pavo ödeme başarısız: ${message || ''}`);
            showSnackbar(message || 'PavoPay ödeme başarısız', 'error');
          }
        });

        pavoSignalRService.onPaymentCancelled(callbackId, (cancelledRequestId) => {
          if (cancelledRequestId === requestId) {
            pavoSignalRService.removeCallbacks(callbackId);
            setPavoP2pPaymentPending(false);
            abandonPaymentIntent(`pavo_${requestId}`, 'Ödeme cihazda iptal edildi');
            showSnackbar('Ödeme cihazda iptal edildi', 'warning');
          }
        });
        
        if (navigateToRestaurantScreen) {
          setTimeout(() => navigateToRestaurantScreen(), 300);
        }
        return;
      }
      
      // Hızlı satış modunda: mevcut bekleme akışı (120sn timeout)
      const result = await new Promise<{ success: boolean; result?: any; error?: string }>((resolve) => {
        const callbackId = `pavo_p2p_${requestId}`;
        let resolved = false;
        
        const cleanup = () => {
          pavoSignalRService.removeCallbacks(callbackId);
          clearTimeout(timeoutId);
        };
        
        const timeoutId = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            cleanup();
            resolve({ success: false, error: 'PavoPay cihazından yanıt alınamadı (zaman aşımı)' });
          }
        }, 120000);
        
        pavoSignalRService.onPaymentCompleted(callbackId, (completedRequestId, resultData) => {
          if (completedRequestId === requestId && !resolved) {
            resolved = true;
            cleanup();
            resolve({ success: true, result: resultData });
          }
        });
        
        pavoSignalRService.onPaymentFailed(callbackId, (failedRequestId, _errorCode, message) => {
          if (failedRequestId === requestId && !resolved) {
            resolved = true;
            cleanup();
            resolve({ success: false, error: message || 'PavoPay ödeme başarısız' });
          }
        });
        
        pavoSignalRService.onPaymentCancelled(callbackId, (cancelledRequestId) => {
          if (cancelledRequestId === requestId && !resolved) {
            resolved = true;
            cleanup();
            resolve({ success: false, error: 'Ödeme cihazda iptal edildi' });
          }
        });
      });
      
      if (result.success) {
        console.log('[PavoP2P] Ödeme başarılı:', result.result);
        if (setPaymentWaitingStatus) setPaymentWaitingStatus('success');
        if (setPaymentWaitingMessage) setPaymentWaitingMessage('Ödeme başarılı!');
        
        try {
          const invoiceItems: QuickSaleInvoiceItem[] = paymentCart.map((item: CartItem) => ({
            id: item.id,
            itemId: item.id,
            itemName: item.name,
            quantity: item.quantity,
            unit: 'Adet',
            unitPrice: item.price,
            vatRate: 0,
            sctRate: 0,
            discountRate: 0,
            lineTotal: item.price * item.quantity,
            netTotal: item.price * item.quantity,
            vatAmount: 0,
            sctAmount: 0,
            grossTotal: item.price * item.quantity,
          }));
          await printReceiptAfterPayment(
            result.result?.PavoSaleNumber || 'P2P',
            'POS',
            invoiceItems,
          );
        } catch (printError) {
          console.warn('[PavoP2P] Fiş yazdırma hatası:', printError);
        }
        
        showSnackbar('PavoPay P2P ödeme başarılı!', 'success');
        
        if (onPaymentSuccessOverride) {
          onPaymentSuccessOverride();
        } else if (!skipClearCartAfterPayment) {
          clearCart();
        }
        
        // 🎫 NUMARA: Yenileme artık merkezi olarak endPaymentScope (finally) içinde yapılır.
      } else {
        console.error('[PavoP2P] Ödeme başarısız:', result.error);
        if (setPaymentWaitingStatus) setPaymentWaitingStatus('error');
        if (setPaymentWaitingMessage) setPaymentWaitingMessage(result.error || 'Bilinmeyen hata');
        showAlertModal('PavoPay P2P Hatası', result.error || 'Ödeme işlemi başarısız oldu');
      }
      
    } catch (error: any) {
      console.error('[PavoP2P] Hata:', error);
      if (isRestaurantMode) {
        setPavoP2pPaymentPending(false);
        showSnackbar(error.message || 'Ödeme talebi oluşturulamadı', 'error');
      } else {
        if (setPaymentWaitingStatus) setPaymentWaitingStatus('error');
        if (setPaymentWaitingMessage) setPaymentWaitingMessage(error.message || 'Bağlantı hatası');
        showAlertModal('PavoPay P2P Hatası', error.message || 'Ödeme talebi oluşturulamadı');
      }
    } finally {
      if (!isRestaurantMode) {
        setIsProcessingPayment(false);
        setTimeout(() => {
          if (setPaymentWaitingModalOpen) setPaymentWaitingModalOpen(false);
        }, 2000);
      }
    }
  };

  // ───────────────────────────────────────────────
  // ✅ SİPARİŞ NUMARASI GARANTİSİ: Tüm ödeme giriş noktaları deneme scope'u
  // ile sarmalanır. Deneme başladıysa (markPaymentAttempt), handler nasıl
  // biterse bitsin (başarı / hata / cihazda iptal) en dıştaki finally'de
  // sipariş numarası TAM BİR KEZ yenilenir. İç içe çağrılar (örn.
  // processPaymentAfterPartialModal → handlePosPaymentForQuickSale) depth
  // sayacı sayesinde fazladan yenileme yapmaz.
  // ───────────────────────────────────────────────
  const processPaymentAfterPartialModal = withPaymentScope(processPaymentAfterPartialModalInner);
  const handleCashPaymentForQuickSale = withPaymentScope(handleCashPaymentForQuickSaleInner);
  const handlePosPaymentForQuickSale = withPaymentScope(handlePosPaymentForQuickSaleInner);
  const handleOpenAccountPayment = withPaymentScope(handleOpenAccountPaymentInner);
  const handlePavoP2pPayment = withPaymentScope(handlePavoP2pPaymentInner);

  // Parçalı ödeme modalından gelen Rest-P2P ödemesi: modal kapandıktan sonra sepet
  // SQL kaydı bitene kadar ekranda durur; kilit o süre boyunca tutulur.
  const processPaymentAfterPartialModalEntry = async (paymentDetails: PaymentDetails): Promise<void> => {
    if (!isRestP2PEnabled || paymentDetails.fiscalProcessed) {
      return processPaymentAfterPartialModal(paymentDetails);
    }
    if (isProcessingPayment) return;
    setIsProcessingPayment(true);
    try {
      await processPaymentAfterPartialModal(paymentDetails);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  return {
    handleCashPaymentForQuickSale,
    handlePosPaymentForQuickSale,
    handleOpenAccountPayment,
    handlePavoP2pPayment,
    openPavoP2pSelect,
    handlePavoP2pDeviceSelected,
    pavoP2pSelectOpen,
    setPavoP2pSelectOpen,
    processPaymentAfterPartialModal: processPaymentAfterPartialModalEntry,
    isProcessingPayment,
    pavoP2pPaymentPending,
    activePavoPayment,
    cancelActivePavoPayment,
    isProcessingPosPayment, // Sadece POS için modal gösterimi
    setIsProcessingPosPayment, // ✅ Parçalı ödeme modalı için
    // ✅ YENİ: Fiş yazdırma (Online ve Diğer ödemeler için dışarıya açıldı)
    printReceiptAfterPayment,
    // POS hata modalı
    posPaymentError,
    isPosErrorModalOpen,
    posRetryCallback,
    // ✅ YENİ (Ingenico): POS hata modalı "Yine de Devam Et" — satış yazarkasasız kaydedilir
    posContinueCallback,
    handleClosePosErrorModal: () => {
      setIsPosErrorModalOpen(false);
      setPosPaymentError(null);
      setPosRetryCallback(null);
      setPosContinueCallback(null);
    },
    handleClosePosErrorModalAndClearCart: () => {
      setIsPosErrorModalOpen(false);
      setPosPaymentError(null);
      setPosRetryCallback(null);
      setPosContinueCallback(null);
      // ✅ Sepet bölme için: onPaymentSuccessOverride varsa onu çağır, yoksa clearCart
      if (onPaymentSuccessOverride) {
        onPaymentSuccessOverride();
      } else {
        clearCart();
      }
    },
    // ✅ YENİ (Ingenico): Yazarkasa hatası sonrası "Yine de Devam Et" onay modalı
    isFiscalErrorContinueOpen,
    fiscalErrorContinueMessage,
    handleFiscalErrorContinueConfirm: () => {
      console.log('[handleFiscalErrorContinueConfirm] Kullanıcı "Yine de Devam Et" onayladı.');
      setIsFiscalErrorContinueOpen(false);
      if (fiscalErrorContinueCallback) {
        fiscalErrorContinueCallback();
      }
      setFiscalErrorContinueCallback(null);
      setFiscalErrorContinueMessage('');
    },
    handleFiscalErrorContinueCancel: () => {
      console.log('[handleFiscalErrorContinueCancel] Kullanıcı iptal etti.');
      setIsFiscalErrorContinueOpen(false);
      setFiscalErrorContinueCallback(null);
      setFiscalErrorContinueMessage('');
      setIsProcessingPayment(false);
      setIsProcessingPosPayment(false);
    },
    // ✅ YENİ: Yazarkasa bağlı değil onay modalı
    isFiscalDisconnectedConfirmOpen,
    pendingPosPaymentCallback,
    handleFiscalDisconnectedConfirm: () => {
      console.log('[handleFiscalDisconnectedConfirm] Kullanıcı onayladı, işlem devam ediyor...');
      setIsFiscalDisconnectedConfirmOpen(false);
      if (pendingPosPaymentCallback) {
        pendingPosPaymentCallback();
      }
      setPendingPosPaymentCallback(null);
    },
    handleFiscalDisconnectedCancel: () => {
      console.log('[handleFiscalDisconnectedCancel] Kullanıcı iptal etti.');
      setIsFiscalDisconnectedConfirmOpen(false);
      setPendingPosPaymentCallback(null);
      setIsProcessingPayment(false);
      setIsProcessingPosPayment(false);
    },
    // ✅ YENİ: Karma ödeme için yazarkasa bağlı değil onay modalı
    isFiscalDisconnectedConfirmOpenPartial,
    handleFiscalDisconnectedConfirmPartial,
    handleFiscalDisconnectedCancelPartial,
    // ✅ Rest-P2P (pavo_rest_p2p) bekleme modalı
    restP2PModalOpen,
    restP2PStatus,
    restP2PMode,
    restP2PTotal,
    restP2PParts,
    restP2PProgress,
    restP2PMessage,
    restP2PErrorCode,
    restP2PDeviceName,
    isRestP2PEnabled,
    handleRestP2PCancel,
    /**
     * Standalone Rest-P2P (PavoPay) ödeme akışı — sepet/fatura/stok ile ilişkisi yok.
     * Rezervasyon "Giriş Yap" → POS deposito tahsilatı gibi senaryolarda kullanılır.
     * Modal kendi içinde gösterilir; outcome döner.
     */
    runRestP2PFlow,
    /**
     * Son runRestP2PFlow çağrısında yakalanan Pavo satış numarası.
     * Standalone tahsilat (ör. kulüp girişi ayrı depozito) sonrası satış no'sunu
     * okumak için kullanılır.
     */
    getLastRestP2PSaleNumber: () => lastRestP2PSaleNumberRef.current,
    handleRestP2PClose,
    handleRestP2PContinueWithoutDevice,
  };
};

export default usePayment; 